import { assetKey } from '../utils/assetIdentity';
import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Wallet, Plus, RefreshCw, Trash2, Send, ShoppingCart, TrendingDown,
  Settings, Users, DollarSign, Activity, ArrowLeftRight, ArrowUpDown, ArrowUp, ArrowDown
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import TradingChart from '../components/TradingChart';
import BalanceChart from '../components/BalanceChart';
import BalanceAnalytics from '../components/BalanceAnalytics';
import SwapHistory from '../components/SwapHistory';
import WarmupDashboard from '../components/WarmupDashboard';
import WarmupConfiguration from '../components/WarmupConfiguration';
import TokenManagement from '../components/TokenManagement';
import WarmupHistory from '../components/WarmupHistory';
import PortfolioSummary from '../components/PortfolioSummary';
import { getApiUrl } from '../utils/api';
import { useTokenConfig } from '../contexts/TokenConfigContext';

const API_URL = getApiUrl();

// Known token icons mapping (cached for performance)
const KNOWN_TOKEN_ICONS = {
  'PUBG': 'https://buycrypt.com/static/buyt_logo.png',
  'TON': 'https://ton.org/download/ton_symbol.png',
  'USDT': 'https://cache.tonapi.io/imgproxy/T3PB4s7oprNVaJkwqbGg54nexKE0ydFoLaHjuDYPv-U/rs:fill:200:200:1/g:no/aHR0cHM6Ly90ZXRoZXIudG8vaW1hZ2VzL2xvZ29DaXJjbGUucG5n.webp',
  'NOT': 'https://cache.tonapi.io/imgproxy/4KCMNm34jZLXt0rqeFm4rH-BK4FoK76EVX9r0cCIGDg/rs:fill:200:200:1/g:no/aHR0cHM6Ly9jZG4uam9pbmNvbW11bml0eS54eXovY2xpY2tlci9ub3RfbG9nby5wbmc.webp',
  'DOGS': 'https://cache.tonapi.io/imgproxy/8tCX0RccMq2vv0gSmypBwqLmDl6dV_iSRK9qBgvjWG4/rs:fill:200:200:1/g:no/aHR0cHM6Ly9jZG4uZG9ncy5kZXYvZG9ncy5wbmc.webp',
  'CHIO': 'https://cache.tonapi.io/imgproxy/HxkQC8RqL-rG2G5G5h5S-P68vAbQehsYE3SWsVm1xG4/rs:fill:200:200:1/g:no/aHR0cHM6Ly9pLmliYi5jby9kUWdNbmJNL2NoaW8tcHJvZmlsZS5wbmc.webp',
};

// Global cache for fetched icons
const iconCache = {};

// Component for jetton icon with fallback
const JettonIcon = ({ tokenAddr, symbol }) => {
  const [iconUrl, setIconUrl] = React.useState(KNOWN_TOKEN_ICONS[symbol] || iconCache[tokenAddr] || null);
  const [error, setError] = React.useState(false);
  const [loading, setLoading] = React.useState(!iconUrl);
  
  React.useEffect(() => {
    // If we already have an icon URL, don't fetch
    if (iconUrl || KNOWN_TOKEN_ICONS[symbol]) return;
    
    // Check cache
    if (iconCache[tokenAddr]) {
      setIconUrl(iconCache[tokenAddr]);
      setLoading(false);
      return;
    }
    
    // Fetch from our API proxy
    const fetchIcon = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/wallets/jetton-icon/${tokenAddr}`);
        if (response.data.success && response.data.icon_url) {
          iconCache[tokenAddr] = response.data.icon_url;
          setIconUrl(response.data.icon_url);
        } else {
          setError(true);
        }
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    
    fetchIcon();
  }, [tokenAddr, symbol, iconUrl]);
  
  if (loading) {
    return (
      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 animate-pulse" />
    );
  }
  
  if (iconUrl && !error) {
    return (
      <img 
        src={iconUrl}
        alt={symbol}
        className="w-6 h-6 rounded-full object-cover"
        onError={() => setError(true)}
      />
    );
  }
  
  // Fallback to first letter
  return (
    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-400 to-blue-500 flex items-center justify-center text-white text-xs font-bold">
      {symbol?.charAt(0) || '?'}
    </div>
  );
};

const WalletManager = () => {
  // Token configuration from context
  const { tokenAddress: globalTokenAddress, tokenSymbol } = useTokenConfig();
  
  const [wallets, setWallets] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [exportGroupName, setExportGroupName] = useState('');
  const [exportOnlyMnemonics, setExportOnlyMnemonics] = useState(false);
  const [filteredGroup, setFilteredGroup] = useState(null); // Фильтр для отображения кошельков
  const [selectedWallets, setSelectedWallets] = useState([]);
  const [, forceUpdate] = useState(0); // Для принудительного обновления
  const [loading, setLoading] = useState(false);
  const [showMnemonicDialog, setShowMnemonicDialog] = useState(false);
  const [selectedWalletForMnemonic, setSelectedWalletForMnemonic] = useState(null);
  const [walletMnemonic, setWalletMnemonic] = useState('');
  const [walletVersionForMnemonic, setWalletVersionForMnemonic] = useState('');
  
  // Expanded view mode
  const [expandedMode, setExpandedMode] = useState(false);
  const [expandedWallets, setExpandedWallets] = useState(new Set());
  const [visibleCount, setVisibleCount] = useState(60); // пагинация рендера: не рендерим все 661 сразу
  const [jettonIcons, setJettonIcons] = useState({}); // Cache for jetton icons
  
  // Helper to get jetton icon from TonAPI
  const getJettonIcon = async (tokenAddr) => {
    if (jettonIcons[tokenAddr]) return jettonIcons[tokenAddr];
    
    try {
      const response = await fetch(`https://tonapi.io/v2/jettons/${tokenAddr}`);
      const data = await response.json();
      const iconUrl = data.preview || data.metadata?.image || null;
      setJettonIcons(prev => ({ ...prev, [tokenAddr]: iconUrl }));
      return iconUrl;
    } catch {
      return null;
    }
  };
  
  // Generation settings
  const [generateCount, setGenerateCount] = useState(10);
  const [groupName, setGroupName] = useState('');
  const [walletV4r2, setWalletV4r2] = useState(true);  // WalletV4R2 enabled by default
  const [walletV5r1, setWalletV5r1] = useState(false); // WalletV5R1 disabled by default
  const [generationDelay, setGenerationDelay] = useState(1.5); // Delay between wallet creations
  
  // Generation progress state (for background generation)
  const [generationProgress, setGenerationProgress] = useState({
    generating: false,
    taskId: null,
    total: 0,
    completed: 0,
    failed: 0,
    currentWallet: null,
    status: null
  });
  
  // Distribution settings
  const [fromAddress, setFromAddress] = useState('');
  const [fromMnemonic, setFromMnemonic] = useState('');
  const [senderWallet, setSenderWallet] = useState(''); // Selected wallet from dropdown
  const [distributionAmountType, setDistributionAmountType] = useState('absolute'); // 'percentage' or 'absolute'
  const [amountPerWallet, setAmountPerWallet] = useState(1.0);
  const [distributionMinPercent, setDistributionMinPercent] = useState(10);
  const [distributionMaxPercent, setDistributionMaxPercent] = useState(20);
  const [distributionDelay, setDistributionDelay] = useState(1.0);
  const [recipientFilter, setRecipientFilter] = useState('selected'); // 'all', 'zero_balance', 'selected', 'custom'
  const [distributionComment, setDistributionComment] = useState('');
  const [intermediateHops, setIntermediateHops] = useState(0);
  const [distributeParallel, setDistributeParallel] = useState(true); // анонимизация: многопоточно/линейно
  const [distributeConcurrency, setDistributeConcurrency] = useState(24);

  // TON Rebalance (sell-group -> buy-group через свежие транзитные адреса)
  const [rbSourceGroup, setRbSourceGroup] = useState('');
  const [rbDestGroup, setRbDestGroup] = useState('');
  const [rbReserve, setRbReserve] = useState(1.0);
  const [rbMinLeg, setRbMinLeg] = useState(2.0);
  const [rbHopsMin, setRbHopsMin] = useState(2);
  const [rbHopsMax, setRbHopsMax] = useState(3);
  const [rbPlan, setRbPlan] = useState(null);
  const [rbLoading, setRbLoading] = useState(false);
  const [rbTask, setRbTask] = useState(null);
  const rbPollRef = useRef(null);

  // Pump Cycle (накопление аптренда -> нетто-слив + точный отчёт)
  const [pcDirection, setPcDirection] = useState('pump_dump');
  const [pcBuyGroup, setPcBuyGroup] = useState('');
  const [pcSellGroup, setPcSellGroup] = useState('');
  const [pcSteps, setPcSteps] = useState(6);              // количество шагов фазы 1
  const [pcTurnoverTon, setPcTurnoverTon] = useState(''); // оборот фазы 1 (TON); пусто = режим диапазона
  const [pcBuyStartTon, setPcBuyStartTon] = useState(0.3);
  const [pcBuyEndTon, setPcBuyEndTon] = useState(4.0);
  const [pcSellMult, setPcSellMult] = useState(2.0);
  const [pcSellCount, setPcSellCount] = useState(2);
  const [pcSlippage, setPcSlippage] = useState(5.0);
  const [pcPlan, setPcPlan] = useState(null);
  const [pcLoading, setPcLoading] = useState(false);
  const [pcTask, setPcTask] = useState(null);
  const pcPollRef = useRef(null);

  // Custom addresses for distribution
  const [customAddressesInput, setCustomAddressesInput] = useState('');
  const [parsedCustomAddresses, setParsedCustomAddresses] = useState([]);
  const [selectedCustomAddresses, setSelectedCustomAddresses] = useState([]);
  
  // Group selection for distribution
  const [selectedDistributionGroup, setSelectedDistributionGroup] = useState('');

  // Pairwise 1-to-1 token transfer (group A → group B / custom list)
  const [pairwiseSourceGroup, setPairwiseSourceGroup] = useState('');
  const [pairwiseDestMode, setPairwiseDestMode] = useState('group'); // 'group' | 'custom'
  const [pairwiseDestGroup, setPairwiseDestGroup] = useState('');
  const [pairwiseDestCustom, setPairwiseDestCustom] = useState('');
  const [pairwiseAmountMode, setPairwiseAmountMode] = useState('full'); // 'full' | 'fixed'
  const [pairwiseFixedAmount, setPairwiseFixedAmount] = useState('');
  const [pairwiseMapping, setPairwiseMapping] = useState(null);

  // Distribute: asset type (TON vs Jetton) and file mode
  const [distributeAssetType, setDistributeAssetType] = useState('ton'); // 'ton' or 'jetton'
  const [distributeMode, setDistributeMode] = useState('wallets'); // 'wallets' or 'file'
  const [distributeFileTransfers, setDistributeFileTransfers] = useState([]); // [{amount, address}, ...]
  
  // Trading settings - use global token address
  const [tokenAddress, setTokenAddress] = useState(globalTokenAddress);
  const [amountType, setAmountType] = useState('percentage'); // 'percentage' or 'absolute'
  const [minPercent, setMinPercent] = useState(10);
  const [maxPercent, setMaxPercent] = useState(20);
  const [minAmountTon, setMinAmountTon] = useState(0.5);
  const [maxAmountTon, setMaxAmountTon] = useState(1.0);
  const [minTradeDelay, setMinTradeDelay] = useState(0.0);
  const [maxTradeDelay, setMaxTradeDelay] = useState(0.0); // Default to instant mode
  const [slippage, setSlippage] = useState(10.0);
  
  // Prices and balances
  const [tonPriceUsd, setTonPriceUsd] = useState(0);
  const [jettonPriceUsd, setJettonPriceUsd] = useState(0);
  const [jettonBalances, setJettonBalances] = useState({});  // {address: {balance, symbol}}
  const [isLoadingJettonBalances, setIsLoadingJettonBalances] = useState(false);

  // Analytics state
  const [balanceHistory, setBalanceHistory] = useState([]);
  const [trackingStatus, setTrackingStatus] = useState({ running: false, snapshot_count: 0 });
  const [analyticsTimeRange, setAnalyticsTimeRange] = useState(24); // hours

  // Helper: Get jetton balance for wallet (handles UQ/EQ address formats)
  const getJettonBalance = (walletAddress) => {
    // Try exact match first
    if (jettonBalances[walletAddress]) {
      const bal = jettonBalances[walletAddress];
      if (!bal.symbol || bal.symbol === 'Unknown' || bal.symbol === 'Error') {
        console.log(`❌ Wallet ${truncateAddress(walletAddress, 20, 6)} has bad symbol: ${bal.symbol}`);
      }
      return bal;
    }
    
    // Try alternate format (UQ <-> EQ)
    let alternateAddress;
    if (walletAddress.startsWith('UQ')) {
      alternateAddress = 'EQ' + walletAddress.substring(2);
    } else if (walletAddress.startsWith('EQ')) {
      alternateAddress = 'UQ' + walletAddress.substring(2);
    }
    
    if (alternateAddress && jettonBalances[alternateAddress]) {
      console.log(`🔄 Found balance for ${truncateAddress(walletAddress, 20, 6)} using alternate format`);
      return jettonBalances[alternateAddress];
    }
    
    // Not found - this shouldn't happen if API returned all balances
    if (Object.keys(jettonBalances).length > 0) {
      console.log(`⚠️ NO BALANCE for ${truncateAddress(walletAddress, 20, 6)}`);
      console.log(`   Available keys (first 3):`, Object.keys(jettonBalances).slice(0, 3).map(k => truncateAddress(k, 20, 0)));
    }
    
    return null;
  };
  
  // State for batch balance updates - restore from localStorage if exists
  const [balanceUpdateProgress, setBalanceUpdateProgress] = useState(() => {
    const saved = localStorage.getItem('balanceUpdateProgress');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Only restore if it was recently (within last 10 minutes)
        if (parsed.timestamp && Date.now() - parsed.timestamp < 10 * 60 * 1000) {
          return {
            current: parsed.current || 0,
            total: parsed.total || 0,
            updating: parsed.updating || false,
            currentWallet: parsed.currentWallet || null,
            timestamp: parsed.timestamp
          };
        }
      } catch (e) {
        console.warn('Failed to restore balance update progress:', e);
      }
    }
    return { 
      current: 0, 
      total: 0, 
      updating: false,
      currentWallet: null,
      timestamp: null
    };
  });

  const SMALL_BALANCE_UPDATE_THRESHOLD = 3; // Меньшие обновления выполняем синхронно
  
  // Binance integration
  const [binanceApiKey, setBinanceApiKey] = useState('');
  const [binanceApiSecret, setBinanceApiSecret] = useState('');
  const [binanceConfigured, setBinanceConfigured] = useState(false);
  
  // Import/Export settings
  const [importMnemonic, setImportMnemonic] = useState('');
  const [derivedAddress, setDerivedAddress] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [importFileContent, setImportFileContent] = useState(null);
  
  // Wallet version selection dialog
  const [showVersionDialog, setShowVersionDialog] = useState(false);
  const [previewWallets, setPreviewWallets] = useState([]);
  const [selectedVersions, setSelectedVersions] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [overwriteExisting, setOverwriteExisting] = useState(false);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [importGroupName, setImportGroupName] = useState('Imported');
  const [createNewGroup, setCreateNewGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  
  // Sorting state
  const [sortField, setSortField] = useState(null); // 'ton' or 'token'
  const [sortDirection, setSortDirection] = useState('desc'); // 'asc' or 'desc'
  const [binanceBalance, setBinanceBalance] = useState(null);
  const [binanceAmountPerWallet, setBinanceAmountPerWallet] = useState(0.5);
  const [binanceRecipientFilter, setBinanceRecipientFilter] = useState('selected');
  const [binanceSelectedGroups, setBinanceSelectedGroups] = useState([]);
  const [binanceAmountType, setBinanceAmountType] = useState('absolute'); // 'percentage' or 'absolute'
  const [binanceMinAmount, setBinanceMinAmount] = useState(0.5);
  const [binanceMaxAmount, setBinanceMaxAmount] = useState(1.0);
  const [binanceMinPercent, setBinanceMinPercent] = useState(10);
  const [binanceMaxPercent, setBinanceMaxPercent] = useState(20);
  const binanceTransferAccounts = [
    { value: 'spot', label: 'Spot' },
    { value: 'funding', label: 'Funding (Пополнения)' },
    { value: 'futures', label: 'Futures (USDT-M)' }
  ];
  const [binanceTransferFrom, setBinanceTransferFrom] = useState('spot');
  const [binanceTransferTo, setBinanceTransferTo] = useState('funding');
  const [binanceTransferAsset, setBinanceTransferAsset] = useState('TON');
  const [binanceTransferAmount, setBinanceTransferAmount] = useState(1);
  const [binanceTransferLoading, setBinanceTransferLoading] = useState(false);
  
  // Progress logs
  const [showProgressDialog, setShowProgressDialog] = useState(false);
  const [progressLogs, setProgressLogs] = useState([]);
  const [operationInProgress, setOperationInProgress] = useState(false);
  
  // Mass trading background task state
  const [massTradingTask, setMassTradingTask] = useState(() => {
    const saved = localStorage.getItem('massTradingTask');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return null;
      }
    }
    return null;
  });

  // Фоновая задача distribute (рассылка) — переживает рефреш через localStorage
  const [distributeTask, setDistributeTask] = useState(() => {
    const saved = localStorage.getItem('distributeTask');
    if (saved) {
      try { return JSON.parse(saved); } catch (e) { return null; }
    }
    return null;
  });

  useEffect(() => {
    loadGroups();
    loadWallets();
    loadPrices();
    loadBinanceCredentials();
    // Load balance tracking status and history
    loadTrackingStatus();
    loadBalanceHistory(analyticsTimeRange);
    // Refresh prices every 30 seconds (even if update is in progress)
    const priceInterval = setInterval(() => {
      loadPrices().catch(err => console.error('Price refresh error:', err));
    }, 30000);
    return () => clearInterval(priceInterval);
  }, []);
  
  // Sync token address from global config
  useEffect(() => {
    if (globalTokenAddress && globalTokenAddress !== tokenAddress) {
      setTokenAddress(globalTokenAddress);
    }
  }, [globalTokenAddress]);
  
  // Load jetton balances when wallets change
  useEffect(() => {
    if (wallets.length > 0) {
      console.log('🔄 Wallets loaded, fetching jetton balances...');
      loadJettonBalances();
    }
  }, [wallets.length]); // Trigger when wallets are loaded
  
  // Helper function to add progress log
  const addProgressLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    const logEntry = {
      timestamp,
      message,
      type // 'info', 'success', 'error', 'warning'
    };
    setProgressLogs(prev => [...prev, logEntry]);
  };

  const loadGroups = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/wallets/groups`);
      setGroups(response.data.groups || []);
    } catch (error) {
      console.error('Error loading groups:', error);
    }
  };

  const loadWallets = async (group = selectedGroup) => {
    setLoading(true);
    try {
      const params = group ? { group_name: group } : {};
      const apiEndpoint = `${API_URL}/api/wallets/list`;
      console.log('🔍 Loading wallets from:', apiEndpoint, 'params:', params);
      const response = await axios.get(apiEndpoint, { params });
      
      console.log('📥 Wallets API response:', {
        count: response.data.count,
        walletsCount: response.data.wallets?.length || 0,
        hasWallets: !!response.data.wallets,
        status: response.status,
        dataType: typeof response.data,
        walletsType: Array.isArray(response.data.wallets) ? 'array' : typeof response.data.wallets
      });
      
      // Sort wallets by TON balance in descending order (highest first)
      const wallets = response.data.wallets || [];
      const sortedWallets = wallets.sort((a, b) => {
        const balanceA = parseFloat(a.ton_balance || 0);
        const balanceB = parseFloat(b.ton_balance || 0);
        return balanceB - balanceA; // Descending order
      });
      
      console.log(`✅ Loaded ${sortedWallets.length} wallets`);
      setWallets(sortedWallets);
      
      if (sortedWallets.length === 0) {
        console.warn('⚠️ No wallets found in response');
        toast.warning('No wallets found');
      }
      return sortedWallets;
    } catch (error) {
      console.error('❌ Error loading wallets:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      toast.error(`Failed to load wallets: ${error.response?.data?.detail || error.message}`);
      setWallets([]); // Clear wallets on error
      return [];
    } finally {
      setLoading(false);
    }
  };


  const loadPrices = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/wallets/prices`, {
        params: { jetton_master: tokenAddress }
      });
      // Only update if we got valid prices (not 0 or null)
      if (response.data.ton_usd && response.data.ton_usd > 0) {
        setTonPriceUsd(response.data.ton_usd);
      }
      if (response.data.jetton_usd && response.data.jetton_usd > 0) {
        setJettonPriceUsd(response.data.jetton_usd);
      }
    } catch (error) {
      console.error('Failed to load prices:', error);
      // Don't reset prices to 0 on error - keep previous values
      // Prices will be retried on next interval
    }
  };

  const loadJettonBalances = async (walletList = null) => {
    // Prevent concurrent calls
    if (isLoadingJettonBalances) {
      console.log('⏸️ Already loading jetton balances, skipping...');
      return;
    }
    
    try {
      setIsLoadingJettonBalances(true);
      
      // Batch request for all wallets
      const sourceWallets = walletList || wallets;
      const addresses = sourceWallets.map(w => w.address);
      if (addresses.length === 0) {
        setIsLoadingJettonBalances(false);
        return;
      }
      
      console.log(`🔄 Loading jetton balances for ${addresses.length} wallets...`);
      
      // Use DB endpoint (fast, no API calls)
      const response = await axios.post(`${API_URL}/api/wallets/jetton-balances-from-db`, {
        addresses,
        jetton_master: tokenAddress
      });
      
      console.log('💾 Jetton balances from DB:', response.data);
      
      if (response.data.success && response.data.balances) {
        const balances = response.data.balances;
        
        // Debug: check each balance
        const withSymbol = Object.keys(balances).filter(addr => balances[addr]?.symbol).length;
        const withoutSymbol = Object.keys(balances).filter(addr => !balances[addr]?.symbol).length;
        
        console.log(`✅ Loaded ${Object.keys(balances).length} jetton balances`);
        console.log(`   - With symbol: ${withSymbol}`);
        console.log(`   - Without symbol: ${withoutSymbol}`);
        
        // Fill missing balances with default values
        const completeBalances = { ...balances };
        addresses.forEach(addr => {
          if (!completeBalances[addr]) {
            console.log(`⚠️ Missing balance for ${truncateAddress(addr, 20, 6)}, setting default`);
            completeBalances[addr] = {
              balance: 0,
              balance_readable: 0,
              symbol: tokenSymbol || 'TOKEN',
              decimals: 9,
              from_db: true
            };
          }
        });
        
        setJettonBalances(completeBalances);
        console.log(`✅ Set ${Object.keys(completeBalances).length} jetton balances in state`);
      } else {
        console.error('❌ Invalid response format:', response.data);
      }
    } catch (error) {
      console.error('❌ Failed to load jetton balances:', error.message);
      // Fallback: set empty balances with BUYT symbol for all wallets
      const emptyBalances = {};
      const sourceWallets = walletList || wallets;
      sourceWallets.forEach(wallet => {
        emptyBalances[wallet.address] = { 
          balance: 0,
          balance_readable: 0, 
          symbol: tokenSymbol || 'TOKEN',
          decimals: 9,
          from_db: true
        };
      });
      setJettonBalances(emptyBalances);
      console.log(`⚠️ Set ${Object.keys(emptyBalances).length} fallback balances`);
    } finally {
      setIsLoadingJettonBalances(false);
    }
  };

  // Reset stuck progress
  const resetStuckProgress = () => {
    const clearedProgress = { 
      current: 0, 
      total: 0, 
      updating: false,
      currentWallet: null,
      timestamp: null
    };
    setBalanceUpdateProgress(clearedProgress);
    localStorage.removeItem('balanceUpdateProgress');
    localStorage.removeItem('balanceUpdateTaskId');
    toast.info('Прогресс обновления сброшен');
  };

  // Check for stuck progress on mount
  useEffect(() => {
    const checkStuckProgress = () => {
      const saved = localStorage.getItem('balanceUpdateProgress');
      const savedTaskId = localStorage.getItem('balanceUpdateTaskId');
      
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          // If progress is older than 30 minutes, consider it stuck
          if (parsed.timestamp && Date.now() - parsed.timestamp > 30 * 60 * 1000) {
            console.warn('Stuck progress detected, resetting...');
            resetStuckProgress();
          }
        } catch (e) {
          console.warn('Failed to check progress:', e);
          resetStuckProgress();
        }
      }
      
      if (savedTaskId) {
        // Check if task is still running
        axios.get(`${API_URL}/api/wallets/update-balances/status/${savedTaskId}`)
          .then(response => {
            const status = response.data.status;
            if (status && status.status !== 'running') {
              // Task completed or failed, clean up
              localStorage.removeItem('balanceUpdateTaskId');
              if (status.status === 'completed') {
                setBalanceUpdateProgress({
                  current: status.total || 0,
                  total: status.total || 0,
                  updating: false,
                  currentWallet: null,
                  timestamp: null
                });
              } else {
                resetStuckProgress();
              }
            }
          })
          .catch(error => {
            console.warn('Error checking task status:', error);
            // If task not found, clean up
            if (error.response?.status === 404) {
              localStorage.removeItem('balanceUpdateTaskId');
              resetStuckProgress();
            }
          });
      }
    };
    
    checkStuckProgress();
  }, []);

  // Update balances one by one with visual feedback
  const runSynchronousBalanceUpdate = async (targetAddresses) => {
    let successCount = 0;
    let errorCount = 0;

    try {
      console.log('⚠️ Выполняется синхронное обновление балансов для небольшой выборки...');

      for (let i = 0; i < targetAddresses.length; i++) {
        const address = targetAddresses[i];
        const progressUpdate = { 
          current: i + 1, 
          total: targetAddresses.length,
          updating: true,
          currentWallet: address,
          timestamp: Date.now()
        };
        setBalanceUpdateProgress(progressUpdate);
        localStorage.setItem('balanceUpdateProgress', JSON.stringify(progressUpdate));

        try {
          console.log(`🔄 Updating wallet ${i + 1}/${targetAddresses.length}: ${truncateAddress(address, 20, 6)}`);

          const response = await axios.post(`${API_URL}/api/wallets/update-balances`, {
            addresses: [address],
            jetton_master: tokenAddress,
            batch_size: 1,
            delay_seconds: 0.5,
            max_retries: 1
          });

          if (response.data.success) {
            successCount++;
            console.log(`✅ Updated wallet ${i + 1}/${targetAddresses.length}`);

            try {
              const walletResponse = await axios.get(`${API_URL}/api/wallets/list`, {
                params: { _t: Date.now() }
              });

              if (walletResponse.data && walletResponse.data.wallets) {
                const updatedWallet = walletResponse.data.wallets.find(w => w.address === address);
                if (updatedWallet) {
                  setWallets(prev => {
                    const oldWallet = prev.find(w => w.address === address);
                    const oldTonBalance = oldWallet ? (parseInt(oldWallet.ton_balance || 0) / 1e9) : 0;
                    const newTonBalance = parseInt(updatedWallet.ton_balance || 0) / 1e9;
                    const updated = prev.map(w => 
                      w.address === address ? { ...updatedWallet } : w
                    );

                    if (oldTonBalance !== newTonBalance) {
                      console.log(`✅ Wallet ${address.slice(0, 20)}... TON balance updated: ${oldTonBalance.toFixed(4)} -> ${newTonBalance.toFixed(4)}`);
                    }

                    return updated;
                  });
                } else {
                  console.warn(`⚠️ Wallet ${address.slice(0, 20)}... not found in response`);
                }
              } else {
                console.warn(`⚠️ Invalid wallet response structure:`, walletResponse.data);
              }

              const balanceResponse = await axios.post(`${API_URL}/api/wallets/jetton-balances-from-db`, {
                addresses: [address],
                jetton_master: tokenAddress
              });

              if (balanceResponse.data.success && balanceResponse.data.balances) {
                setJettonBalances(prev => ({
                  ...prev,
                  ...balanceResponse.data.balances
                }));
              }
            } catch (balanceError) {
              console.warn('Could not reload balance for wallet:', balanceError);
            }
          } else {
            errorCount++;
            console.warn(`⚠️ Failed to update wallet ${i + 1}/${targetAddresses.length}`);
          }

          if (i < targetAddresses.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        } catch (error) {
          errorCount++;
          console.error(`❌ Error updating wallet ${i + 1}/${targetAddresses.length}:`, error);

          if (i < targetAddresses.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
      }

      const finalProgress = { 
        current: targetAddresses.length, 
        total: targetAddresses.length, 
        updating: false,
        currentWallet: null,
        timestamp: Date.now()
      };
      setBalanceUpdateProgress(finalProgress);
      localStorage.setItem('balanceUpdateProgress', JSON.stringify(finalProgress));

      console.log('🔄 Reloading all wallets after balance update...');
      
      const timestamp = Date.now();
      const walletsResponse = await axios.get(`${API_URL}/api/wallets/list`, { 
        params: { _t: timestamp } 
      });
      
      if (walletsResponse.data.wallets) {
        const sortedWallets = walletsResponse.data.wallets.sort((a, b) => {
          const balanceA = parseFloat(a.ton_balance || 0);
          const balanceB = parseFloat(b.ton_balance || 0);
          return balanceB - balanceA;
        });
        console.log(`✅ Loaded ${sortedWallets.length} wallets with fresh data`);
        console.log(`📊 Sample wallet TON balance:`, sortedWallets[0] ? (parseInt(sortedWallets[0].ton_balance || 0) / 1e9).toFixed(4) + ' TON' : 'N/A');
        setWallets(sortedWallets);
      }
      
      await loadWallets();
      await loadJettonBalances();
      await loadPrices();
      await loadTrackingStatus();
      await loadBalanceHistory(analyticsTimeRange);

      if (successCount > 0) {
        toast.success(`Обновлено кошельков: ${successCount}/${targetAddresses.length}`);
      }
      
      if (errorCount > 0) {
        toast.warning(`Ошибок при обновлении: ${errorCount}`);
      }

    } catch (error) {
      console.error('❌ Update failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to update balances');
    } finally {
      const clearedProgress = { 
        current: 0, 
        total: 0, 
        updating: false,
        currentWallet: null,
        timestamp: null
      };
      setBalanceUpdateProgress(clearedProgress);
      localStorage.removeItem('balanceUpdateProgress');
    }
  };

  const updateBalancesFromAPI = async () => {
    // Check if progress is stuck (older than 5 minutes)
    if (balanceUpdateProgress.updating && balanceUpdateProgress.timestamp) {
      const age = Date.now() - balanceUpdateProgress.timestamp;
      if (age > 5 * 60 * 1000) {
        console.warn('Progress appears stuck, resetting...');
        resetStuckProgress();
      } else {
        toast.info('Обновление балансов уже выполняется. Если зависло, нажмите "Сбросить"');
        return;
      }
    }
    
    if (balanceUpdateProgress.updating) {
      toast.info('Обновление балансов уже выполняется');
      return;
    }

    const targetAddresses = selectedWallets.length > 0
      ? selectedWallets
      : wallets.map(w => w.address);

    if (targetAddresses.length === 0) {
      toast.warning('Нет кошельков для обновления');
      return;
    }

    try {
      const initialProgress = { 
        current: 0, 
        total: targetAddresses.length, 
        updating: true,
        currentWallet: null,
        timestamp: Date.now()
      };
      setBalanceUpdateProgress(initialProgress);
      localStorage.setItem('balanceUpdateProgress', JSON.stringify(initialProgress));
      
      if (targetAddresses.length <= SMALL_BALANCE_UPDATE_THRESHOLD) {
        console.log('⚡ Небольшая выборка кошельков — используем синхронный путь');
        await runSynchronousBalanceUpdate(targetAddresses);
        return;
      }

      console.log(`🔄 Updating ${targetAddresses.length} wallet balances one by one...`);
      console.log(`🔄 Starting background balance update for ${targetAddresses.length} wallets...`);
      
      const response = await axios.post(`${API_URL}/api/wallets/update-balances?background=true`, {
        addresses: targetAddresses,
        jetton_master: tokenAddress,
        batch_size: 1,
        delay_seconds: 1.0,
        max_retries: 3
      });

      if (response.data.success && response.data.task_id) {
        const taskId = response.data.task_id;
        console.log(`✅ Background update started with task_id: ${taskId}`);
        
        // Poll for status updates with timeout handling
        let pollTimeout: NodeJS.Timeout | null = null;
        let lastUpdateTime = Date.now();
        const MAX_STUCK_TIME = 5 * 60 * 1000; // 5 minutes
        
        const pollStatus = async () => {
          try {
            const statusResponse = await axios.get(`${API_URL}/api/wallets/update-balances/status/${taskId}`);
            const status = statusResponse.data.status;
            
            if (!status) {
              // Task not found, clean up
              if (pollTimeout) clearTimeout(pollTimeout);
              localStorage.removeItem('balanceUpdateTaskId');
              resetStuckProgress();
              return;
            }
            
            lastUpdateTime = Date.now();
            
            setBalanceUpdateProgress({
              current: status.current || 0,
              total: status.total || targetAddresses.length,
              updating: status.status === 'running',
              currentWallet: status.current_wallet || null,
              timestamp: Date.now()
            });
            
            // Update wallets in real-time from status.updated_wallets
            if (status.updated_wallets && status.updated_wallets.length > 0) {
              setWallets(prevWallets => {
                const walletsMap = new Map(prevWallets.map(w => [w.address, w]));
                for (const updatedWallet of status.updated_wallets) {
                  if (updatedWallet.address) {
                    walletsMap.set(updatedWallet.address, {
                      ...walletsMap.get(updatedWallet.address),
                      ...updatedWallet
                    });
                  }
                }
                return Array.from(walletsMap.values());
              });
            }
            
            // Update balances periodically
            if (status.status === 'running') {
              // Only reload full balances every 10 wallets to reduce API load
              if ((status.current || 0) % 10 === 0) {
                await loadJettonBalances();
                await loadPrices();
              }
              pollTimeout = setTimeout(pollStatus, 1500); // Poll every 1.5 seconds for faster updates
            } else {
              if (pollTimeout) clearTimeout(pollTimeout);
              
              setBalanceUpdateProgress({
                current: status.total || targetAddresses.length,
                total: status.total || targetAddresses.length,
                updating: false,
                currentWallet: null,
                timestamp: null
              });
              
              localStorage.removeItem('balanceUpdateTaskId');
              
              await loadWallets();
              await loadJettonBalances();
              await loadPrices();
              await loadTrackingStatus();
              await loadBalanceHistory(analyticsTimeRange);
              
              if (status.status === 'completed') {
                toast.success(`Обновлено кошельков: ${status.success_count || 0}/${status.total || targetAddresses.length}`);
              } else {
                toast.error(`Ошибка обновления: ${status.error || 'Unknown error'}`);
              }
            }
          } catch (error) {
            console.error('Error polling status:', error);
            
            if (Date.now() - lastUpdateTime > MAX_STUCK_TIME) {
              console.warn('Polling appears stuck, resetting...');
              if (pollTimeout) clearTimeout(pollTimeout);
              localStorage.removeItem('balanceUpdateTaskId');
              resetStuckProgress();
              toast.warning('Обновление зависло и было сброшено');
              return;
            }
            
            if (error.response && error.response.status === 404) {
              if (pollTimeout) clearTimeout(pollTimeout);
              localStorage.removeItem('balanceUpdateTaskId');
              resetStuckProgress();
              return;
            }
            
            pollTimeout = setTimeout(pollStatus, 5000); // Retry after 5 seconds on error
          }
        };
        
        pollStatus();
        
        const balanceUpdateInterval = setInterval(async () => {
          if (balanceUpdateProgress.updating) {
            await loadJettonBalances();
            await loadPrices();
          } else {
            clearInterval(balanceUpdateInterval);
          }
        }, 3000);
        
        return;
      }
      
      console.log('⚠️ Background update not available, using synchronous method');
      await runSynchronousBalanceUpdate(targetAddresses);
      return;
    } catch (error) {
      console.error('❌ Update failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to update balances');
    } finally {
      const clearedProgress = { 
        current: 0, 
        total: 0, 
        updating: false,
        currentWallet: null,
        timestamp: null
      };
      setBalanceUpdateProgress(clearedProgress);
      localStorage.removeItem('balanceUpdateProgress');
    }
  };

  const handleUpdateBalances = () => {
    updateBalancesFromAPI();
  };

  // Poll generation status
  const pollGenerationStatus = async (taskId) => {
    try {
      const response = await axios.get(`${API_URL}/api/wallets/generate/status/${taskId}`);
      const status = response.data.status;
      
      if (status) {
        setGenerationProgress({
          generating: status.status === 'running',
          taskId: taskId,
          total: status.total,
          completed: status.completed,
          failed: status.failed,
          currentWallet: status.current_wallet,
          status: status.status
        });
        
        // Save to localStorage for persistence across page reloads
        localStorage.setItem('walletGenerationTask', JSON.stringify({
          taskId,
          groupName: status.group_name
        }));
        
        // Continue polling if still running
        if (status.status === 'running') {
          setTimeout(() => pollGenerationStatus(taskId), 1500);
        } else {
          // Generation completed
          localStorage.removeItem('walletGenerationTask');
          
          if (status.status === 'completed') {
            toast.success(`Generated ${status.completed} wallets in group "${status.group_name}" (${status.failed} failed)`);
          } else if (status.status === 'error') {
            toast.error(`Generation failed: ${status.errors?.[0]?.error || 'Unknown error'}`);
          } else if (status.status === 'cancelled') {
            toast.warning('Generation was cancelled');
          }
          
          // Reload wallets and groups
          await loadGroups();
          await loadWallets();
          
          // Reset form
          setGenerateCount(10);
          setGroupName('');
          setLoading(false);
        }
      }
    } catch (error) {
      console.error('Error polling generation status:', error);
      // Keep polling even if there's a temporary error
      setTimeout(() => pollGenerationStatus(taskId), 3000);
    }
  };
  
  // Check for running generation task on mount
  useEffect(() => {
    const savedTask = localStorage.getItem('walletGenerationTask');
    if (savedTask) {
      try {
        const { taskId } = JSON.parse(savedTask);
        if (taskId) {
          setLoading(true);
          pollGenerationStatus(taskId);
        }
      } catch (e) {
        localStorage.removeItem('walletGenerationTask');
      }
    }
  }, []);
  
  // Wallet generation function
  const handleGenerateWallets = async () => {
    if (!groupName || groupName.trim() === '') {
      toast.error('Please enter a group name');
      return;
    }

    if (generateCount < 1 || generateCount > 1000) {
      toast.error('Number of wallets must be between 1 and 1000');
      return;
    }

    // At least one wallet version must be selected
    if (!walletV4r2 && !walletV5r1) {
      toast.error('Please select at least one wallet version');
      return;
    }

    setLoading(true);
    setGenerationProgress({
      generating: true,
      taskId: null,
      total: generateCount,
      completed: 0,
      failed: 0,
      currentWallet: 0,
      status: 'starting'
    });
    
    try {
      const response = await axios.post(`${API_URL}/api/wallets/generate`, {
        count: generateCount,
        group_name: groupName,
        wallet_v4r2: walletV4r2,
        wallet_v5r1: walletV5r1,
        delay_seconds: generationDelay,
        background: true  // Always use background generation
      });

      if (response.data.success && response.data.background) {
        // Background generation started
        const taskId = response.data.task_id;
        toast.success(`Generation started for ${generateCount} wallets. Progress will be shown below.`);
        
        // Start polling for progress
        pollGenerationStatus(taskId);
      } else if (response.data.success) {
        // Synchronous generation (fallback)
        const versionCounts = response.data.version_counts || {};
        const versionInfo = Object.entries(versionCounts)
          .map(([v, count]) => `${v}: ${count}`)
          .join(', ');
        
        toast.success(`Generated ${response.data.wallets?.length || generateCount} wallets in group "${groupName}" (${versionInfo})`);
        
        setGenerateCount(10);
        setGroupName('');
        setGenerationProgress({ generating: false, taskId: null, total: 0, completed: 0, failed: 0, currentWallet: null, status: null });
        
        await loadGroups();
        await loadWallets();
        setLoading(false);
      }
    } catch (error) {
      console.error('Generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate wallets');
      setGenerationProgress({ generating: false, taskId: null, total: 0, completed: 0, failed: 0, currentWallet: null, status: null });
      setLoading(false);
    }
  };
  
  // Cancel generation
  const handleCancelGeneration = async () => {
    if (!generationProgress.taskId) return;
    
    try {
      await axios.post(`${API_URL}/api/wallets/generate/cancel/${generationProgress.taskId}`);
      toast.success('Cancelling generation...');
    } catch (error) {
      console.error('Cancel error:', error);
      toast.error('Failed to cancel generation');
    }
  };

  // Balance tracking functions
  const loadTrackingStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/wallets/balance-tracking/status`);
      setTrackingStatus(response.data);
    } catch (error) {
      console.error('Failed to load tracking status:', error);
    }
  };

  const loadBalanceHistory = async (hours = 24) => {
    try {
      const response = await axios.get(`${API_URL}/api/wallets/balance-tracking/history`, {
        params: { hours }
      });
      setBalanceHistory(response.data.history || []);
    } catch (error) {
      console.error('Failed to load balance history:', error);
      setBalanceHistory([]);
    }
  };

  const startTracking = async () => {
    try {
      await axios.post(`${API_URL}/api/wallets/balance-tracking/start`);
      toast.success('Balance tracking started');
      await loadTrackingStatus();
    } catch (error) {
      console.error('Failed to start tracking:', error);
      toast.error('Failed to start tracking');
    }
  };

  const stopTracking = async () => {
    try {
      await axios.post(`${API_URL}/api/wallets/balance-tracking/stop`);
      toast.success('Balance tracking stopped');
      await loadTrackingStatus();
    } catch (error) {
      console.error('Failed to stop tracking:', error);
      toast.error('Failed to stop tracking');
    }
  };

  const takeSnapshot = async () => {
    try {
      await axios.post(`${API_URL}/api/wallets/balance-tracking/snapshot`);
      toast.success('Snapshot taken');
      await loadTrackingStatus();
      await loadBalanceHistory(analyticsTimeRange);
    } catch (error) {
      console.error('Failed to take snapshot:', error);
      toast.error('Failed to take snapshot');
    }
  };

  // Parse custom addresses from textarea input
  const parseCustomAddresses = (input) => {
    if (!input || input.trim() === '') {
      setParsedCustomAddresses([]);
      setSelectedCustomAddresses([]);
      return;
    }
    
    // Split by comma, space, or newline
    const addresses = input
      .split(/[\n,\s]+/)
      .map(addr => addr.trim())
      .filter(addr => addr.length > 0);
    
    // Validate and parse addresses
    const validated = addresses.map((addr, index) => {
      // Check if address is valid TON format (UQ/EQ or 0:)
      const isValid = /^(UQ|EQ|kQ)[A-Za-z0-9_-]{46,48}$/.test(addr) || /^0:[a-fA-F0-9]{64}$/.test(addr) || /^-1:[a-fA-F0-9]{64}$/.test(addr);
      
      // Check if address is in our wallet list
      const isManaged = wallets.some(w => w.address === addr);
      
      return {
        id: `custom-${index}`,
        address: addr,
        isValid: isValid,
        isManaged: isManaged,
        walletInfo: isManaged ? wallets.find(w => w.address === addr) : null
      };
    });
    
    // Remove duplicates
    const unique = validated.reduce((acc, current) => {
      const existing = acc.find(item => item.address === current.address);
      if (!existing) {
        acc.push(current);
      }
      return acc;
    }, []);
    
    setParsedCustomAddresses(unique);
    // Auto-select all valid addresses
    setSelectedCustomAddresses(unique.filter(a => a.isValid).map(a => a.address));
  };
  
  // Handle custom addresses input change
  const handleCustomAddressesChange = (value) => {
    setCustomAddressesInput(value);
    parseCustomAddresses(value);
  };

  // Parse distribute file (amount,address per line)
  const handleDistributeFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result || '';
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      const transfers = [];
      for (const line of lines) {
        const parts = line.split(/[,;\t]/).map(p => p.trim());
        if (parts.length >= 2) {
          const amount = parseFloat(parts[0]);
          const address = parts[1];
          if (!isNaN(amount) && amount > 0 && address.length >= 40) {
            transfers.push({ amount, address });
          }
        }
      }
      setDistributeFileTransfers(transfers);
      toast.success(`Parsed ${transfers.length} transfers from file`);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleDistributeFromFile = async () => {
    setProgressLogs([]);
    setShowProgressDialog(true);
    setOperationInProgress(true);
    setLoading(true);
    addProgressLog(`🚀 Starting ${distributeAssetType.toUpperCase()} distribution from file...`, 'info');

    let senderAddress = fromAddress;
    let senderMnemonic = fromMnemonic;

    if (senderWallet && senderWallet !== 'custom') {
      const selectedWalletObj = wallets.find(w => w.address === senderWallet);
      if (!selectedWalletObj) {
        addProgressLog('❌ Selected sender wallet not found', 'error');
        toast.error('Selected sender wallet not found');
        setLoading(false);
        setOperationInProgress(false);
        return;
      }
      try {
        const mnemonicResponse = await axios.get(`${API_URL}/api/wallets/wallet/${senderWallet}/mnemonic`);
        senderMnemonic = mnemonicResponse.data.mnemonic;
        senderAddress = senderWallet;
      } catch (error) {
        addProgressLog('❌ Failed to fetch sender wallet mnemonic', 'error');
        toast.error('Failed to fetch sender wallet mnemonic');
        setLoading(false);
        setOperationInProgress(false);
        return;
      }
    } else if (senderWallet === 'custom') {
      if (!fromAddress || !fromMnemonic) {
        addProgressLog('❌ Custom wallet address or mnemonic missing', 'error');
        toast.error('Please enter from address and mnemonic');
        setLoading(false);
        setOperationInProgress(false);
        return;
      }
    } else {
      addProgressLog('❌ No sender wallet selected', 'error');
      toast.error('Please select a sender wallet');
      setLoading(false);
      setOperationInProgress(false);
      return;
    }

    if (distributeFileTransfers.length === 0) {
      addProgressLog('❌ No valid transfers in file', 'error');
      toast.error('Please upload a file with amount,address per line');
      setLoading(false);
      setOperationInProgress(false);
      return;
    }

    addProgressLog(`📬 Sending to ${distributeFileTransfers.length} addresses...`, 'info');

    try {
      const requestPayload = {
        from_address: senderAddress,
        from_mnemonic: senderMnemonic,
        asset_type: distributeAssetType,
        transfers: distributeFileTransfers,
        delay_seconds: distributionDelay,
        background: true
      };
      if (distributeAssetType === 'jetton') {
        requestPayload.jetton_master = tokenAddress || globalTokenAddress;
      }

      const response = await axios.post(`${API_URL}/api/wallets/distribute-from-file`, requestPayload);

      // Фоновый режим: получаем task_id, дальше прогресс ведёт поллинг (переживает рефреш)
      if (response.data.background && response.data.task_id) {
        const taskId = response.data.task_id;
        addProgressLog(`🆔 Рассылка запущена в фоне (task ${taskId.slice(0, 8)}…). Прогресс — внизу ⬇️`, 'info');
        const init = { taskId, operation: 'distribute', total: response.data.total, completed: 0, successful: 0, failed: 0, status: 'running' };
        localStorage.setItem('distributeTask', JSON.stringify(init));
        setDistributeTask({ ...init, logs: [] });
        pollDistributeStatus(taskId);
        setLoading(false); // фон — кнопку освобождаем; operationInProgress держим до завершения
        return;
      }

      const results = response.data.results;
      addProgressLog(`\n📊 Distribution completed!`, 'success');
      addProgressLog(`✅ Successful: ${results.success.length}`, 'success');
      addProgressLog(`❌ Failed: ${results.failed.length}`, results.failed.length > 0 ? 'error' : 'info');

      if (results.failed.length > 0) {
        addProgressLog('\n⚠️ Failed transactions:', 'warning');
        results.failed.slice(0, 10).forEach(f => {
          addProgressLog(`  • ${truncateAddress(f.address)} - ${f.error}`, 'error');
        });
        if (results.failed.length > 10) {
          addProgressLog(`  • ...and ${results.failed.length - 10} more`, 'error');
        }
      }

      toast.success(`Distribution complete: ${results.success.length} success, ${results.failed.length} failed`);
      addProgressLog('\n🔄 Refreshing wallet data...', 'info');
      await loadWallets();
      addProgressLog('✅ Wallet data refreshed', 'success');
      setLoading(false);
      setOperationInProgress(false);
      addProgressLog('\n✅ Operation finished', 'success');
    } catch (error) {
      addProgressLog(`\n❌ Error: ${error.response?.data?.detail || error.message}`, 'error');
      toast.error(error.response?.data?.detail || 'Failed to distribute');
      setLoading(false);
      setOperationInProgress(false);
    }
  };

  const handleDistributeTon = async () => {
    // Reset and show progress dialog
    setProgressLogs([]);
    setShowProgressDialog(true);
    setOperationInProgress(true);
    setLoading(true);
    
    addProgressLog('🚀 Starting TON distribution...', 'info');
    
    // Determine sender wallet
    let senderAddress = fromAddress;
    let senderMnemonic = fromMnemonic;
    
    if (senderWallet && senderWallet !== 'custom') {
      addProgressLog(`📍 Using sender wallet: ${truncateAddress(senderWallet)}`, 'info');
      // Use selected wallet from dropdown
      const selectedWalletObj = wallets.find(w => w.address === senderWallet);
      if (!selectedWalletObj) {
        addProgressLog('❌ Selected sender wallet not found', 'error');
        toast.error('Selected sender wallet not found');
        setLoading(false);
        setOperationInProgress(false);
        return;
      }
      // Fetch mnemonic for selected wallet
      try {
        addProgressLog('🔑 Fetching sender wallet mnemonic...', 'info');
        const mnemonicResponse = await axios.get(`${API_URL}/api/wallets/wallet/${senderWallet}/mnemonic`);
        senderMnemonic = mnemonicResponse.data.mnemonic;
        senderAddress = senderWallet;
        addProgressLog('✅ Mnemonic fetched successfully', 'success');
      } catch (error) {
        addProgressLog('❌ Failed to fetch sender wallet mnemonic', 'error');
        toast.error('Failed to fetch sender wallet mnemonic');
        setLoading(false);
        setOperationInProgress(false);
        return;
      }
    } else if (senderWallet === 'custom') {
      addProgressLog('📍 Using custom sender wallet', 'info');
      if (!fromAddress || !fromMnemonic) {
        addProgressLog('❌ Custom wallet address or mnemonic missing', 'error');
        toast.error('Please enter from address and mnemonic');
        setLoading(false);
        setOperationInProgress(false);
        return;
      }
    } else {
      addProgressLog('❌ No sender wallet selected', 'error');
      toast.error('Please select a sender wallet');
      setLoading(false);
      setOperationInProgress(false);
      return;
    }
    
    // Determine recipient addresses based on filter
    let recipientAddresses = [];
    if (recipientFilter === 'all') {
      recipientAddresses = wallets.map(w => w.address);
      addProgressLog(`📬 Recipients: All wallets (${recipientAddresses.length})`, 'info');
    } else if (recipientFilter === 'zero_balance') {
      recipientAddresses = wallets.filter(w => parseInt(w.ton_balance || 0) === 0).map(w => w.address);
      if (recipientAddresses.length === 0) {
        addProgressLog('❌ No wallets with zero balance found', 'error');
        toast.error('No wallets with zero balance found');
        setLoading(false);
        setOperationInProgress(false);
        return;
      }
      addProgressLog(`📬 Recipients: Zero-balance wallets (${recipientAddresses.length})`, 'info');
    } else if (recipientFilter === 'selected') {
      if (selectedWallets.length === 0) {
        addProgressLog('❌ No wallets selected', 'error');
        toast.error('Please select wallets to distribute to');
        setLoading(false);
        setOperationInProgress(false);
        return;
      }
      recipientAddresses = selectedWallets;
      addProgressLog(`📬 Recipients: Selected wallets (${recipientAddresses.length})`, 'info');
    } else if (recipientFilter === 'custom') {
      if (selectedCustomAddresses.length === 0) {
        addProgressLog('❌ No custom addresses selected', 'error');
        toast.error('Please enter and select custom addresses to distribute to');
        setLoading(false);
        setOperationInProgress(false);
        return;
      }
      recipientAddresses = selectedCustomAddresses;
      addProgressLog(`📬 Recipients: Custom addresses (${recipientAddresses.length})`, 'info');
    } else if (recipientFilter === 'group') {
      if (!selectedDistributionGroup) {
        addProgressLog('❌ No group selected', 'error');
        toast.error('Please select a group to distribute to');
        setLoading(false);
        setOperationInProgress(false);
        return;
      }
      recipientAddresses = wallets.filter(w => {
        const wGroupName = w.group_name || 'No Group';
        return wGroupName === selectedDistributionGroup;
      }).map(w => w.address);
      
      if (recipientAddresses.length === 0) {
        addProgressLog(`❌ No wallets found in group "${selectedDistributionGroup}"`, 'error');
        toast.error(`No wallets found in group "${selectedDistributionGroup}"`);
        setLoading(false);
        setOperationInProgress(false);
        return;
      }
      addProgressLog(`📬 Recipients: Group "${selectedDistributionGroup}" (${recipientAddresses.length} wallets)`, 'info');
    }
    
    // Check sender balance before distribution
    addProgressLog('💰 Checking sender balance...', 'info');
    try {
      const senderWalletObj = wallets.find(w => w.address === senderAddress);
      if (senderWalletObj) {
        const senderBalance = parseFloat(senderWalletObj.ton_balance || 0);
        addProgressLog(`  Sender balance: ${senderBalance.toFixed(4)} TON`, 'info');
        
        // Calculate required amount
        let requiredAmount = 0;
        if (distributionAmountType === 'percentage') {
          // For percentage, estimate with average
          const avgPercent = (distributionMinPercent + distributionMaxPercent) / 2;
          requiredAmount = (senderBalance * avgPercent / 100);
        } else {
          requiredAmount = amountPerWallet * recipientAddresses.length;
        }
        
        // Add buffer for gas fees (0.1 TON per transaction)
        const gasBuffer = 0.1 * recipientAddresses.length;
        const totalRequired = requiredAmount + gasBuffer;
        
        addProgressLog(`  Required: ${totalRequired.toFixed(4)} TON (${requiredAmount.toFixed(4)} + ${gasBuffer.toFixed(4)} gas)`, 'info');
        
        if (senderBalance < totalRequired) {
          addProgressLog(`❌ Insufficient balance! Need ${totalRequired.toFixed(4)} TON, have ${senderBalance.toFixed(4)} TON`, 'error');
          toast.error(`Insufficient balance: need ${totalRequired.toFixed(2)} TON, have ${senderBalance.toFixed(2)} TON`);
          setLoading(false);
          setOperationInProgress(false);
          return;
        }
        
        addProgressLog(`✅ Balance check passed`, 'success');
      } else {
        addProgressLog('⚠️ Sender wallet not in managed list, skipping balance check', 'warning');
      }
    } catch (error) {
      addProgressLog(`⚠️ Could not check balance: ${error.message}`, 'warning');
    }
    
    addProgressLog(`💰 Amount type: ${distributionAmountType}`, 'info');
    if (intermediateHops > 0) {
      addProgressLog(`🔀 Anonymization: ${intermediateHops} intermediate hops`, 'info');
    }

    try {
      addProgressLog('📤 Sending distribution request to backend...', 'info');
      
      const requestPayload = {
        from_address: senderAddress,
        from_mnemonic: senderMnemonic,
        to_addresses: recipientAddresses,
        amount_type: distributionAmountType,
        delay_seconds: distributionDelay,
        comment: distributionComment,
        intermediate_hops: intermediateHops,
        parallel: distributeParallel,
        concurrency: distributeConcurrency,
        background: true
      };
      
      // Add amount parameters based on type
      if (distributionAmountType === 'percentage') {
        requestPayload.min_percent = distributionMinPercent;
        requestPayload.max_percent = distributionMaxPercent;
        addProgressLog(`  Range: ${distributionMinPercent}% - ${distributionMaxPercent}%`, 'info');
      } else {
        requestPayload.amount_per_wallet = amountPerWallet;
        addProgressLog(`  Amount per wallet: ${amountPerWallet} TON`, 'info');
      }
      
      const response = await axios.post(`${API_URL}/api/wallets/distribute-ton`, requestPayload);

      // Фоновый режим: дальше прогресс ведёт поллинг (переживает рефреш)
      if (response.data.background && response.data.task_id) {
        const taskId = response.data.task_id;
        addProgressLog(`🆔 Рассылка запущена в фоне (task ${taskId.slice(0, 8)}…). Прогресс — внизу ⬇️`, 'info');
        const init = { taskId, operation: 'distribute', total: response.data.total, completed: 0, successful: 0, failed: 0, status: 'running' };
        localStorage.setItem('distributeTask', JSON.stringify(init));
        setDistributeTask({ ...init, logs: [] });
        pollDistributeStatus(taskId);
        setLoading(false);
        return;
      }

      const results = response.data.results;
      addProgressLog(`\n📊 Distribution completed!`, 'success');
      addProgressLog(`✅ Successful: ${results.success.length}`, 'success');
      addProgressLog(`❌ Failed: ${results.failed.length}`, results.failed.length > 0 ? 'error' : 'info');

      if (results.failed.length > 0) {
        addProgressLog('\n⚠️ Failed transactions:', 'warning');
        results.failed.forEach(failure => {
          addProgressLog(`  • ${truncateAddress(failure.address)} - ${failure.error}`, 'error');
        });
      }

      if (results.success.length > 0) {
        addProgressLog('\n✅ Successful transactions:', 'success');
        results.success.slice(0, 5).forEach(success => {
          addProgressLog(`  • ${truncateAddress(success.address)}`, 'success');
        });
        if (results.success.length > 5) {
          addProgressLog(`  • ...and ${results.success.length - 5} more`, 'success');
        }
      }

      toast.success(`Distribution complete: ${results.success.length} success, ${results.failed.length} failed`);

      addProgressLog('\n🔄 Refreshing wallet data...', 'info');
      await loadWallets();
      addProgressLog('✅ Wallet data refreshed', 'success');
      setLoading(false);
      setOperationInProgress(false);
      addProgressLog('\n✅ Operation finished', 'success');
    } catch (error) {
      addProgressLog(`\n❌ Error: ${error.response?.data?.detail || error.message}`, 'error');
      toast.error(error.response?.data?.detail || 'Failed to distribute TON');
      setLoading(false);
      setOperationInProgress(false);
    }
  };

  // Poll mass trading task status
  const pollMassTradingStatus = async (taskId) => {
    try {
      const response = await axios.get(`${API_URL}/api/wallets/mass-trading/status/${taskId}`);
      const status = response.data.status;
      
      if (status) {
        // Update logs from server
        if (status.logs && status.logs.length > 0) {
          const newLogs = status.logs.slice(-50); // Last 50 logs
          setProgressLogs(newLogs.map(log => ({
            timestamp: new Date(log.timestamp).toLocaleTimeString(),
            message: log.message,
            level: log.level
          })));
        }
        
        // Save task to localStorage
        localStorage.setItem('massTradingTask', JSON.stringify({
          taskId,
          operation: status.operation,
          total: status.total,
          completed: status.completed,
          successful: status.successful,
          failed: status.failed,
          status: status.status
        }));
        setMassTradingTask(status);
        
        // Continue polling if still running
        if (status.status === 'running') {
          setTimeout(() => pollMassTradingStatus(taskId), 1500);
        } else {
          // Task finished
          localStorage.removeItem('massTradingTask');
          setOperationInProgress(false);
          setLoading(false);
          
          if (status.status === 'completed') {
            toast.success(`${status.operation === 'buy' ? 'Mass buy' : 'Mass sell'} completed: ${status.successful} success, ${status.failed} failed`);
          } else if (status.status === 'cancelled') {
            toast.warning('Operation was cancelled');
          } else if (status.status === 'error') {
            toast.error('Operation failed with error');
          }
          
          // Refresh wallet data
          await loadWallets();
          await loadJettonBalances();
        }
      }
    } catch (error) {
      // Задача не найдена (бэкенд перезапущен) — снимаем застрявшее состояние,
      // иначе loading навсегда true и кнопки (в т.ч. Distribute) задизейблены.
      if (error.response && error.response.status === 404) {
        console.warn('Mass trading task not found (404) — снимаю застрявшее состояние');
        localStorage.removeItem('massTradingTask');
        setMassTradingTask(prev => (prev ? { ...prev, status: 'interrupted' } : null));
        setOperationInProgress(false);
        setLoading(false);
        return;
      }
      console.error('Error polling mass trading status:', error);
      // Keep polling even on error
      setTimeout(() => pollMassTradingStatus(taskId), 3000);
    }
  };
  
  // Poll distribute (рассылка) task status — прогресс + переживание рефреша
  const pollDistributeStatus = async (taskId) => {
    try {
      const response = await axios.get(`${API_URL}/api/wallets/distribute/status/${taskId}`);
      const status = response.data.status;
      if (status) {
        if (status.logs && status.logs.length > 0) {
          const newLogs = status.logs.slice(-50);
          setProgressLogs(newLogs.map(log => ({
            timestamp: new Date(log.timestamp).toLocaleTimeString(),
            message: log.message,
            level: log.level
          })));
        }
        localStorage.setItem('distributeTask', JSON.stringify({
          taskId, operation: status.operation, total: status.total,
          completed: status.completed, successful: status.successful,
          failed: status.failed, uncertain: status.uncertain, status: status.status
        }));
        setDistributeTask(status);

        if (status.status === 'running') {
          setTimeout(() => pollDistributeStatus(taskId), 1500);
        } else {
          localStorage.removeItem('distributeTask');
          setOperationInProgress(false);
          setLoading(false);
          if (status.status === 'completed') {
            toast.success(`Рассылка завершена: ✅ ${status.successful}, ❌ ${status.failed}` + (status.uncertain ? `, ❓ ${status.uncertain}` : ''));
          } else if (status.status === 'cancelled') {
            toast.warning('Рассылка отменена');
          } else if (status.status === 'interrupted') {
            toast.warning('Рассылка прервана (перезапуск бэкенда)');
          } else if (status.status === 'error') {
            toast.error(`Рассылка с ошибкой: ${status.error || ''}`);
          }
          await loadWallets();
        }
      }
    } catch (error) {
      // Задача не найдена (бэкенд перезапущен / память очищена) — снимаем застрявшее
      // состояние, иначе loading навсегда true и кнопки задизейблены.
      if (error.response && error.response.status === 404) {
        console.warn('Distribute task not found (404) — снимаю застрявшее состояние');
        localStorage.removeItem('distributeTask');
        setDistributeTask(prev => (prev ? { ...prev, status: 'interrupted' } : null));
        setOperationInProgress(false);
        setLoading(false);
        return;
      }
      console.error('Error polling distribute status:', error);
      setTimeout(() => pollDistributeStatus(taskId), 3000);
    }
  };

  // Cancel distribute task
  const handleCancelDistribute = async () => {
    const saved = localStorage.getItem('distributeTask');
    if (!saved) return;
    try {
      const { taskId } = JSON.parse(saved);
      if (taskId) {
        await axios.post(`${API_URL}/api/wallets/distribute/cancel/${taskId}`);
        toast.success('Запрошена отмена рассылки...');
      }
    } catch (error) {
      console.error('Cancel distribute error:', error);
    }
  };

  // Продолжить рассылку ТОЛЬКО для недоставленных адресов — в том же окне (плашке)
  const handleResumeDistribute = async () => {
    const runId = distributeTask?.task_id || distributeTask?.taskId;
    if (!runId) { toast.error('Нет run_id для продолжения'); return; }
    try {
      const resp = await axios.post(`${API_URL}/api/wallets/distribute/resume/${runId}`);
      const newId = resp.data.task_id;
      const undelivered = resp.data.undelivered;
      toast.success(`Продолжаю: ${undelivered} недоставленных адрес(ов)`);
      const init = { taskId: newId, task_id: newId, operation: 'distribute', total: undelivered, completed: 0, successful: 0, failed: 0, status: 'running' };
      localStorage.setItem('distributeTask', JSON.stringify(init));
      setProgressLogs([]);
      setDistributeTask({ ...init, logs: [] });
      setOperationInProgress(true);
      pollDistributeStatus(newId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Не удалось продолжить рассылку');
    }
  };

  // Check for running tasks on mount (mass trading + distribute)
  useEffect(() => {
    const savedTask = localStorage.getItem('massTradingTask');
    if (savedTask) {
      try {
        const { taskId, status } = JSON.parse(savedTask);
        if (taskId && status === 'running') {
          setShowProgressDialog(true);
          setOperationInProgress(true);
          setLoading(true);
          pollMassTradingStatus(taskId);
        }
      } catch (e) {
        localStorage.removeItem('massTradingTask');
      }
    }
    const savedDist = localStorage.getItem('distributeTask');
    if (savedDist) {
      try {
        const { taskId, status } = JSON.parse(savedDist);
        if (taskId && status === 'running') {
          setShowProgressDialog(true);
          setOperationInProgress(true);
          setLoading(true);
          pollDistributeStatus(taskId);
        }
      } catch (e) {
        localStorage.removeItem('distributeTask');
      }
    }
  }, []);
  
  // Cancel mass trading task
  const handleCancelMassTrading = async () => {
    const savedTask = localStorage.getItem('massTradingTask');
    if (savedTask) {
      try {
        const { taskId } = JSON.parse(savedTask);
        if (taskId) {
          await axios.post(`${API_URL}/api/wallets/mass-trading/cancel/${taskId}`);
          toast.success('Cancel requested...');
        }
      } catch (error) {
        console.error('Cancel error:', error);
        toast.error('Failed to cancel');
      }
    }
  };

  // ===== TON Rebalance handlers =====
  const handleRebalancePreview = async () => {
    if (!rbSourceGroup || !rbDestGroup) { toast.warning('Выберите source и dest группы'); return; }
    if (rbSourceGroup === rbDestGroup) { toast.warning('Source и dest группы должны отличаться'); return; }
    setRbLoading(true);
    setRbPlan(null);
    try {
      const resp = await axios.post(`${API_URL}/api/wallets/rebalance/plan`, {
        source_group: rbSourceGroup,
        dest_group: rbDestGroup,
        source_reserve_ton: Number(rbReserve) || 0,
        min_leg_ton: Number(rbMinLeg) || 0,
      });
      if (resp.data.success) {
        setRbPlan(resp.data);
        const s = resp.data.summary || {};
        toast.success(`План: ${s.leg_count} ног, ~${(s.planned_total_ton || 0).toFixed(2)} TON`);
      } else {
        toast.error(resp.data.error || 'Не удалось построить план');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ошибка плана');
    } finally {
      setRbLoading(false);
    }
  };

  const pollRebalanceStatus = async (taskId) => {
    try {
      const resp = await axios.get(`${API_URL}/api/wallets/rebalance/status/${taskId}`);
      const st = resp.data.status;
      setRbTask(st);
      if (st && (st.status === 'running')) {
        rbPollRef.current = setTimeout(() => pollRebalanceStatus(taskId), 2000);
      } else {
        if (rbPollRef.current) clearTimeout(rbPollRef.current);
        await loadWallets();
        if (st) toast[st.status === 'completed' ? 'success' : 'warning'](
          `Rebalance ${st.status}: ${st.success_legs}/${st.total_legs} ног, ~${(st.moved_ton || 0).toFixed(2)} TON`
        );
      }
    } catch (e) {
      if (rbPollRef.current) clearTimeout(rbPollRef.current);
      console.error('rebalance poll error', e);
    }
  };

  const handleRebalanceStart = async () => {
    if (!rbPlan || !(rbPlan.legs || []).length) { toast.warning('Сначала Preview'); return; }
    const s = rbPlan.summary || {};
    if (!window.confirm(
      `Перекинуть ~${(s.planned_total_ton || 0).toFixed(2)} TON из «${rbSourceGroup}» в «${rbDestGroup}» ` +
      `(${s.leg_count} ног, через ${rbHopsMin}-${rbHopsMax} свежих транзита на ногу)?`
    )) return;
    setRbLoading(true);
    try {
      const resp = await axios.post(`${API_URL}/api/wallets/rebalance/start`, {
        source_group: rbSourceGroup,
        dest_group: rbDestGroup,
        source_reserve_ton: Number(rbReserve) || 0,
        min_leg_ton: Number(rbMinLeg) || 0,
        hops_min: Number(rbHopsMin) || 2,
        hops_max: Number(rbHopsMax) || 3,
      });
      if (resp.data.success && resp.data.task_id) {
        toast.success('Rebalance запущен');
        setRbTask({ status: 'running', total_legs: resp.data.legs, completed_legs: 0, success_legs: 0, failed_legs: 0, moved_ton: 0, logs: [] });
        pollRebalanceStatus(resp.data.task_id);
      } else {
        toast.error(resp.data.error || 'Не удалось запустить');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ошибка запуска');
    } finally {
      setRbLoading(false);
    }
  };

  // ===== Pump Cycle handlers =====
  const pcMmUrl = `${API_URL}/api/market-maker/pump-cycle`;
  const handlePumpPreview = async () => {
    if (!pcBuyGroup || !pcSellGroup) { toast.warning('Выберите buy и sell группы'); return; }
    if (pcBuyGroup === pcSellGroup) { toast.warning('Buy и sell группы должны отличаться'); return; }
    setPcLoading(true); setPcPlan(null);
    try {
      const body = {
        direction: pcDirection, buy_group: pcBuyGroup, sell_group: pcSellGroup,
        buy_count_min: Number(pcSteps), buy_count_max: Number(pcSteps),
        phase1_total_ton: pcTurnoverTon ? Number(pcTurnoverTon) : undefined,
        buy_start_ton: Number(pcBuyStartTon), buy_end_ton: Number(pcBuyEndTon),
        sell_multiplier: Number(pcSellMult), sell_count: Number(pcSellCount), slippage: Number(pcSlippage),
      };
      const r = await axios.post(`${pcMmUrl}/plan`, body);
      if (r.data.success) { setPcPlan(r.data); toast.success('План готов'); }
      else toast.error(r.data.error || 'Не удалось построить план');
    } catch (e) { toast.error(e.response?.data?.detail || 'Ошибка плана'); }
    finally { setPcLoading(false); }
  };
  const pollPump = async (taskId) => {
    try {
      const r = await axios.get(`${pcMmUrl}/status/${taskId}`);
      const st = r.data.status; setPcTask(st);
      if (st && st.status === 'running') pcPollRef.current = setTimeout(() => pollPump(taskId), 2500);
      else { if (pcPollRef.current) clearTimeout(pcPollRef.current); await loadWallets(); }
    } catch (e) { if (pcPollRef.current) clearTimeout(pcPollRef.current); }
  };
  const handlePumpStart = async () => {
    if (!pcPlan?.summary) { toast.warning('Сначала Preview'); return; }
    const s = pcPlan.summary;
    if (!window.confirm(
      `Запустить памп-цикл: ${s.buy_count} нарастающих покупок (~${s.total_buy_usd}$) из «${pcBuyGroup}», ` +
      `затем слив ~$${s.target_sell_usd} (${(s.planned_sell_legs||[]).length} продаж) из «${pcSellGroup}»? Это РЕАЛЬНЫЕ сделки.`
    )) return;
    setPcLoading(true);
    try {
      const body = {
        direction: pcDirection, buy_group: pcBuyGroup, sell_group: pcSellGroup,
        buy_count_min: Number(pcSteps), buy_count_max: Number(pcSteps),
        phase1_total_ton: pcTurnoverTon ? Number(pcTurnoverTon) : undefined,
        buy_start_ton: Number(pcBuyStartTon), buy_end_ton: Number(pcBuyEndTon),
        sell_multiplier: Number(pcSellMult), sell_count: Number(pcSellCount), slippage: Number(pcSlippage),
      };
      const r = await axios.post(`${pcMmUrl}/start`, body);
      if (r.data.success && r.data.task_id) {
        toast.success('Памп-цикл запущен');
        setPcTask({ status: 'running', phase: 'accumulate', legs: [], logs: [], report: null });
        pollPump(r.data.task_id);
      } else toast.error(r.data.error || 'Не удалось запустить');
    } catch (e) { toast.error(e.response?.data?.detail || 'Ошибка запуска'); }
    finally { setPcLoading(false); }
  };

  const handleMassBuy = async () => {
    if (selectedWallets.length === 0) {
      toast.error('Please select wallets');
      return;
    }
    if (!tokenAddress) {
      toast.error('Please enter token address');
      return;
    }

    // Reset progress - no popup, inline display
    setProgressLogs([]);
    setOperationInProgress(true);
    setLoading(true);
    
    addProgressLog(`🚀 Starting mass buy for ${selectedWallets.length} wallet(s)...`, 'info');
    addProgressLog(`💎 Token: ${truncateAddress(tokenAddress)}`, 'info');
    addProgressLog(`💰 Amount type: ${amountType}`, 'info');
    
    try {
      addProgressLog('📤 Sending request to backend...', 'info');
      
      const response = await axios.post(`${API_URL}/api/wallets/mass-buy`, {
        addresses: selectedWallets,
        token_address: tokenAddress,
        amount_type: amountType,
        min_percent: amountType === 'percentage' ? minPercent : null,
        max_percent: amountType === 'percentage' ? maxPercent : null,
        min_amount_ton: amountType === 'absolute' ? minAmountTon : null,
        max_amount_ton: amountType === 'absolute' ? maxAmountTon : null,
        min_delay_seconds: minTradeDelay,  // 0 = памп без задержек, true concurrent
        max_delay_seconds: maxTradeDelay,
        slippage: slippage,
        background: true  // Always use background mode
      });
      
      if (response.data.background && response.data.task_id) {
        // Background mode - start polling
        const taskId = response.data.task_id;
        addProgressLog(`✅ Task started: ${taskId.substring(0, 8)}...`, 'success');
        addProgressLog(`📊 Processing ${response.data.total} wallets...`, 'info');
        
        localStorage.setItem('massTradingTask', JSON.stringify({
          taskId,
          operation: 'buy',
          total: response.data.total,
          status: 'running'
        }));
        
        // Start polling
        pollMassTradingStatus(taskId);
      } else {
        // Synchronous mode (fallback)
        const results = response.data.results;
        
        addProgressLog(`\n📊 Operation completed!`, 'success');
        addProgressLog(`✅ Successful: ${results.success.length}`, 'success');
        addProgressLog(`❌ Failed: ${results.failed.length}`, results.failed.length > 0 ? 'error' : 'info');
        
        if (results.failed.length > 0) {
          addProgressLog('\n⚠️ Failed transactions:', 'warning');
          results.failed.forEach(failure => {
            addProgressLog(`  • ${truncateAddress(failure.address)} - ${failure.error}`, 'error');
          });
        }
        
        if (results.success.length > 0) {
          addProgressLog('\n✅ Successful transactions:', 'success');
          results.success.forEach(success => {
            const txHash = success.transaction_hash || 'pending';
            addProgressLog(`  • ${truncateAddress(success.address)} - TX: ${truncateAddress(txHash, 16, 8)}`, 'success');
          });
          toast.success(`Mass buy completed for ${results.success.length} wallet(s)`);
        }
        
        await loadWallets();
        setLoading(false);
        setOperationInProgress(false);
      }
      
    } catch (error) {
      const d = error.response?.data?.detail;
      // 422 -> detail это массив {loc,msg}; делаем читаемо (напр. «slippage: ≤ 100»)
      const reason = Array.isArray(d)
        ? d.map((e) => `${(e.loc || []).slice(-1)[0]}: ${e.msg}`).join('; ')
        : (d || error.message);
      addProgressLog(`\n❌ Error: ${reason}`, 'error');
      toast.error(`Mass buy failed — ${reason}`);
      setLoading(false);
      setOperationInProgress(false);
    }
  };

  const handleMassSell = async () => {
    if (selectedWallets.length === 0) {
      toast.error('Please select wallets');
      return;
    }

    // Reset progress - no popup, inline display
    setProgressLogs([]);
    setOperationInProgress(true);
    setLoading(true);
    
    addProgressLog(`🚀 Starting mass sell for ${selectedWallets.length} wallet(s)...`, 'info');
    addProgressLog(`💎 Token: ${truncateAddress(tokenAddress)}`, 'info');
    addProgressLog(`📊 Sell range: ${minPercent}% - ${maxPercent}%`, 'info');
    
    try {
      addProgressLog('📤 Sending request to backend...', 'info');
      
      const response = await axios.post(`${API_URL}/api/wallets/mass-sell`, {
        addresses: selectedWallets,
        token_address: tokenAddress,
        min_percent: minPercent,
        max_percent: maxPercent,
        min_delay_seconds: minTradeDelay,  // 0 = памп без задержек, true concurrent
        max_delay_seconds: maxTradeDelay,
        slippage: slippage,
        background: true  // Always use background mode
      });
      
      if (response.data.background && response.data.task_id) {
        // Background mode - start polling
        const taskId = response.data.task_id;
        addProgressLog(`✅ Task started: ${taskId.substring(0, 8)}...`, 'success');
        addProgressLog(`📊 Processing ${response.data.total} wallets...`, 'info');
        
        localStorage.setItem('massTradingTask', JSON.stringify({
          taskId,
          operation: 'sell',
          total: response.data.total,
          status: 'running'
        }));
        
        // Start polling
        pollMassTradingStatus(taskId);
      } else {
        // Synchronous mode (fallback)
        const results = response.data.results;
        
        addProgressLog(`\n📊 Operation completed!`, 'success');
        addProgressLog(`✅ Successful: ${results.success?.length || 0}`, 'success');
        addProgressLog(`❌ Failed: ${results.failed?.length || 0}`, results.failed?.length > 0 ? 'error' : 'info');
        
        if (results.failed && results.failed.length > 0) {
          addProgressLog('\n⚠️ Failed transactions:', 'warning');
          results.failed.forEach(failure => {
            addProgressLog(`  • ${truncateAddress(failure.address)} - ${failure.error}`, 'error');
          });
        }
        
        if (results.success && results.success.length > 0) {
          toast.success(`Mass sell completed for ${results.success.length} wallet(s)`);
        }
        
        await loadWallets();
        await loadJettonBalances();
        setLoading(false);
        setOperationInProgress(false);
      }
      
    } catch (error) {
      addProgressLog(`\n❌ Error: ${error.response?.data?.detail || error.message}`, 'error');
      toast.error('Failed to execute mass sell');
      setLoading(false);
      setOperationInProgress(false);
    }
  };

  const toggleWalletSelection = (address) => {
    setSelectedWallets(prev => 
      prev.includes(address) 
        ? prev.filter(a => a !== address)
        : [...prev, address]
    );
  };

  // Фильтрация кошельков по выбранной группе
  const filteredWallets = React.useMemo(() => {
    console.log('🟠 Фильтрация кошельков:', { filteredGroup, всегоКошельков: wallets.length });
    
    let result;
    if (!filteredGroup) {
      console.log('⚪ Нет фильтра, возвращаем все кошельки');
      result = wallets;
    } else {
      const filtered = wallets.filter(w => {
        const wGroupName = (w.group_name || 'No Group').trim();
        const filterGroupName = filteredGroup.trim();
        const matches = wGroupName === filterGroupName;
        return matches;
      });
      
      console.log('✅ Отфильтровано кошельков:', filtered.length, 'из', wallets.length);
      console.log('📋 Группы в отфильтрованном списке:', [...new Set(filtered.map(w => w.group_name || 'No Group'))]);
      
      result = filtered;
    }
    
    // Apply sorting if active
    if (sortField) {
      result = [...result].sort((a, b) => {
        let aValue, bValue;
        
        if (sortField === 'ton') {
          // Sort by TON balance
          aValue = parseFloat(a.ton_balance) || 0;
          bValue = parseFloat(b.ton_balance) || 0;
        } else if (sortField === 'token') {
          // Sort by Token balance
          const aBalance = getJettonBalance(a.address);
          const bBalance = getJettonBalance(b.address);
          aValue = aBalance?.balance_readable || 0;
          bValue = bBalance?.balance_readable || 0;
        }
        
        if (sortDirection === 'desc') {
          return bValue - aValue; // От большего к меньшему
        } else {
          return aValue - bValue; // От меньшего к большему
        }
      });
    }
    
    return result;
  }, [filteredGroup, wallets, sortField, sortDirection, jettonBalances]);

  const pagedWallets = React.useMemo(() => filteredWallets.slice(0, visibleCount), [filteredWallets, visibleCount]);
  React.useEffect(() => { setVisibleCount(60); }, [filteredGroup]);

  const selectAllWallets = () => {
    setSelectedWallets(filteredWallets.map(w => w.address));
  };

  const deselectAllWallets = () => {
    setSelectedWallets([]);
  };

  // Handle column sort
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(prev => prev === 'desc' ? 'asc' : 'desc');
    } else {
      // New field, default to descending (большее к меньшему)
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Get sort icon for column
  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    }
    if (sortDirection === 'desc') {
      return <ArrowDown className="h-4 w-4 ml-1" />;
    }
    return <ArrowUp className="h-4 w-4 ml-1" />;
  };

  const selectWalletsByGroup = (groupName) => {
    const groupWallets = wallets.filter(w => {
      const wGroupName = w.group_name || 'No Group';
      return wGroupName === groupName;
    }).map(w => w.address);
    
    setSelectedWallets(groupWallets);
    toast.success(`Selected ${groupWallets.length} wallets from ${groupName}`);
  };

  // Обработчик клика на карточку группы - устанавливает фильтр
  const handleGroupFilter = (groupName) => {
    console.log('🔵 handleGroupFilter вызван:', { groupName, текущийFilteredGroup: filteredGroup });
    
    if (filteredGroup === groupName) {
      // Если группа уже выбрана, сбрасываем фильтр
      console.log('🟢 Сбрасываем фильтр');
      setFilteredGroup(null);
      forceUpdate(prev => prev + 1);
      toast.info('Показаны все группы');
    } else {
      // Устанавливаем фильтр
      console.log('🟡 Устанавливаем фильтр для группы:', groupName);
      setFilteredGroup(groupName);
      forceUpdate(prev => prev + 1);
      toast.success(`Показаны кошельки группы: ${groupName}`);
    }
  };

  const getWalletGroups = () => {
    const groupsMap = {};
    wallets.forEach(wallet => {
      const groupName = wallet.group_name || 'No Group';
      if (!groupsMap[groupName]) {
        groupsMap[groupName] = {
          name: groupName,
          count: 0,
          totalTON: 0,
          totalBUYT: 0
        };
      }
      groupsMap[groupName].count++;
      groupsMap[groupName].totalTON += (parseInt(wallet.ton_balance || 0)) / 1e9;
      
      const balance = getJettonBalance(wallet.address);
      if (balance) {
        groupsMap[groupName].totalBUYT += balance.balance_readable || 0;
      }
    });
    return Object.values(groupsMap);
  };

  // ===== Pairwise 1-to-1 token transfer (group A → group B / custom list) =====
  const PAIRWISE_GAS_MIN_TON = 0.45; // прикладываем ~0.4 TON газа на jetton-перевод + комиссия
  const buildPairwiseMapping = () => {
    const aWallets = wallets
      .filter(w => (w.group_name || 'No Group') === pairwiseSourceGroup)
      .slice()
      .sort((x, y) => x.address.localeCompare(y.address));
    let bAddrs = [];
    if (pairwiseDestMode === 'group') {
      bAddrs = wallets
        .filter(w => (w.group_name || 'No Group') === pairwiseDestGroup)
        .map(w => w.address)
        .sort((x, y) => x.localeCompare(y));
    } else {
      bAddrs = (pairwiseDestCustom || '').split(/[\n,\s]+/).map(s => s.trim()).filter(Boolean);
    }
    if (aWallets.length === 0) { toast.error('Группа A пуста — выберите группу источников'); return; }
    if (bAddrs.length === 0) { toast.error('Группа/список B пуст — выберите получателей'); return; }
    const isFixed = pairwiseAmountMode === 'fixed';
    const fixedAmt = parseFloat(pairwiseFixedAmount);
    if (isFixed && (!fixedAmt || fixedAmt <= 0)) { toast.error('Укажите фиксированное количество токенов (> 0)'); return; }
    const pairs = aWallets.map((w, i) => {
      const overflow = i >= bAddrs.length;
      const dest = overflow ? bAddrs[Math.floor(Math.random() * bAddrs.length)] : bAddrs[i];
      const jb = getJettonBalance(w.address);
      const tokenBal = (jb && jb.balance_readable) || 0;
      const tonBal = (parseInt(w.ton_balance || 0)) / 1e9;
      const gasOk = tonBal >= PAIRWISE_GAS_MIN_TON;
      const sendAmt = isFixed ? fixedAmt : tokenBal;
      let reason = '';
      if (!gasOk) reason = `газ ${tonBal.toFixed(3)} TON`;
      else if (tokenBal <= 0) reason = '0 токенов';
      else if (isFixed && tokenBal < fixedAmt) reason = `мало: ${tokenBal.toFixed(2)} < ${fixedAmt}`;
      const skip = reason !== '';
      return { source: w.address, dest, tokenBal, tonBal, gasOk, sendAmt, skip, overflow, reason };
    });
    setPairwiseMapping({ pairs, aCount: aWallets.length, bCount: bAddrs.length, isFixed, fixedAmt: isFixed ? fixedAmt : null });
  };

  const executePairwise = async () => {
    if (!pairwiseMapping) { toast.error('Сначала постройте маппинг'); return; }
    const sendable = pairwiseMapping.pairs.filter(p => !p.skip).map(p => ({ source: p.source, dest: p.dest }));
    if (sendable.length === 0) { toast.error('Нет пар к отправке (нет газа/баланса)'); return; }
    const totalTokens = pairwiseMapping.pairs.filter(p => !p.skip).reduce((s, p) => s + p.sendAmt, 0);
    const amtDesc = pairwiseMapping.isFixed
      ? `по ${pairwiseMapping.fixedAmt} ${tokenSymbol || 'TOKEN'} с каждого`
      : `ВЕСЬ баланс токенов`;
    if (!window.confirm(
      `Перекинуть ${amtDesc} 1-к-1 с ${sendable.length} кошельк(ов) ` +
      `(≈${totalTokens.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${tokenSymbol || 'TOKEN'} всего) на их пары из B?\n\n` +
      `Это необратимо. Рекомендуется остановить маркетмейкер на время переноса.`)) return;
    try {
      const resp = await axios.post(`${API_URL}/api/wallets/distribute-jetton-pairwise`, {
        pairs: sendable,
        amount_per_wallet: pairwiseMapping.isFixed ? pairwiseMapping.fixedAmt : null,
        delay_seconds: distributionDelay || 1,
        gas_min_ton: PAIRWISE_GAS_MIN_TON,
        background: true,
      });
      if (resp.data && resp.data.task_id) {
        toast.success(`Запущено: ${sendable.length} пар(ы). Следим за прогрессом…`);
        setDistributeTask({
          taskId: resp.data.task_id, operation: 'jetton_pairwise',
          total: resp.data.total, completed: 0, successful: 0, failed: 0, status: 'running', logs: [],
        });
        pollDistributeStatus(resp.data.task_id);
      }
    } catch (e) {
      toast.error('Ошибка запуска: ' + (e.response?.data?.detail || e.message));
    }
  };


  const formatBalance = (balance) => {
    const num = parseInt(balance || 0) / 1_000_000_000;
    return num.toFixed(4);
  };

  // Get jetton balance for a wallet (handles various key formats in jetton_balances)
  const getJettonBalanceForWallet = (wallet, jettonMasterAddress) => {
    if (!wallet?.jetton_balances || !jettonMasterAddress) return null;
    const jb = wallet.jetton_balances;
    const tryKey = (key) => {
      const val = jb[key];
      if (val == null) return null;
      const isObj = typeof val === 'object';
      const balance = isObj ? (val.balance ?? 0) : val;
      const decimals = isObj ? (val.decimals ?? 9) : 9;
      return { raw: parseInt(balance || 0), decimals };
    };
    let result = tryKey(jettonMasterAddress);
    if (result) return result;
    // Case-insensitive lookup (addresses are case-insensitive in TON)
    for (const key of Object.keys(jb)) {
      if (key.toLowerCase() === jettonMasterAddress.toLowerCase()) return tryKey(key);
    }
    return null;
  };

  const formatAddress = (addr) => {
    if (!addr || typeof addr !== 'string') return 'N/A';
    return addr;
  };

  const truncateAddress = (addr, prefix = 12, suffix = 8) => {
    if (!addr || typeof addr !== 'string') return 'N/A';
    if (addr.length <= prefix + suffix + 3) return addr;
    return `${addr.substring(0, prefix)}...${addr.substring(addr.length - suffix)}`;
  };

  const copyToClipboard = (text, label = 'Address') => {
    const done = () => toast.success(`${label} скопирован`);
    const fallback = () => {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.top = '-9999px';
        ta.style.opacity = '0';
        // Внутри Radix-диалога focus-trap отбирает фокус у textarea в body
        // до execCommand -> ничего не копируется. Кладём textarea ВНУТРЬ
        // открытого диалога, чтобы остаться в focus-scope.
        const container = document.querySelector('[role="dialog"]') || document.body;
        container.appendChild(ta);
        ta.focus();
        ta.select();
        ta.setSelectionRange(0, ta.value.length);
        const ok = document.execCommand('copy');
        container.removeChild(ta);
        if (ok) done(); else toast.error('Не удалось скопировать');
      } catch (e) {
        toast.error('Не удалось скопировать');
      }
    };
    // navigator.clipboard доступен только на HTTPS/localhost; pubgton по HTTP -> fallback
    try {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text).then(done).catch(fallback);
      } else {
        fallback();
      }
    } catch (e) {
      fallback();
    }
  };

  const handleShowMnemonic = async (address) => {
    try {
      const response = await axios.get(`${API_URL}/api/wallets/wallet/${address}/mnemonic`);
      setWalletMnemonic(response.data.mnemonic);
      setSelectedWalletForMnemonic(address);
      setWalletVersionForMnemonic(response.data.wallet_version || 'v4r2');
      setShowMnemonicDialog(true);
    } catch (error) {
      toast.error('Failed to load mnemonic');
    }
  };

  const handleUpdateWalletGroup = async (address, newGroupName) => {
    try {
      const response = await axios.post(`${API_URL}/api/wallets/update-group`, {
        addresses: [address],
        new_group_name: newGroupName
      });
      
      if (response.data.success) {
        toast.success(`Группа изменена на "${newGroupName}"`);
        // Обновить локальное состояние
        setWallets(prevWallets => 
          prevWallets.map(w => 
            w.address === address ? { ...w, group_name: newGroupName } : w
          )
        );
        // Перезагрузить группы
        loadGroups();
      }
    } catch (error) {
      toast.error(`Ошибка изменения группы: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleBulkUpdateGroup = async () => {
    if (selectedWallets.length === 0) {
      toast.warning('Выберите хотя бы один кошелек');
      return;
    }

    const newGroupName = prompt('Введите новое имя группы:');
    if (!newGroupName || !newGroupName.trim()) {
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API_URL}/api/wallets/update-group`, {
        addresses: selectedWallets,
        new_group_name: newGroupName.trim()
      });
      
      if (response.data.success) {
        toast.success(`Группа изменена для ${response.data.updated} кошельков`);
        // Обновить локальное состояние
        setWallets(prevWallets => 
          prevWallets.map(w => 
            selectedWallets.includes(w.address) ? { ...w, group_name: newGroupName.trim() } : w
          )
        );
        // Очистить выбор
        setSelectedWallets([]);
        // Перезагрузить группы
        loadGroups();
      }
    } catch (error) {
      toast.error(`Ошибка изменения группы: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Binance Integration Functions
  const loadBinanceCredentials = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/binance/credentials`);
      if (response.data.configured) {
        setBinanceApiKey(response.data.api_key);
        setBinanceApiSecret(response.data.api_secret);
        setBinanceConfigured(true);
        // Load balance if configured
        loadBinanceBalance();
      }
    } catch (error) {
      console.error('Error loading Binance credentials:', error);
    }
  };

  const saveBinanceCredentials = async () => {
    if (!binanceApiKey || !binanceApiSecret) {
      toast.error('Please enter both API Key and Secret');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/binance/credentials`, {
        api_key: binanceApiKey,
        api_secret: binanceApiSecret
      });
      
      setBinanceConfigured(true);
      toast.success('Binance credentials saved successfully');
      
      // Load balance after saving
      await loadBinanceBalance();
    } catch (error) {
      console.error('Error saving Binance credentials:', error);
      toast.error(error.response?.data?.detail || 'Failed to save Binance credentials');
    } finally {
      setLoading(false);
    }
  };

  const loadBinanceBalance = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/binance/balance`);
      setBinanceBalance(response.data);
    } catch (error) {
      console.error('Error loading Binance balance:', error);
      if (error.response?.status === 400) {
        setBinanceConfigured(false);
      }
    }
  };

  const handleBinanceDistribute = async () => {
    if (!binanceConfigured) {
      toast.error('Please configure Binance credentials first');
      return;
    }

    // Determine recipient addresses
    let recipientAddresses = [];
    if (binanceRecipientFilter === 'all') {
      recipientAddresses = wallets.map(w => w.address);
    } else if (binanceRecipientFilter === 'zero_balance') {
      recipientAddresses = wallets.filter(w => parseInt(w.ton_balance || 0) === 0).map(w => w.address);
    } else if (binanceRecipientFilter === 'groups' && binanceSelectedGroups.length > 0) {
      recipientAddresses = wallets
        .filter(w => binanceSelectedGroups.includes(w.group_name || 'No Group'))
        .map(w => w.address);
    } else {
      recipientAddresses = selectedWallets;
    }

    if (recipientAddresses.length === 0) {
      toast.error('No wallets selected for distribution');
      return;
    }

    // Validate amount parameters based on type
    let amountPerWallet = null;
    let minAmount = null;
    let maxAmount = null;
    let minPercent = null;
    let maxPercent = null;

    if (binanceAmountType === 'absolute') {
      if (binanceMinAmount <= 0 || binanceMaxAmount <= 0) {
        toast.error('Amount must be greater than 0');
        return;
      }
      if (binanceMinAmount > binanceMaxAmount) {
        toast.error('Min amount must be less than or equal to max amount');
        return;
      }
      minAmount = binanceMinAmount;
      maxAmount = binanceMaxAmount;
      // Calculate average for total required estimation
      amountPerWallet = (binanceMinAmount + binanceMaxAmount) / 2;
    } else {
      if (binanceMinPercent <= 0 || binanceMaxPercent <= 0 || binanceMinPercent > 100 || binanceMaxPercent > 100) {
        toast.error('Percent must be between 0 and 100');
        return;
      }
      if (binanceMinPercent > binanceMaxPercent) {
        toast.error('Min percent must be less than or equal to max percent');
        return;
      }
      minPercent = binanceMinPercent;
      maxPercent = binanceMaxPercent;
      // For percentage mode, we need balance to calculate
      if (!binanceBalance || !binanceBalance.free) {
        toast.error('Cannot calculate percentage: balance not available');
        return;
      }
      // For percentage mode, amounts are calculated dynamically from remaining balance
      // So we don't need to check total required upfront - backend handles it dynamically
      amountPerWallet = null; // Not used for percentage mode
    }

    // Only check balance for absolute mode (percentage mode is handled dynamically on backend)
    if (binanceAmountType === 'absolute') {
      const totalRequired = amountPerWallet * recipientAddresses.length;
      if (binanceBalance && totalRequired > binanceBalance.free) {
        toast.error(`Insufficient balance. Estimated required: ${totalRequired.toFixed(2)} TON, Available: ${binanceBalance.free} TON`);
        return;
      }
    }

    setLoading(true);
    let taskId = null;
    let progressInterval = null;
    
    try {
      toast.info(`Starting distribution to ${recipientAddresses.length} wallets...`);
      
      const requestData = {
        addresses: recipientAddresses,
        amount_type: binanceAmountType,
        delay_seconds: 10.0
      };

      if (binanceAmountType === 'absolute') {
        requestData.min_amount = minAmount;
        requestData.max_amount = maxAmount;
      } else {
        requestData.min_percent = minPercent;
        requestData.max_percent = maxPercent;
      }
      
      // Start distribution (async)
      const response = await axios.post(`${API_URL}/api/binance/distribute`, requestData);
      taskId = response.data.task_id;
      
      toast.info(`Distribution started. Task ID: ${taskId.substring(0, 8)}...`, {
        description: `Processing ${recipientAddresses.length} wallets with 10-20s delays between each`
      });
      
      // Poll for status
      const checkStatus = async () => {
        try {
          const statusResponse = await axios.get(`${API_URL}/api/binance/distribute/status/${taskId}`);
          const status = statusResponse.data;
          
          const { current, total, successful, failed } = status.progress;
          const percent = total > 0 ? Math.round((current / total) * 100) : 0;
          
          // Update progress in toast or show progress dialog
          if (status.status === 'running') {
            // Show progress
            toast.loading(
              `Processing: ${current}/${total} (${percent}%) - Success: ${successful}, Failed: ${failed}`,
              { id: `binance-progress-${taskId}` }
            );
          } else if (status.status === 'completed') {
            // Clear progress toast
            toast.dismiss(`binance-progress-${taskId}`);
            
            // Clear interval
            if (progressInterval) {
              clearInterval(progressInterval);
              progressInterval = null;
            }
            
            // Show results
            if (status.failed && status.failed.length > 0) {
              // Log detailed errors to console
              console.group('❌ Failed Binance Withdrawals:');
              status.failed.forEach((failure, idx) => {
                console.error(`${idx + 1}. ${truncateAddress(failure.address, 12, 8)}:`, {
                  address: failure.address,
                  amount: failure.amount,
                  error: failure.error
                });
              });
              console.groupEnd();
              
              // Analyze common errors
              const whitelistErrors = status.failed.filter(f => 
                f.error && (f.error.toLowerCase().includes('whitelist') || 
                f.error.toLowerCase().includes('address not in addressbook'))
              );
              const balanceErrors = status.failed.filter(f => 
                f.error && f.error.toLowerCase().includes('insufficient balance')
              );
              
              let errorDescription = `Failed: ${status.failed.length}/${total}`;
              if (whitelistErrors.length > 0) {
                errorDescription += `\n⚠️ ${whitelistErrors.length} address(es) not whitelisted. Add them to Binance address book first.`;
              }
              if (balanceErrors.length > 0) {
                errorDescription += `\n⚠️ ${balanceErrors.length} withdrawal(s) failed due to insufficient balance.`;
              }
              
              toast.error(
                `Distribution completed with errors`,
                {
                  description: errorDescription,
                  duration: 15000
                }
              );
            } else {
              toast.success(
                `Distribution complete! ${status.successful?.length || 0}/${total} successful`
              );
            }
            
            // Refresh balance
            await loadBinanceBalance();
            setLoading(false);
          } else if (status.status === 'failed') {
            // Clear progress toast
            toast.dismiss(`binance-progress-${taskId}`);
            
            // Clear interval
            if (progressInterval) {
              clearInterval(progressInterval);
              progressInterval = null;
            }
            
            toast.error(`Distribution failed: ${status.error || 'Unknown error'}`);
            setLoading(false);
          }
        } catch (error) {
          console.error('Error checking status:', error);
          // Don't stop polling on error, just log it
        }
      };
      
      // Poll every 2 seconds
      progressInterval = setInterval(checkStatus, 2000);
      // Check immediately
      await checkStatus();
      
    } catch (error) {
      // Clear interval on error
      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }
      
      console.error('Error distributing from Binance:', error);
      toast.error(error.response?.data?.detail || 'Failed to start distribution');
      setLoading(false);
    }
  };

  const handleBinanceInternalTransfer = async () => {
    if (!binanceConfigured) {
      toast.error('Please configure Binance credentials first');
      return;
    }
    
    if (binanceTransferFrom === binanceTransferTo) {
      toast.error('Source and destination accounts must be different');
      return;
    }
    
    const normalizedAmount = parseFloat(binanceTransferAmount);
    if (!normalizedAmount || normalizedAmount <= 0) {
      toast.error('Amount must be greater than 0');
      return;
    }
    
    if (!binanceTransferAsset.trim()) {
      toast.error('Asset symbol is required');
      return;
    }
    
    setBinanceTransferLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/binance/transfer`, {
        asset: binanceTransferAsset.trim().toUpperCase(),
        amount: normalizedAmount,
        from_account: binanceTransferFrom,
        to_account: binanceTransferTo
      });
      
      toast.success(
        `Transfer ${response.data.amount} ${response.data.asset} initiated (${response.data.transfer_id})`
      );
      await loadBinanceBalance();
    } catch (error) {
      console.error('Error executing Binance internal transfer:', error);
      toast.error(error.response?.data?.detail || 'Failed to execute internal transfer');
    } finally {
      setBinanceTransferLoading(false);
    }
  };

  // Load available groups
  const loadAvailableGroups = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/wallets/groups`);
      if (response.data.success) {
        setAvailableGroups(response.data.groups);
      }
    } catch (error) {
      console.error('Error loading groups:', error);
    }
  };

  // Preview mnemonic - shows both wallet versions with balances
  const handlePreviewMnemonic = async () => {
    if (!importMnemonic) {
      toast.error('Введите мнемоническую фразу');
      return;
    }

    const words = importMnemonic.trim().split(/\s+/);
    if (words.length !== 24) {
      toast.error('Мнемоника должна содержать 24 слова');
      return;
    }

    setPreviewLoading(true);
    try {
      // Load groups first
      await loadAvailableGroups();
      
      const response = await axios.post(`${API_URL}/api/wallets/preview-mnemonic`, {
        mnemonic: importMnemonic.trim()
      });

      if (response.data.success) {
        setPreviewWallets(response.data.wallets);
        
        // Check if any wallet already exists
        const existingWallet = response.data.wallets.find(w => w.exists_in_db);
        if (existingWallet && existingWallet.group_name) {
          setImportGroupName(existingWallet.group_name);
        } else {
          setImportGroupName('Imported');
        }
        
        // Pre-select wallets with balance or existing in DB
        const defaultSelected = response.data.wallets
          .filter(w => w.address && (w.ton_balance > 0 || w.token_balance > 0 || w.exists_in_db))
          .map(w => w.version);
        setSelectedVersions(defaultSelected.length > 0 ? defaultSelected : ['v4r2']);
        setShowVersionDialog(true);
      }
    } catch (error) {
      console.error('Preview error:', error);
      toast.error(error.response?.data?.detail || 'Ошибка проверки мнемоники');
    } finally {
      setPreviewLoading(false);
    }
  };

  // Import selected wallet versions
  const handleImportSelectedVersions = async () => {
    if (selectedVersions.length === 0) {
      toast.error('Выберите хотя бы одну версию кошелька');
      return;
    }

    // Validate group name
    const groupName = createNewGroup ? newGroupName : importGroupName;
    if (!groupName || groupName.trim() === '') {
      toast.error('Укажите название группы');
      return;
    }

    setImportLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/wallets/import-mnemonic-versioned`, {
        mnemonic: importMnemonic.trim(),
        wallet_versions: selectedVersions,
        group_name: groupName.trim(),
        create_new_group: createNewGroup
      });

      if (response.data.success) {
        const imported = response.data.results.filter(r => r.success);
        // Разделить по action чтобы пользователь видел 'created' vs 'updated'.
        const created = imported.filter(r => r.action === 'created');
        const updated = imported.filter(r => r.action === 'updated');
        const failed = response.data.results.filter(r => !r.success);

        if (created.length > 0 && updated.length === 0) {
          toast.success(`✅ Создано ${created.length} кошелек(а) в группу "${groupName}"`);
        } else if (updated.length > 0 && created.length === 0) {
          toast.info(`ℹ️ Кошелёк уже существовал — обновлён mnemonic (${updated.length} версий)`);
        } else if (created.length > 0 && updated.length > 0) {
          toast.success(
            `✅ Создано ${created.length}, обновлено ${updated.length} кошельков в "${groupName}"`
          );
        } else {
          toast.warning('Ничего не импортировано');
        }
        if (failed.length > 0) {
          toast.error(`Ошибки: ${failed.map(f => `${f.version}: ${f.error}`).join(', ')}`);
        }

        // Clear fields and close dialog
        setImportMnemonic('');
        setDerivedAddress('');
        setShowVersionDialog(false);
        setPreviewWallets([]);
        setSelectedVersions([]);
        setImportGroupName('Imported');
        setCreateNewGroup(false);
        setNewGroupName('');

        // Reload wallets
        await loadWallets();
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.detail || 'Ошибка импорта');
    } finally {
      setImportLoading(false);
    }
  };

  // Toggle version selection
  const toggleVersionSelection = (version) => {
    setSelectedVersions(prev => 
      prev.includes(version) 
        ? prev.filter(v => v !== version)
        : [...prev, version]
    );
  };

  // Legacy import (direct, without dialog)
  const handleImportMnemonic = async () => {
    if (!importMnemonic) {
      toast.error('Введите мнемоническую фразу');
      return;
    }

    const words = importMnemonic.trim().split(/\s+/);
    if (words.length !== 24) {
      toast.error('Мнемоника должна содержать 24 слова');
      return;
    }

    // Use preview dialog instead of direct import
    await handlePreviewMnemonic();
  };

  // Handle mnemonic input change
  const handleMnemonicChange = (e) => {
    const value = e.target.value;
    setImportMnemonic(value);
    setDerivedAddress('');
  };

  const handleExportWallets = async (groupName = null) => {
    try {
      const response = await axios.get(`${API_URL}/api/wallets/export-all`);
      
      if (response.data.success) {
        let exportWallets = response.data.wallets || [];
        if (groupName) {
          exportWallets = exportWallets.filter(w => (w.group_name || '') === groupName);
          if (exportWallets.length === 0) {
            toast.error(`No wallets found in group "${groupName}"`);
            return;
          }
        }
        // Только мнемоники → .txt, по одной на строку (без лишних данных)
        if (exportOnlyMnemonics) {
          const mnemonics = exportWallets.map(w => w.mnemonic).filter(mn => mn && mn.trim());
          if (mnemonics.length === 0) {
            toast.error(`No mnemonics found${groupName ? ` in group "${groupName}"` : ''}`);
            return;
          }
          const blob = new Blob([mnemonics.join('\n\n') + '\n'], { type: 'text/plain' });
          const u = window.URL.createObjectURL(blob);
          const a2 = document.createElement('a');
          a2.href = u;
          const sg = groupName ? `_${String(groupName).replace(/[^a-zA-Z0-9_-]/g, '_')}` : '';
          a2.download = `mnemonics${sg}_${new Date().toISOString().split('T')[0]}.txt`;
          document.body.appendChild(a2); a2.click(); document.body.removeChild(a2);
          window.URL.revokeObjectURL(u);
          toast.success(`Exported ${mnemonics.length} mnemonics${groupName ? ` from "${groupName}"` : ''} (.txt)`);
          return;
        }
        const payload = { ...response.data, group_name: groupName || null, count: exportWallets.length, wallets: exportWallets };
        const dataStr = JSON.stringify(payload, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        
        // Create download link
        const url = window.URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        const safeGroup = groupName ? `_${String(groupName).replace(/[^a-zA-Z0-9_-]/g, '_')}` : '';
        link.download = `wallets_backup${safeGroup}_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.success(`Exported ${payload.count} wallets${groupName ? ` from group "${groupName}"` : ''} to file`);
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export wallets');
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const json = JSON.parse(e.target.result);
        setImportFileContent(json);
        toast.success(`File loaded: ${json.wallets?.length || 0} wallets found`);
      } catch (error) {
        toast.error('Invalid JSON file');
        setImportFileContent(null);
      }
    };
    reader.readAsText(file);
  };

  const handleImportBackup = async () => {
    if (!importFileContent || !importFileContent.wallets) {
      toast.error('Please select a valid backup file');
      return;
    }

    setImportLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/wallets/import-backup`, {
        wallets: importFileContent.wallets,
        overwrite_existing: overwriteExisting
      });

      if (response.data.success) {
        const { imported, updated, skipped, errors } = response.data;
        
        let message = [];
        if (imported > 0) message.push(`${imported} imported`);
        if (updated > 0) message.push(`${updated} updated`);
        if (skipped > 0) message.push(`${skipped} skipped`);
        if (errors.length > 0) message.push(`${errors.length} errors`);
        
        toast.success(`Backup restored: ${message.join(', ')}`);
        
        if (errors.length > 0) {
          console.error('Import errors:', errors);
        }
        
        // Clear and reload
        setImportFileContent(null);
        await loadWallets();
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.detail || 'Failed to import backup');
    } finally {
      setImportLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-3 md:p-6 space-y-4 md:space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2 md:gap-3">
            <Wallet className="h-6 w-6 md:h-8 md:w-8" />
            Wallet Manager
          </h1>
          <p className="text-sm md:text-base text-gray-600 mt-1">Manage multiple TON wallets for trading operations</p>
        </div>
        <Button onClick={() => loadWallets()} disabled={loading} size="sm" className="w-full md:w-auto">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards - Compact on Mobile */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2 md:gap-4">
        <Card>
          <CardContent className="p-3 md:pt-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-1">
              <div className="flex-1">
                <p className="text-xs md:text-sm text-gray-600">Total Wallets</p>
                <p className="text-lg md:text-2xl font-bold">{wallets.length}</p>
              </div>
              <Users className="h-5 w-5 md:h-8 md:w-8 text-blue-500 self-end md:self-auto" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-3 md:pt-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-1">
              <div className="flex-1">
                <p className="text-xs md:text-sm text-gray-600">Selected</p>
                <p className="text-lg md:text-2xl font-bold">{selectedWallets.length}</p>
                {selectedWallets.length > 0 && (() => {
                  const sel = new Set(selectedWallets);
                  let totalTon = 0, totalTokens = 0;
                  wallets.forEach(w => {
                    if (sel.has(w.address)) {
                      totalTon += (parseFloat(w.ton_balance) || 0) / 1e9;
                      const jb = getJettonBalance(w.address);
                      totalTokens += (jb?.balance_readable || 0);
                    }
                  });
                  return (
                    <p className="text-[10px] md:text-xs text-gray-500 mt-0.5 leading-tight">
                      {totalTokens.toLocaleString(undefined, { maximumFractionDigits: 2 })} {tokenSymbol || 'TOKEN'}
                      <br />
                      {totalTon.toLocaleString(undefined, { maximumFractionDigits: 4 })} TON
                    </p>
                  );
                })()}
              </div>
              <Activity className="h-5 w-5 md:h-8 md:w-8 text-purple-500 self-end md:self-auto" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-3 md:pt-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-1">
              <div className="flex-1">
                <p className="text-xs md:text-sm text-gray-600">Groups</p>
                <p className="text-lg md:text-2xl font-bold">{groups.length}</p>
              </div>
              <Wallet className="h-5 w-5 md:h-8 md:w-8 text-green-500 self-end md:self-auto" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-3 md:pt-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-1">
              <div className="flex-1">
                <p className="text-xs md:text-sm text-gray-600">Total Balance</p>
                <p className="text-base md:text-2xl font-bold">
                  {(wallets.reduce((sum, w) => sum + parseInt(w.ton_balance || 0), 0) / 1e9).toFixed(2)} TON
                </p>
                {tonPriceUsd > 0 && (
                  <p className="text-xs text-gray-500">
                    ${((wallets.reduce((sum, w) => sum + parseInt(w.ton_balance || 0), 0) / 1e9) * tonPriceUsd).toFixed(2)}
                  </p>
                )}
              </div>
              <DollarSign className="h-5 w-5 md:h-8 md:w-8 text-orange-500 self-end md:self-auto" />
            </div>
          </CardContent>
        </Card>

        <Card className="col-span-2 md:col-span-1">
          <CardContent className="p-3 md:pt-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-1">
              <div className="flex-1">
                <p className="text-xs md:text-sm text-gray-600">Total {tokenSymbol || 'TOKEN'}</p>
                <p className="text-base md:text-2xl font-bold">
                  {Object.values(jettonBalances).reduce((sum, b) => sum + (b?.balance_readable || 0), 0).toFixed(2)} {tokenSymbol || 'TOKEN'}
                </p>
                {jettonPriceUsd > 0 && (
                  <p className="text-xs text-gray-500">
                    ${(Object.values(jettonBalances).reduce((sum, b) => sum + (b?.balance_readable || 0), 0) * jettonPriceUsd).toFixed(2)}
                  </p>
                )}
              </div>
              <Activity className="h-5 w-5 md:h-8 md:w-8 text-blue-500 self-end md:self-auto" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs defaultValue="wallets" className="w-full">
        {/* Mobile: Scrollable tabs */}
        <div className="md:hidden overflow-x-auto scrollbar-hide">
          <TabsList className="inline-flex w-auto min-w-full">
            <TabsTrigger value="wallets" className="whitespace-nowrap">Wallets</TabsTrigger>
            <TabsTrigger value="analytics" className="whitespace-nowrap">Analytics</TabsTrigger>
            <TabsTrigger value="generate" className="whitespace-nowrap">Generate</TabsTrigger>
            <TabsTrigger value="distribute" className="whitespace-nowrap">Distribute</TabsTrigger>
            <TabsTrigger value="rebalance" className="whitespace-nowrap">Rebalance</TabsTrigger>
            <TabsTrigger value="pump" className="whitespace-nowrap">Pump</TabsTrigger>
            <TabsTrigger value="trade" className="whitespace-nowrap">Trade</TabsTrigger>
            <TabsTrigger value="warmup" className="whitespace-nowrap">Warmup</TabsTrigger>
            <TabsTrigger value="portfolio" className="whitespace-nowrap">Portfolio</TabsTrigger>
            <TabsTrigger value="binance" className="whitespace-nowrap">Binance</TabsTrigger>
            <TabsTrigger value="settings" className="whitespace-nowrap">Settings</TabsTrigger>
          </TabsList>
        </div>
        
        {/* Desktop: Grid tabs */}
        <TabsList className="hidden md:grid w-full grid-cols-11">
          <TabsTrigger value="wallets">Wallets</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="generate">Generate</TabsTrigger>
          <TabsTrigger value="distribute">Distribute</TabsTrigger>
          <TabsTrigger value="rebalance">Rebalance</TabsTrigger>
          <TabsTrigger value="pump">Pump</TabsTrigger>
          <TabsTrigger value="trade">Trade</TabsTrigger>
          <TabsTrigger value="warmup">Warmup</TabsTrigger>
          <TabsTrigger value="portfolio">Portfolio</TabsTrigger>
          <TabsTrigger value="binance">Binance</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        {/* Wallets Tab */}
        <TabsContent value="wallets" className="mt-4 md:mt-6">
          <Card>
            <CardHeader className="p-3 md:p-6">
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg md:text-xl">
                    Managed Wallets
                    {filteredGroup && (
                      <span className="ml-2 text-sm text-blue-600 font-normal">
                        (Группа: {filteredGroup})
                      </span>
                    )}
                  </CardTitle>
                  {filteredGroup && (
                    <Badge variant="secondary" className="text-xs">
                      Показано: {filteredWallets.length} из {wallets.length} кошельков
                    </Badge>
                  )}
                </div>
                {filteredWallets.length > visibleCount && (
                  <div className="flex items-center justify-center gap-2 py-2">
                    <span className="text-xs text-gray-500">Рендер: {visibleCount} из {filteredWallets.length}</span>
                    <Button size="sm" variant="outline" onClick={() => setVisibleCount(c => c + 100)}>Показать ещё 100</Button>
                    <Button size="sm" variant="ghost" onClick={() => setVisibleCount(filteredWallets.length)}>Все</Button>
                  </div>
                )}
                
                {/* Mobile: Stacked Buttons */}
                <div className="flex md:hidden flex-col gap-2">
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={selectAllWallets} className="flex-1">
                      Select All
                    </Button>
                    <Button size="sm" variant="outline" onClick={deselectAllWallets} className="flex-1">
                      Deselect All
                    </Button>
                  </div>
                  {selectedWallets.length > 0 && (
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={handleBulkUpdateGroup}
                      disabled={loading}
                      className="border-green-500 text-green-700 hover:bg-green-50"
                    >
                      Изменить группу ({selectedWallets.length})
                    </Button>
                  )}
                  
                  {/* Group Selection Buttons - Mobile */}
                  <div className="flex gap-2 flex-wrap">
                    {getWalletGroups().map(group => {
                      const formatNumber = (num) => Math.floor(num).toLocaleString('en-US');
                      const isFiltered = filteredGroup === group.name;
                      return (
                        <button 
                          key={group.name}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleGroupFilter(group.name);
                          }}
                          className={`border-2 rounded-lg px-2 py-1 text-left flex-1 min-w-[120px] transition-all ${
                            isFiltered
                              ? 'border-blue-600 bg-blue-200 shadow-md'
                              : 'border-blue-300 bg-blue-50 hover:bg-blue-100'
                          }`}
                        >
                          <div className="font-semibold text-xs text-blue-900">{group.name}</div>
                          <div className="text-[9px] text-blue-600">
                            {group.count} | T:{formatNumber(group.totalTON)} | {tokenSymbol?.charAt(0) || 'T'}:{formatNumber(group.totalBUYT)}
                          </div>
                        </button>
                      );
                    })}
                    {filteredGroup && (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setFilteredGroup(null);
                          toast.info('Показаны все группы');
                        }}
                        className="border-2 border-gray-400 bg-gray-100 hover:bg-gray-200 rounded-lg px-2 py-1 text-left flex-1 min-w-[120px]"
                      >
                        <div className="font-semibold text-xs text-gray-900">Показать все</div>
                        <div className="text-[9px] text-gray-600">Сбросить фильтр</div>
                      </button>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => {
                        const threshold = prompt('Выбрать кошельки с TON меньше чем (например, 0.5):', '0.5');
                        if (threshold) {
                          const thresholdValue = parseFloat(threshold);
                          const filtered = wallets.filter(w => (parseInt(w.ton_balance || 0) / 1e9) < thresholdValue);
                          setSelectedWallets(filtered.map(w => w.address));
                          toast.success(`Выбрано ${filtered.length} кошельков с TON < ${thresholdValue}`);
                        }
                      }}
                      className="flex-1"
                    >
                      TON &lt; N
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const threshold = prompt('Выбрать кошельки с TON больше чем (например, 0.5):', '0.5');
                        if (threshold) {
                          const thresholdValue = parseFloat(threshold);
                          const filtered = wallets.filter(w => (parseInt(w.ton_balance || 0) / 1e9) > thresholdValue);
                          setSelectedWallets(filtered.map(w => w.address));
                          toast.success(`Выбрано ${filtered.length} кошельков с TON > ${thresholdValue}`);
                        }
                      }}
                      className="flex-1"
                    >
                      TON &gt; N
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const threshold = prompt(`Выбрать кошельки с ${tokenSymbol || 'TOKEN'} больше чем (например, 1000):`, '1000');
                        if (threshold) {
                          const thresholdValue = parseFloat(threshold);
                          const filtered = wallets.filter(w => {
                            const balance = getJettonBalance(w.address);
                            return balance && balance.balance_readable > thresholdValue;
                          });
                          setSelectedWallets(filtered.map(w => w.address));
                          toast.success(`Выбрано ${filtered.length} кошельков с ${tokenSymbol || 'TOKEN'} > ${thresholdValue}`);
                        }
                      }}
                      className="flex-1"
                    >
                      {tokenSymbol || 'TOKEN'} &gt; N
                    </Button>
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={handleUpdateBalances} 
                    disabled={balanceUpdateProgress.updating || loading} 
                    className="w-full"
                  >
                    <RefreshCw className={`h-3 w-3 mr-1 ${balanceUpdateProgress.updating ? 'animate-spin' : ''}`} />
                    {balanceUpdateProgress.updating 
                      ? `Updating ${balanceUpdateProgress.current}/${balanceUpdateProgress.total}...` 
                      : 'Update Balances'
                    }
                  </Button>
                  
                  {/* Expanded Mode Toggle for Mobile */}
                  <label className="flex items-center gap-2 px-3 py-1.5 rounded border border-gray-200 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={expandedMode}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setExpandedMode(checked);
                        if (checked) {
                          // Expand all wallets by default
                          setExpandedWallets(new Set(filteredWallets.map(w => w.address)));
                        } else {
                          setExpandedWallets(new Set());
                        }
                      }}
                      className="w-4 h-4 rounded"
                    />
                    <span className="text-sm font-medium">Расширено</span>
                  </label>
                </div>

                {/* Desktop: Horizontal Buttons */}
                <div className="hidden md:flex gap-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={selectAllWallets}>
                    Select All
                  </Button>
                  <Button size="sm" variant="outline" onClick={deselectAllWallets}>
                    Deselect All
                  </Button>
                  {selectedWallets.length > 0 && (
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={handleBulkUpdateGroup}
                      disabled={loading}
                      className="border-green-500 text-green-700 hover:bg-green-50"
                    >
                      Изменить группу ({selectedWallets.length})
                    </Button>
                  )}
                  
                  {/* Group Selection Buttons */}
                  {getWalletGroups().map(group => {
                    const formatNumber = (num) => Math.floor(num).toLocaleString('en-US');
                    const isFiltered = filteredGroup === group.name;
                    return (
                      <button 
                        key={group.name}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleGroupFilter(group.name);
                        }}
                        className={`border-2 rounded-lg px-3 py-1 transition-all text-left ${
                          isFiltered
                            ? 'border-blue-600 bg-blue-200 shadow-md hover:bg-blue-300'
                            : 'border-blue-300 bg-blue-50 hover:bg-blue-100'
                        }`}
                      >
                        <div className="font-semibold text-xs text-blue-900">{group.name}</div>
                        <div className="text-[10px] text-blue-600">
                          {group.count} wallets | TON: {formatNumber(group.totalTON)} | {tokenSymbol || 'TOKEN'}: {formatNumber(group.totalBUYT)}
                        </div>
                      </button>
                    );
                  })}
                  {filteredGroup && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setFilteredGroup(null);
                        toast.info('Показаны все группы');
                      }}
                      className="border-gray-400 bg-gray-100 hover:bg-gray-200"
                    >
                      Показать все
                    </Button>
                  )}
                  
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => {
                      const threshold = prompt('Выбрать кошельки с TON меньше чем (например, 0.5):', '0.5');
                      if (threshold) {
                        const thresholdValue = parseFloat(threshold);
                        const filtered = wallets.filter(w => (parseInt(w.ton_balance || 0) / 1e9) < thresholdValue);
                        setSelectedWallets(filtered.map(w => w.address));
                        toast.success(`Выбрано ${filtered.length} кошельков с TON < ${thresholdValue}`);
                      }
                    }}
                  >
                    TON &lt; N
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const threshold = prompt('Выбрать кошельки с TON больше чем (например, 0.5):', '0.5');
                      if (threshold) {
                        const thresholdValue = parseFloat(threshold);
                        const filtered = wallets.filter(w => (parseInt(w.ton_balance || 0) / 1e9) > thresholdValue);
                        setSelectedWallets(filtered.map(w => w.address));
                        toast.success(`Выбрано ${filtered.length} кошельков с TON > ${thresholdValue}`);
                      }
                    }}
                  >
                    TON &gt; N
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const threshold = prompt(`Выбрать кошельки с ${tokenSymbol || 'TOKEN'} больше чем (например, 1000):`, '1000');
                      if (threshold) {
                        const thresholdValue = parseFloat(threshold);
                        const filtered = wallets.filter(w => {
                          const balance = getJettonBalance(w.address);
                          return balance && balance.balance_readable > thresholdValue;
                        });
                        setSelectedWallets(filtered.map(w => w.address));
                        toast.success(`Выбрано ${filtered.length} кошельков с ${tokenSymbol || 'TOKEN'} > ${thresholdValue}`);
                      }
                    }}
                  >
                    {tokenSymbol || 'TOKEN'} &gt; N
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={handleUpdateBalances} 
                    disabled={balanceUpdateProgress.updating || loading}
                  >
                    <RefreshCw className={`h-3 w-3 mr-1 ${balanceUpdateProgress.updating ? 'animate-spin' : ''}`} />
                    {balanceUpdateProgress.updating 
                      ? `${balanceUpdateProgress.current}/${balanceUpdateProgress.total}` 
                      : 'Update Balances'
                    }
                  </Button>
                  
                  {/* Expanded Mode Toggle */}
                  <label className="flex items-center gap-2 px-3 py-1.5 rounded border border-gray-200 cursor-pointer hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={expandedMode}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setExpandedMode(checked);
                        if (checked) {
                          // Expand all wallets by default
                          setExpandedWallets(new Set(filteredWallets.map(w => w.address)));
                        } else {
                          setExpandedWallets(new Set());
                        }
                      }}
                      className="w-4 h-4 rounded"
                    />
                    <span className="text-sm font-medium">Расширено</span>
                  </label>
                </div>
              </div>
              
              {/* Progress bar for balance updates */}
              {balanceUpdateProgress.updating && (
                <div className="px-4 py-3 bg-blue-50 border-b border-blue-200">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-blue-700 font-medium">
                      {balanceUpdateProgress.currentWallet 
                        ? `Обновляется: ${truncateAddress(balanceUpdateProgress.currentWallet, 30, 10)}` 
                        : 'Обновление балансов...'}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-blue-600 font-semibold">{balanceUpdateProgress.current} / {balanceUpdateProgress.total}</span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={resetStuckProgress}
                        className="h-6 px-2 text-xs"
                      >
                        Сбросить
                      </Button>
                    </div>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-2 overflow-hidden">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${balanceUpdateProgress.total > 0 ? (balanceUpdateProgress.current / balanceUpdateProgress.total) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              )}
            </CardHeader>
            <CardContent className="p-0 md:p-6">
              
              {/* Mobile: Compact Card View */}
              <div className="md:hidden" key={`mobile-wallets-${filteredGroup || 'all'}`}>
                <ScrollArea className="h-[400px]">
                  <div className="space-y-2 p-3">
                    {pagedWallets.map((wallet) => {
                      const jettonBalance = getJettonBalance(wallet.address);
                      // Always use tokenSymbol from global config
                      const displaySymbol = tokenSymbol || 'TOKEN';
                      const isUpdating = balanceUpdateProgress.updating && balanceUpdateProgress.currentWallet === wallet.address;
                      
                      return (
                        <div 
                          key={wallet.address}
                          className={`border rounded-lg p-3 space-y-2 transition-all ${
                            isUpdating 
                              ? 'bg-blue-50 border-blue-300 border-2 animate-pulse' 
                              : 'bg-white border-gray-200'
                          }`}
                        >
                          {/* Checkbox and Status */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={selectedWallets.includes(wallet.address)}
                                onChange={() => toggleWalletSelection(wallet.address)}
                                className="rounded"
                              />
                              <Select
                                value={wallet.group_name || 'No Group'}
                                onValueChange={(newGroup) => handleUpdateWalletGroup(wallet.address, newGroup)}
                              >
                                <SelectTrigger className="w-[120px] h-7 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {groups.map((group) => (
                                    <SelectItem key={group.group_name} value={group.group_name}>
                                      {group.group_name}
                                    </SelectItem>
                                  ))}
                                  <SelectItem value="No Group">No Group</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <Badge variant={wallet.status === 'active' ? 'default' : 'secondary'} className="text-xs">
                              {wallet.status}
                            </Badge>
                          </div>
                          
                          {/* Balances - Main Info */}
                          <div className="grid grid-cols-2 gap-3">
                            {/* TON Balance */}
                            <div>
                              <div className="text-xs text-gray-500">TON Balance</div>
                              <div className="font-semibold text-sm">
                                {formatBalance(wallet.ton_balance)} TON
                              </div>
                              {tonPriceUsd > 0 && (
                                <div className="text-xs text-gray-500">
                                  ${((wallet.ton_balance / 1e9) * tonPriceUsd).toFixed(2)}
                                </div>
                              )}
                            </div>
                            
                            {/* Token Balance */}
                            <div>
                              <div className="text-xs text-gray-500">Token Balance</div>
                              {jettonBalance ? (
                                <>
                                  <div className="font-semibold text-sm">
                                    {(jettonBalance.balance_readable || 0).toFixed(2)} {displaySymbol}
                                  </div>
                                  {jettonPriceUsd > 0 && jettonBalance.balance_readable > 0 && (
                                    <div className="text-xs text-gray-500">
                                      ${(jettonBalance.balance_readable * jettonPriceUsd).toFixed(2)}
                                    </div>
                                  )}
                                </>
                              ) : (
                                <div className="text-sm text-gray-400">
                                  {Object.keys(jettonBalances).length === 0 ? 'Loading...' : `0.00 ${tokenSymbol || 'TOKEN'}`}
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Address - Small and Clickable */}
                          <div 
                            className="text-xs font-mono text-gray-500 cursor-pointer hover:text-gray-700 truncate"
                            onClick={() => copyToClipboard(wallet.address)}
                            title="Click to copy full address"
                          >
                          {truncateAddress(wallet.address)}
                          </div>
                          
                          {/* Action Buttons */}
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleShowMnemonic(wallet.address)}
                              className="flex-1 text-xs"
                            >
                              Mnemonic
                            </Button>
                            {expandedMode && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setExpandedWallets(prev => {
                                    const next = new Set(prev);
                                    if (next.has(wallet.address)) {
                                      next.delete(wallet.address);
                                    } else {
                                      next.add(wallet.address);
                                    }
                                    return next;
                                  });
                                }}
                                className="text-xs"
                              >
                                {expandedWallets.has(wallet.address) ? 'Свернуть' : 'Токены'}
                              </Button>
                            )}
                          </div>
                          
                          {/* Expanded Tokens List for Mobile */}
                          {expandedMode && expandedWallets.has(wallet.address) && wallet.jetton_balances && (
                            <div className="mt-2 p-2 bg-gray-50 rounded-lg space-y-1">
                              {(() => {
                                // Known token addresses mapping
                                const knownTokens = {
                                  [globalTokenAddress]: tokenSymbol,
                                  [assetKey(globalTokenAddress)]: tokenSymbol,
                                  'EQBmR1S2TdeCDMl956IxzoZ7IPx4f-D2s6Fj2seoUxRy1dbG': 'PUBG',
                                  '0:664754b64dd7820cc97de7a231ce867b20fc787fe0f6b3a163dac7a8531472d5': 'PUBG'
                                };
                                
                                // Filter and deduplicate tokens
                                const seenAssets = new Set();
                                const uniqueTokens = [];
                                
                                Object.entries(wallet.jetton_balances).forEach(([tokenAddr, tokenData]) => {
                                  const isObject = typeof tokenData === 'object';
                                  
                                  let symbol, balance, decimals;
                                  
                                  if (isObject) {
                                    symbol = knownTokens[assetKey(tokenAddr)] || knownTokens[tokenAddr] || tokenData.symbol;
                                    balance = tokenData.balance;
                                    decimals = tokenData.decimals || 9;
                                  } else {
                                    // Old format - just a number, lookup symbol
                                    symbol = knownTokens[assetKey(tokenAddr)] || knownTokens[tokenAddr] || null;
                                    balance = tokenData;
                                    decimals = 9;
                                  }
                                  
                                  // Skip entries without valid symbol
                                  if (!symbol || symbol === 'TOKEN' || symbol === 'Unknown') return;
                                  
                                  // Deduplicate by symbol
                                  if (seenAssets.has(assetKey(tokenAddr))) return;
                                  seenAssets.add(assetKey(tokenAddr));
                                  
                                  uniqueTokens.push([tokenAddr, { symbol, balance, decimals }]);
                                });
                                
                                return uniqueTokens.map(([tokenAddr, tokenData]) => {
                                  const { symbol, balance, decimals } = tokenData;
                                  const readableBalance = parseFloat(balance) / Math.pow(10, decimals);
                                  
                                  if (readableBalance <= 0) return null;
                                  
                                  return (
                                    <a
                                      key={tokenAddr}
                                      href={`https://tonviewer.com/${wallet.address}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center gap-2 p-1.5 rounded bg-white border border-gray-200"
                                    >
                                      <JettonIcon tokenAddr={tokenAddr} symbol={symbol} />
                                      <span className="text-xs font-medium">{symbol}</span>
                                      <span className="text-xs text-gray-500 ml-auto">
                                        {readableBalance >= 1000 ? (readableBalance / 1000).toFixed(1) + 'K' : readableBalance.toFixed(2)}
                                      </span>
                                    </a>
                                  );
                                });
                              })()}
                              {(() => {
                                const knownTokens = {
                                  [globalTokenAddress]: tokenSymbol,
                                  [assetKey(globalTokenAddress)]: tokenSymbol,
                                  'EQBmR1S2TdeCDMl956IxzoZ7IPx4f-D2s6Fj2seoUxRy1dbG': 'PUBG',
                                  '0:664754b64dd7820cc97de7a231ce867b20fc787fe0f6b3a163dac7a8531472d5': 'PUBG'
                                };
                                const hasTokens = Object.entries(wallet.jetton_balances || {}).some(([k, d]) => {
                                  const isObject = typeof d === 'object';
                                  const symbol = isObject ? d.symbol : knownTokens[assetKey(k)];
                                  return symbol && symbol !== 'TOKEN' && symbol !== 'Unknown';
                                });
                                return !hasTokens ? <div className="text-xs text-gray-400 text-center py-2">Нет токенов</div> : null;
                              })()}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    
                    {filteredWallets.length === 0 && (
                      <div className="text-center py-12 text-gray-500">
                        {filteredGroup ? `No wallets in group "${filteredGroup}"` : 'No wallets yet. Generate some wallets to get started.'}
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </div>
              
              {/* Desktop: Table View */}
              <div className="hidden md:block overflow-x-auto" key={`desktop-wallets-${filteredGroup || 'all'}`}>
                <ScrollArea className="h-[500px]">
                  <Table className="min-w-[800px]">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">Select</TableHead>
                        <TableHead className="min-w-[200px]">Address</TableHead>
                        <TableHead>Group</TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-gray-100 transition-colors"
                          onClick={() => handleSort('ton')}
                        >
                          <div className="flex items-center">
                            TON Balance
                            {getSortIcon('ton')}
                          </div>
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-gray-100 transition-colors"
                          onClick={() => handleSort('token')}
                        >
                          <div className="flex items-center">
                            Token Balance
                            {getSortIcon('token')}
                          </div>
                        </TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                  <TableBody key={`table-body-${filteredGroup || 'all'}`}>
                    {pagedWallets.map((wallet) => {
                      const isUpdating = balanceUpdateProgress.updating && balanceUpdateProgress.currentWallet === wallet.address;
                      return (
                      <React.Fragment key={wallet.address}>
                      <TableRow 
                        className={isUpdating ? "bg-blue-50 border-blue-300 border-2 animate-pulse" : ""}
                      >
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={selectedWallets.includes(wallet.address)}
                            onChange={() => toggleWalletSelection(wallet.address)}
                            className="rounded"
                          />
                        </TableCell>
                        <TableCell 
                          className="font-mono text-xs cursor-pointer hover:bg-gray-100"
                          onClick={() => copyToClipboard(wallet.address)}
                          title="Click to copy"
                        >
                          {truncateAddress(wallet.address)}
                        </TableCell>
                        <TableCell>
                          <Select
                            value={wallet.group_name || 'No Group'}
                            onValueChange={(newGroup) => handleUpdateWalletGroup(wallet.address, newGroup)}
                          >
                            <SelectTrigger className="w-[150px] h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {groups && groups.length > 0 ? (
                                groups.map((group) => (
                                  <SelectItem key={group.group_name} value={group.group_name}>
                                    {group.group_name}
                                  </SelectItem>
                                ))
                              ) : (
                                <SelectItem value="No Group">No Group</SelectItem>
                              )}
                              <SelectItem value="No Group">No Group</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="font-medium">
                              {formatBalance(wallet.ton_balance)} TON
                            </div>
                            {tonPriceUsd > 0 && (
                              <div className="text-xs text-gray-500">
                                ${((wallet.ton_balance / 1e9) * tonPriceUsd).toFixed(2)} USD
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const balance = getJettonBalance(wallet.address);
                            if (!balance) {
                              return (
                                <div className="text-gray-400">
                                  {Object.keys(jettonBalances).length === 0 ? 'Loading...' : `0.00 ${tokenSymbol || 'TOKEN'}`}
                                </div>
                              );
                            }
                            
                            // Always use tokenSymbol from global config
                            const displaySymbol = tokenSymbol || 'TOKEN';
                            
                            return (
                              <div className="space-y-1">
                                <div className="font-medium">
                                  {(balance.balance_readable || 0).toFixed(2)} {displaySymbol}
                                </div>
                                {jettonPriceUsd > 0 && balance.balance_readable > 0 && (
                                  <div className="text-xs text-gray-500">
                                    ${(balance.balance_readable * jettonPriceUsd).toFixed(2)} USD
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          <Badge variant={wallet.status === 'active' ? 'default' : 'secondary'}>
                            {wallet.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {expandedMode && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setExpandedWallets(prev => {
                                    const next = new Set(prev);
                                    if (next.has(wallet.address)) {
                                      next.delete(wallet.address);
                                    } else {
                                      next.add(wallet.address);
                                    }
                                    return next;
                                  });
                                }}
                                className="h-7 w-7 p-0"
                              >
                                {expandedWallets.has(wallet.address) ? '▼' : '▶'}
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleShowMnemonic(wallet.address)}
                            >
                              Mnemonic
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      
                      {/* Expanded Row - Jetton Balances */}
                      {expandedMode && expandedWallets.has(wallet.address) && wallet.jetton_balances && (
                        <TableRow className="bg-gray-50 hover:bg-gray-100">
                          <TableCell colSpan={7} className="p-2">
                            <div className="pl-8 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                              {(() => {
                                // Known token addresses mapping
                                const knownTokens = {
                                  [globalTokenAddress]: tokenSymbol,
                                  [assetKey(globalTokenAddress)]: tokenSymbol,
                                  'EQBmR1S2TdeCDMl956IxzoZ7IPx4f-D2s6Fj2seoUxRy1dbG': 'PUBG',
                                  '0:664754b64dd7820cc97de7a231ce867b20fc787fe0f6b3a163dac7a8531472d5': 'PUBG'
                                };
                                
                                // Filter and deduplicate tokens
                                const seenAssets = new Set();
                                const uniqueTokens = [];
                                
                                Object.entries(wallet.jetton_balances).forEach(([tokenAddr, tokenData]) => {
                                  const isObject = typeof tokenData === 'object';
                                  
                                  let symbol, balance, decimals;
                                  
                                  if (isObject) {
                                    symbol = knownTokens[assetKey(tokenAddr)] || knownTokens[tokenAddr] || tokenData.symbol;
                                    balance = tokenData.balance;
                                    decimals = tokenData.decimals || 9;
                                  } else {
                                    // Old format - just a number, lookup symbol
                                    symbol = knownTokens[assetKey(tokenAddr)] || knownTokens[tokenAddr] || null;
                                    balance = tokenData;
                                    decimals = 9;
                                  }
                                  
                                  // Skip entries without valid symbol or with generic names
                                  if (!symbol || symbol === 'TOKEN' || symbol === 'Unknown') return;
                                  
                                  // Deduplicate by symbol
                                  if (seenAssets.has(assetKey(tokenAddr))) return;
                                  seenAssets.add(assetKey(tokenAddr));
                                  
                                  uniqueTokens.push([tokenAddr, { symbol, balance, decimals }]);
                                });
                                
                                return uniqueTokens.map(([tokenAddr, tokenData]) => {
                                  const { symbol, balance, decimals } = tokenData;
                                  const readableBalance = parseFloat(balance) / Math.pow(10, decimals);
                                  
                                  if (readableBalance <= 0) return null;
                                  
                                  return (
                                    <a
                                      key={tokenAddr}
                                      href={`https://tonviewer.com/${wallet.address}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center gap-2 p-2 rounded-lg bg-white border border-gray-200 hover:border-blue-300 hover:shadow-sm transition-all"
                                    >
                                      <JettonIcon tokenAddr={tokenAddr} symbol={symbol} />
                                      <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium truncate">
                                          {symbol}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                          {readableBalance >= 1000000 
                                            ? (readableBalance / 1000000).toFixed(2) + 'M'
                                            : readableBalance >= 1000 
                                              ? (readableBalance / 1000).toFixed(2) + 'K'
                                              : readableBalance.toFixed(2)
                                          }
                                        </div>
                                      </div>
                                    </a>
                                  );
                                });
                              })()}
                              {(() => {
                                const knownTokens = {
                                  [globalTokenAddress]: tokenSymbol,
                                  [assetKey(globalTokenAddress)]: tokenSymbol,
                                  'EQBmR1S2TdeCDMl956IxzoZ7IPx4f-D2s6Fj2seoUxRy1dbG': 'PUBG',
                                  '0:664754b64dd7820cc97de7a231ce867b20fc787fe0f6b3a163dac7a8531472d5': 'PUBG'
                                };
                                const hasTokens = Object.entries(wallet.jetton_balances || {}).some(([k, d]) => {
                                  const isObject = typeof d === 'object';
                                  const symbol = isObject ? d.symbol : knownTokens[assetKey(k)];
                                  return symbol && symbol !== 'TOKEN' && symbol !== 'Unknown';
                                });
                                return !hasTokens ? (
                                  <div className="col-span-full text-gray-400 text-sm py-2">
                                    Нет токенов
                                  </div>
                                ) : null;
                              })()}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                      </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
                {wallets.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    No wallets yet. Generate some wallets to get started.
                  </div>
                )}
              </ScrollArea>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="mt-4 md:mt-6">
          <BalanceAnalytics />
        </TabsContent>

        {/* Generate Tab */}
        <TabsContent value="generate" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5" />
                Generate Wallets
              </CardTitle>
              <CardDescription>
                Create multiple TON wallets in a named group
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Number of Wallets</label>
                  <Input
                    type="number"
                    min="1"
                    max="1000"
                    value={generateCount}
                    onChange={(e) => setGenerateCount(parseInt(e.target.value))}
                    placeholder="10"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Group Name</label>
                  <Input
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    placeholder="e.g., Group-1, TestWallets, etc."
                  />
                </div>
              </div>
              
              {/* Wallet Version Selection */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Wallet Versions</label>
                <p className="text-xs text-muted-foreground">
                  Select wallet versions to generate. If both selected, versions will be randomly distributed.
                </p>
                <div className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={walletV4r2}
                      onChange={(e) => setWalletV4r2(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300"
                    />
                    <span className="text-sm">WalletV4R2</span>
                    <Badge variant="outline" className="text-xs">Standard</Badge>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={walletV5r1}
                      onChange={(e) => setWalletV5r1(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300"
                    />
                    <span className="text-sm">WalletV5R1</span>
                    <Badge variant="secondary" className="text-xs">New</Badge>
                  </label>
                </div>
                {!walletV4r2 && !walletV5r1 && (
                  <p className="text-xs text-red-500">Select at least one wallet version</p>
                )}
              </div>
              
              {/* Delay Setting */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Delay between wallets (seconds)</label>
                <p className="text-xs text-muted-foreground">
                  Increase delay if you encounter rate limit errors. Recommended: 1.5-3 seconds.
                </p>
                <Input
                  type="number"
                  min="0.5"
                  max="10"
                  step="0.5"
                  value={generationDelay}
                  onChange={(e) => setGenerationDelay(parseFloat(e.target.value) || 1.5)}
                  placeholder="1.5"
                  disabled={loading}
                />
              </div>
              
              {/* Generation Progress */}
              {generationProgress.generating && (
                <div className="space-y-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                      Generating wallets...
                    </span>
                    <span className="text-sm text-blue-600 dark:text-blue-400">
                      {generationProgress.completed} / {generationProgress.total}
                      {generationProgress.failed > 0 && ` (${generationProgress.failed} failed)`}
                    </span>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-3 overflow-hidden">
                    <div 
                      className="bg-blue-600 dark:bg-blue-400 h-3 rounded-full transition-all duration-500 ease-out"
                      style={{ 
                        width: `${generationProgress.total > 0 ? (generationProgress.completed / generationProgress.total) * 100 : 0}%` 
                      }}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between text-xs text-blue-600 dark:text-blue-400">
                    <span>
                      {generationProgress.currentWallet ? `Creating wallet #${generationProgress.currentWallet}` : 'Starting...'}
                    </span>
                    <span>
                      {Math.round((generationProgress.completed / generationProgress.total) * 100)}%
                    </span>
                  </div>
                  
                  {/* Cancel Button */}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleCancelGeneration}
                    className="w-full text-red-600 border-red-300 hover:bg-red-50"
                  >
                    Cancel Generation
                  </Button>
                </div>
              )}

              <Button 
                onClick={handleGenerateWallets} 
                disabled={loading || (!walletV4r2 && !walletV5r1) || generationProgress.generating} 
                className="w-full"
              >
                {generationProgress.generating ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Generating... {generationProgress.completed}/{generationProgress.total}
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    Generate Wallets
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Distribute Tab */}
        <TabsContent value="distribute" className="mt-6">
          {/* ===== Pairwise 1-to-1 token transfer (A → B) ===== */}
          <Card className="mb-4 border-purple-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="h-5 w-5 text-purple-600" />
                Pairwise 1-к-1: перенос {tokenSymbol || 'TOKEN'} (группа A → B)
              </CardTitle>
              <CardDescription>
                С каждого кошелька группы A переносится ВЕСЬ баланс токенов на его пару из B (A1→B1, A2→B2…).
                Никакой консолидации. Если в A больше кошельков — лишние шлют на случайные из B (🎲).
                Источники без газа (&lt;0.06 TON) и с 0 токенов пропускаются.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label className="text-sm font-medium">Группа A (источники)</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-1">
                  {getWalletGroups().map(g => (
                    <button key={g.name} type="button"
                      onClick={() => { setPairwiseSourceGroup(g.name); setPairwiseMapping(null); }}
                      className={`text-left p-2 rounded border text-xs ${pairwiseSourceGroup === g.name ? 'border-purple-500 bg-purple-50' : 'border-gray-200'}`}>
                      <div className="font-semibold truncate">{g.name}</div>
                      <div className="text-gray-500">{g.count} шт · {g.totalBUYT.toLocaleString(undefined, { maximumFractionDigits: 0 })} {tokenSymbol || 'TOK'}</div>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Получатели B</label>
                <div className="flex gap-2 mt-1">
                  <Button type="button" size="sm" variant={pairwiseDestMode === 'group' ? 'default' : 'outline'} onClick={() => { setPairwiseDestMode('group'); setPairwiseMapping(null); }}>Группа B</Button>
                  <Button type="button" size="sm" variant={pairwiseDestMode === 'custom' ? 'default' : 'outline'} onClick={() => { setPairwiseDestMode('custom'); setPairwiseMapping(null); }}>Кастомный список</Button>
                </div>
              </div>
              {pairwiseDestMode === 'group' ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {getWalletGroups().map(g => (
                    <button key={g.name} type="button"
                      onClick={() => { setPairwiseDestGroup(g.name); setPairwiseMapping(null); }}
                      className={`text-left p-2 rounded border text-xs ${pairwiseDestGroup === g.name ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
                      <div className="font-semibold truncate">{g.name}</div>
                      <div className="text-gray-500">{g.count} шт</div>
                    </button>
                  ))}
                </div>
              ) : (
                <textarea value={pairwiseDestCustom} onChange={(e) => { setPairwiseDestCustom(e.target.value); setPairwiseMapping(null); }}
                  placeholder="UQ... адреса B, по одному в строке" rows="4"
                  className="w-full px-3 py-2 border rounded-md font-mono text-xs" />
              )}
              <div>
                <label className="text-sm font-medium">Сколько переносить</label>
                <div className="flex gap-2 mt-1 items-center flex-wrap">
                  <Button type="button" size="sm" variant={pairwiseAmountMode === 'full' ? 'default' : 'outline'} onClick={() => { setPairwiseAmountMode('full'); setPairwiseMapping(null); }}>Весь баланс</Button>
                  <Button type="button" size="sm" variant={pairwiseAmountMode === 'fixed' ? 'default' : 'outline'} onClick={() => { setPairwiseAmountMode('fixed'); setPairwiseMapping(null); }}>Фикс. количество</Button>
                  {pairwiseAmountMode === 'fixed' && (
                    <input type="number" min="0" step="any" value={pairwiseFixedAmount}
                      onChange={(e) => { setPairwiseFixedAmount(e.target.value); setPairwiseMapping(null); }}
                      placeholder={`кол-во ${tokenSymbol || 'TOKEN'} с каждого`}
                      className="px-2 py-1 border rounded text-sm w-48" />
                  )}
                </div>
                {pairwiseAmountMode === 'fixed' && (
                  <p className="text-[11px] text-gray-500 mt-1">С каждого A-кошелька уйдёт ровно это количество; у кого баланс меньше — пропускается.</p>
                )}
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button type="button" variant="outline" onClick={buildPairwiseMapping}>Построить маппинг</Button>
                {pairwiseMapping && (
                  <Button type="button" className="bg-purple-600 hover:bg-purple-700 text-white" onClick={executePairwise}
                    disabled={pairwiseMapping.pairs.filter(p => !p.skip).length === 0}>
                    Перекинуть токены ({pairwiseMapping.pairs.filter(p => !p.skip).length})
                  </Button>
                )}
              </div>
              {pairwiseMapping && (
                <div className="border rounded-md overflow-hidden">
                  <div className="text-xs bg-gray-50 px-2 py-1 flex justify-between flex-wrap gap-1">
                    <span>A: {pairwiseMapping.aCount} · B: {pairwiseMapping.bCount}</span>
                    <span>
                      к отправке: {pairwiseMapping.pairs.filter(p => !p.skip).length} · пропуск: {pairwiseMapping.pairs.filter(p => p.skip).length}
                      {pairwiseMapping.aCount > pairwiseMapping.bCount && ` · 🎲 overflow: ${pairwiseMapping.pairs.filter(p => p.overflow).length}`}
                    </span>
                  </div>
                  <div className="max-h-64 overflow-auto text-[11px] font-mono">
                    {pairwiseMapping.pairs.map((p, i) => (
                      <div key={i} className={`flex items-center justify-between px-2 py-1 border-t gap-2 ${p.skip ? 'bg-red-50 text-red-700' : ''}`}>
                        <span className="truncate">{p.source.slice(0, 8)}…{p.source.slice(-4)} → {p.dest.slice(0, 8)}…{p.dest.slice(-4)}{p.overflow ? ' 🎲' : ''}</span>
                        <span className="whitespace-nowrap">
                          →{p.sendAmt.toLocaleString(undefined, { maximumFractionDigits: 2 })} {tokenSymbol || 'TOK'} (бал {p.tokenBal.toLocaleString(undefined, { maximumFractionDigits: 2 })}) · {p.tonBal.toFixed(3)}T
                          {p.skip && ` · ⏭️ ${p.reason}`}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="h-5 w-5" />
                Distribute
              </CardTitle>
              <CardDescription>
                Send TON or {tokenSymbol || 'TOKEN'} from sender wallet to recipients
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Asset Type Toggle: TON vs Jetton */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Asset to distribute</label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={distributeAssetType === 'ton' ? 'default' : 'outline'}
                    onClick={() => setDistributeAssetType('ton')}
                    className="flex-1"
                  >
                    TON
                  </Button>
                  <Button
                    type="button"
                    variant={distributeAssetType === 'jetton' ? 'default' : 'outline'}
                    onClick={() => {
                      setDistributeAssetType('jetton');
                      setDistributeMode('file');
                    }}
                    className="flex-1"
                  >
                    {tokenSymbol || 'TOKEN'}
                  </Button>
                </div>
              </div>

              {/* Mode Toggle: Wallets vs File */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Distribution mode</label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={distributeMode === 'wallets' ? 'default' : 'outline'}
                    onClick={() => setDistributeMode('wallets')}
                    className="flex-1"
                    disabled={distributeAssetType === 'jetton'}
                    title={distributeAssetType === 'jetton' ? 'Jetton requires file mode' : ''}
                  >
                    Wallets / Groups
                  </Button>
                  <Button
                    type="button"
                    variant={distributeMode === 'file' ? 'default' : 'outline'}
                    onClick={() => setDistributeMode('file')}
                    className="flex-1"
                  >
                    From File
                  </Button>
                </div>
                {distributeAssetType === 'jetton' && (
                  <p className="text-xs text-amber-600">
                    {tokenSymbol || 'TOKEN'} distribution uses file mode (amount,address per line)
                  </p>
                )}
              </div>

              {/* Sender Wallet Selection */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Sender Wallet</label>
                <select
                  value={senderWallet}
                  onChange={(e) => setSenderWallet(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md bg-white"
                >
                  <option value="">Select sender wallet...</option>
                  {pagedWallets.map((wallet) => {
                    const jettonAddr = tokenAddress || globalTokenAddress;
                    const showJetton = distributeAssetType === 'jetton' && jettonAddr;
                    const jettonInfo = showJetton ? getJettonBalanceForWallet(wallet, jettonAddr) : null;
                    const displayBalance = showJetton
                      ? (jettonInfo
                          ? (jettonInfo.raw / Math.pow(10, jettonInfo.decimals)).toFixed(2)
                          : '0') + ` ${tokenSymbol || 'TOKEN'}`
                      : `${formatBalance(wallet.ton_balance)} TON`;
                    return (
                      <option key={wallet.address} value={wallet.address}>
                        {`${truncateAddress(wallet.address, 8, 6)} - ${displayBalance}`}
                      </option>
                    );
                  })}
                  <option value="custom">Custom Address (Manual Input)</option>
                </select>
              </div>

              {/* Show manual input if custom is selected */}
              {senderWallet === 'custom' && (
                <>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">From Address</label>
                    <Input
                      value={fromAddress}
                      onChange={(e) => setFromAddress(e.target.value)}
                      placeholder="EQ..."
                      className="font-mono text-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">From Mnemonic (24 words)</label>
                    <Input
                      type="password"
                      value={fromMnemonic}
                      onChange={(e) => setFromMnemonic(e.target.value)}
                      placeholder="word1 word2 word3..."
                    />
                  </div>
                </>
              )}

              {/* File Mode: Upload TXT with amount,address per line */}
              {distributeMode === 'file' && (
                <div className="space-y-3 p-4 bg-blue-50 border border-blue-200 rounded-md">
                  <label className="text-sm font-medium">Upload file (TXT)</label>
                  <p className="text-xs text-gray-600">
                    Format: amount,address per line. Example: 26,UQD1zKPxauim8W0dq_YdEtSajfwvTjm0N61EtubhhPm8kPIG
                  </p>
                  <div className="flex gap-2">
                    <Input
                      type="file"
                      accept=".txt,.csv"
                      onChange={handleDistributeFileUpload}
                      className="flex-1"
                    />
                  </div>
                  {distributeFileTransfers.length > 0 && (
                    <div className="text-sm">
                      <span className="font-medium">Parsed: {distributeFileTransfers.length} transfers</span>
                      <div className="max-h-32 overflow-y-auto mt-2 border rounded p-2 bg-white text-xs font-mono">
                        {distributeFileTransfers.slice(0, 5).map((t, i) => (
                          <div key={i}>{t.amount} → {truncateAddress(t.address, 12, 8)}</div>
                        ))}
                        {distributeFileTransfers.length > 5 && (
                          <div className="text-gray-500">...and {distributeFileTransfers.length - 5} more</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Amount Type Toggle - Wallets mode only */}
              {distributeMode === 'wallets' && (
              <>
              <div className="space-y-2">
                <label className="text-sm font-medium">Amount Mode</label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={distributionAmountType === 'absolute' ? 'default' : 'outline'}
                    onClick={() => setDistributionAmountType('absolute')}
                    className="flex-1"
                  >
                    Absolute (TON)
                  </Button>
                  <Button
                    type="button"
                    variant={distributionAmountType === 'percentage' ? 'default' : 'outline'}
                    onClick={() => setDistributionAmountType('percentage')}
                    className="flex-1"
                  >
                    Percentage (%)
                  </Button>
                </div>
              </div>

              {/* Conditional Amount Inputs */}
              {distributionAmountType === 'absolute' ? (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Amount per Wallet (TON)</label>
                  <Input
                    type="number"
                    step="0.1"
                    value={amountPerWallet}
                    onChange={(e) => setAmountPerWallet(parseFloat(e.target.value))}
                  />
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Min % of Balance</label>
                    <Input
                      type="number"
                      min="1"
                      max="100"
                      value={distributionMinPercent}
                      onChange={(e) => setDistributionMinPercent(parseFloat(e.target.value))}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Max % of Balance</label>
                    <Input
                      type="number"
                      min="1"
                      max="100"
                      value={distributionMaxPercent}
                      onChange={(e) => setDistributionMaxPercent(parseFloat(e.target.value))}
                    />
                  </div>
                </div>
              )}

              {/* Recipient Filter Selection */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Send To</label>
                <select
                  value={recipientFilter}
                  onChange={(e) => setRecipientFilter(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md bg-white"
                >
                  <option value="selected">Selected Wallets ({selectedWallets.length})</option>
                  <option value="all">All Wallets ({wallets.length})</option>
                  <option value="zero_balance">Zero Balance Wallets ({wallets.filter(w => parseInt(w.ton_balance || 0) === 0).length})</option>
                  <option value="group">By Group</option>
                  <option value="custom">Custom Addresses ({selectedCustomAddresses.length})</option>
                </select>
              </div>

              {/* Custom Addresses Input */}
              {recipientFilter === 'custom' && (
                <div className="space-y-3 p-4 bg-blue-50 border border-blue-200 rounded-md">
                  <label className="text-sm font-medium">Enter Addresses</label>
                  <p className="text-xs text-gray-600">
                    Enter TON addresses separated by comma, space, or new line
                  </p>
                  <textarea
                    value={customAddressesInput}
                    onChange={(e) => handleCustomAddressesChange(e.target.value)}
                    placeholder="UQDdaN5-GpkPGtQ8ojyEeYulA8gKRzL6SZ825AH-CLiu1dB_&#10;EQCXvoOojZWa6OEnoZC5-8GXiVNo4OTsFcXMc06TLgaBP-nN&#10;0:af720701bc8c59e6572451e73492f79861cddee9edac4581c6dc06efd2d969d3"
                    className="w-full px-3 py-2 border rounded-md bg-white font-mono text-xs"
                    rows="5"
                  />
                  
                  {parsedCustomAddresses.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">
                          Parsed Addresses ({parsedCustomAddresses.length})
                        </span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setSelectedCustomAddresses(parsedCustomAddresses.filter(a => a.isValid).map(a => a.address))}
                            className="text-xs text-blue-600 hover:text-blue-800"
                          >
                            Select All Valid
                          </button>
                          <button
                            onClick={() => setSelectedCustomAddresses([])}
                            className="text-xs text-red-600 hover:text-red-800"
                          >
                            Deselect All
                          </button>
                        </div>
                      </div>
                      
                      <div className="max-h-64 overflow-y-auto space-y-1 border rounded-md p-2 bg-white">
                        {parsedCustomAddresses.map((addr) => (
                          <div
                            key={addr.id}
                            className={`flex items-center gap-2 p-2 rounded ${
                              !addr.isValid ? 'bg-red-50 border-red-200' : 'hover:bg-gray-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedCustomAddresses.includes(addr.address)}
                              disabled={!addr.isValid}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedCustomAddresses([...selectedCustomAddresses, addr.address]);
                                } else {
                                  setSelectedCustomAddresses(selectedCustomAddresses.filter(a => a !== addr.address));
                                }
                              }}
                              className="h-4 w-4"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="font-mono text-xs truncate" title={addr.address}>
                                {addr.address}
                              </div>
                              {addr.isManaged && addr.walletInfo && (
                                <div className="text-xs text-blue-600">
                                  Managed: {addr.walletInfo.name || 'Unnamed'} • {parseFloat(addr.walletInfo.ton_balance || 0).toFixed(2)} TON
                                </div>
                              )}
                              {!addr.isValid && (
                                <div className="text-xs text-red-600">
                                  ❌ Invalid TON address format
                                </div>
                              )}
                            </div>
                            {addr.isValid && (
                              <Badge variant={addr.isManaged ? "default" : "secondary"} className="text-xs">
                                {addr.isManaged ? "Managed" : "External"}
                              </Badge>
                            )}
                          </div>
                        ))}
                      </div>
                      
                      {parsedCustomAddresses.filter(a => !a.isValid).length > 0 && (
                        <p className="text-xs text-red-600">
                          ⚠️ {parsedCustomAddresses.filter(a => !a.isValid).length} invalid address(es) will be skipped
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}


              {/* Group Selection */}
              {recipientFilter === 'group' && (
                <div className="space-y-3 p-4 bg-blue-50 border border-blue-200 rounded-md">
                  <label className="text-sm font-medium">Select Group</label>
                  <p className="text-xs text-gray-600 mb-3">
                    Choose a wallet group to distribute TON to all wallets in that group
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {getWalletGroups().map(group => {
                      const formatNumber = (num) => Math.floor(num).toLocaleString('en-US');
                      const isSelected = selectedDistributionGroup === group.name;
                      
                      return (
                        <button 
                          key={group.name}
                          onClick={() => setSelectedDistributionGroup(group.name)}
                          className={`border-2 rounded-lg p-3 transition-all text-left ${
                            isSelected 
                              ? 'border-blue-600 bg-blue-100 shadow-md' 
                              : 'border-blue-200 bg-white hover:bg-blue-50'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="font-semibold text-sm text-blue-900">{group.name}</div>
                              <div className="text-xs text-gray-600 mt-2 space-y-1">
                                <div>📦 Wallets: <span className="font-medium">{group.count}</span></div>
                                <div>💎 Total TON: <span className="font-medium">{formatNumber(group.totalTON)}</span></div>
                                <div>🪙 Total {tokenSymbol || 'TOKEN'}: <span className="font-medium">{formatNumber(group.totalBUYT)}</span></div>
                              </div>
                            </div>
                            {isSelected && (
                              <div className="ml-2">
                                <svg className="h-6 w-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  
                  {selectedDistributionGroup && (
                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-md">
                      <p className="text-sm text-green-800">
                        ✅ Selected: <span className="font-semibold">{selectedDistributionGroup}</span>
                        {' '}({wallets.filter(w => (w.group_name || 'No Group') === selectedDistributionGroup).length} wallets)
                      </p>
                    </div>
                  )}
                </div>
              )}


              {/* Delay */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Delay Between Transfers (seconds)</label>
                <Input
                  type="number"
                  step="0.1"
                  value={distributionDelay}
                  onChange={(e) => setDistributionDelay(parseFloat(e.target.value))}
                />
              </div>

              {/* Transaction Comment */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Transaction Comment (Optional)</label>
                <Input
                  type="text"
                  value={distributionComment}
                  onChange={(e) => setDistributionComment(e.target.value)}
                  placeholder="Leave empty for no comment"
                />
              </div>

              {/* Intermediate Hops for Anonymization */}
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Anonymization Hops (0-5)
                  <span className="text-xs text-gray-500 ml-2">Route through intermediate wallets to hide trail</span>
                </label>
                <select
                  value={intermediateHops}
                  onChange={(e) => setIntermediateHops(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border rounded-md bg-white"
                >
                  <option value="0">0 - Direct transfer (no anonymization)</option>
                  <option value="1">1 - Through 1 intermediate wallet</option>
                  <option value="2">2 - Through 2 intermediate wallets</option>
                  <option value="3">3 - Through 3 intermediate wallets</option>
                  <option value="4">4 - Through 4 intermediate wallets</option>
                  <option value="5">5 - Through 5 intermediate wallets</option>
                </select>
                {intermediateHops > 0 && (
                  <>
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-sm font-medium">Режим:</span>
                      <div className="flex rounded-md overflow-hidden border">
                        <button type="button"
                          onClick={() => setDistributeParallel(true)}
                          className={`px-3 py-1 text-sm ${distributeParallel ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}>
                          ⚡ Многопоточно
                        </button>
                        <button type="button"
                          onClick={() => setDistributeParallel(false)}
                          className={`px-3 py-1 text-sm ${!distributeParallel ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}>
                          🐌 Линейно
                        </button>
                      </div>
                      {distributeParallel && (
                        <label className="text-sm flex items-center gap-1">
                          потоков:
                          <input type="number" min="1" max="64" value={distributeConcurrency}
                            onChange={(e) => setDistributeConcurrency(Math.max(1, Math.min(64, parseInt(e.target.value) || 1)))}
                            className="w-16 px-2 py-1 border rounded-md" />
                        </label>
                      )}
                    </div>
                    <p className="text-xs text-amber-600">
                      ⚠️ Анонимизация ВКЛ. На КАЖДЫЙ адрес — свой маршрут из СЛУЧАЙНЫХ 3–6 СВЕЖИХ транзитов
                      (много новых адресов, маршруты уникальные, первое колено у всех разное). Число в селекторе
                      не важно — главное &gt;0 (вкл).
                      {distributeParallel
                        ? ` ⚡ Многопоточно: до ${distributeConcurrency} маршрутов одновременно (фундинг 1-го колена идёт батчами с источника, низовые колена — параллельно). Учитывайте лимит ~30 req/s.`
                        : ' 🐌 Линейно: по одному адресу — надёжно, но для многих адресов это десятки минут/часы.'}
                      {' '}Не запускайте с активного MM-кошелька (гонка seqno).
                    </p>
                  </>
                )}
              </div>
              </>
              )}

              <Button
                onClick={distributeMode === 'file' ? handleDistributeFromFile : handleDistributeTon}
                disabled={loading || !senderWallet || distributeTask?.status === 'running' || (distributeMode === 'file' && distributeFileTransfers.length === 0)}
                className="w-full"
              >
                <Send className="h-4 w-4 mr-2" />
                {distributeTask?.status === 'running'
                  ? 'Рассылка идёт…'
                  : (distributeMode === 'file'
                      ? `Distribute ${distributeAssetType === 'ton' ? 'TON' : (tokenSymbol || 'TOKEN')}`
                      : 'Distribute TON')}
              </Button>
            </CardContent>
          </Card>

          {/* Distribute progress panel — внутри вкладки Distribute, виден сразу под формой */}
          {distributeTask && (
            <Card className={`mt-4 border-2 ${
              distributeTask?.status === 'running' ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-900/20' :
              distributeTask?.status === 'completed' ? 'border-green-500 bg-green-50/50 dark:bg-green-900/20' :
              (distributeTask?.status === 'interrupted' || distributeTask?.status === 'cancelled') ? 'border-orange-500 bg-orange-50/50 dark:bg-orange-900/20' :
              'border-red-500 bg-red-50/50 dark:bg-red-900/20'
            }`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Send className="h-5 w-5" />
                    Рассылка (Distribute)
                    {distributeTask?.status === 'running' && ' — идёт'}
                    {distributeTask?.status === 'completed' && ' ✅ завершена'}
                    {distributeTask?.status === 'interrupted' && ' ⚠️ прервана'}
                    {distributeTask?.status === 'cancelled' && ' 🛑 отменена'}
                    {distributeTask?.status === 'error' && ' ❌ ошибка'}
                    {distributeTask?.status === 'running' && <RefreshCw className="h-4 w-4 animate-spin" />}
                  </CardTitle>
                  {distributeTask?.status !== 'running' && (
                    <Button variant="ghost" size="sm" onClick={() => {
                      setDistributeTask(null);
                      setProgressLogs([]);
                      setOperationInProgress(false);
                      localStorage.removeItem('distributeTask');
                    }}>✕ Закрыть</Button>
                  )}
                </div>
                <CardDescription>
                  {distributeTask?.status === 'running' && 'Страницу можно обновлять — рассылка не прервётся'}
                  {distributeTask?.status === 'completed' && distributeTask?.finished_at && `Завершено в ${new Date(distributeTask.finished_at).toLocaleTimeString()}`}
                  {distributeTask?.status === 'interrupted' && 'Прервана (перезапуск сервера) — можно запустить заново'}
                  {distributeTask?.status === 'error' && (distributeTask?.error || 'Ошибка выполнения')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-lg font-semibold">
                    Прогресс: {distributeTask?.completed || 0} / {distributeTask?.total || 0}
                  </span>
                  <div className="flex gap-4 text-sm">
                    <span className="text-green-600 font-medium">✅ {distributeTask?.successful || 0}</span>
                    <span className="text-red-600 font-medium">❌ {distributeTask?.failed || 0}</span>
                    {distributeTask?.uncertain > 0 && <span className="text-yellow-600 font-medium">❓ {distributeTask?.uncertain}</span>}
                  </div>
                </div>

                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4 overflow-hidden">
                  <div
                    className={`h-4 rounded-full transition-all duration-500 flex items-center justify-center text-xs text-white font-medium ${
                      distributeTask?.status === 'completed' ? 'bg-green-600' :
                      (distributeTask?.status === 'interrupted' || distributeTask?.status === 'cancelled') ? 'bg-orange-500' :
                      distributeTask?.status === 'error' ? 'bg-red-500' : 'bg-blue-600'
                    }`}
                    style={{
                      width: `${distributeTask?.total > 0 ? ((distributeTask?.completed || 0) / distributeTask.total) * 100 : 0}%`,
                      minWidth: distributeTask?.completed > 0 ? '2rem' : '0'
                    }}
                  >
                    {distributeTask?.total > 0 ? Math.round(((distributeTask?.completed || 0) / distributeTask.total) * 100) : 0}%
                  </div>
                </div>

                <div className="bg-gray-900 rounded-lg p-3 max-h-64 overflow-y-auto font-mono text-xs">
                  {progressLogs.slice(-20).map((log, index) => (
                    <div key={index} className={`py-0.5 ${
                      log.level === 'error' ? 'text-red-400' :
                      log.level === 'warning' ? 'text-yellow-400' :
                      log.level === 'success' ? 'text-green-400' : 'text-gray-300'
                    }`}>
                      <span className="text-gray-500">[{log.timestamp}]</span> {log.message}
                    </div>
                  ))}
                  {progressLogs.length === 0 && <div className="text-gray-500">Логи появятся по мере отправки…</div>}
                </div>

                {distributeTask?.status === 'running' && (
                  <Button variant="destructive" onClick={handleCancelDistribute} className="w-full">
                    🛑 Отменить рассылку
                  </Button>
                )}
                {distributeTask?.status && distributeTask.status !== 'running' && (
                  <Button onClick={handleResumeDistribute} className="w-full">
                    ▶️ Продолжить недоставленным (без задвоения)
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Rebalance Tab */}
        <TabsContent value="rebalance" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>TON Rebalance</CardTitle>
              <CardDescription>
                Перекинуть «лишний» TON из одной группы в другую через 2-3 <b>свежих транзитных адреса</b>
                (новые каждый раз, без прямой связи source→dest). Слить весь излишек ровно по получателям.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-sm font-medium">Источник (откуда слить)</label>
                  <select className="w-full border rounded px-2 py-2 bg-background text-sm"
                          value={rbSourceGroup} onChange={(e) => { setRbSourceGroup(e.target.value); setRbPlan(null); }}>
                    <option value="">— выбрать группу —</option>
                    {(groups || []).map((g, i) => {
                      const name = typeof g === 'object' ? (g.group_name || g.name) : g;
                      return <option key={i} value={name}>{name}</option>;
                    })}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">Получатель (кому долить)</label>
                  <select className="w-full border rounded px-2 py-2 bg-background text-sm"
                          value={rbDestGroup} onChange={(e) => { setRbDestGroup(e.target.value); setRbPlan(null); }}>
                    <option value="">— выбрать группу —</option>
                    {(groups || []).map((g, i) => {
                      const name = typeof g === 'object' ? (g.group_name || g.name) : g;
                      return <option key={i} value={name}>{name}</option>;
                    })}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-1">
                  <label className="text-xs text-gray-500">Reserve на source (TON)</label>
                  <Input type="number" step="any" min="0" value={rbReserve}
                         onChange={(e) => { const v = parseFloat(e.target.value); setRbReserve(Number.isNaN(v) ? 0 : v); setRbPlan(null); }} />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-gray-500">Мин. нога (TON)</label>
                  <Input type="number" step="any" min="0" value={rbMinLeg}
                         onChange={(e) => { const v = parseFloat(e.target.value); setRbMinLeg(Number.isNaN(v) ? 0 : v); setRbPlan(null); }} />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-gray-500">Хопы min</label>
                  <Input type="number" step="1" min="0" max="5" value={rbHopsMin}
                         onChange={(e) => setRbHopsMin(parseInt(e.target.value) || 0)} />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-gray-500">Хопы max</label>
                  <Input type="number" step="1" min="0" max="5" value={rbHopsMax}
                         onChange={(e) => setRbHopsMax(parseInt(e.target.value) || 0)} />
                </div>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" onClick={handleRebalancePreview}
                        disabled={rbLoading || rbTask?.status === 'running'}>
                  Preview
                </Button>
                <Button onClick={handleRebalanceStart}
                        disabled={rbLoading || !rbPlan || !(rbPlan?.legs || []).length || rbTask?.status === 'running'}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  Выполнить
                </Button>
              </div>

              {rbPlan?.summary && (
                <div className="rounded border p-3 text-sm bg-muted/30">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <div><span className="text-gray-500">Доступно:</span> {(rbPlan.summary.total_available_ton || 0).toFixed(2)} TON</div>
                    <div><span className="text-gray-500">На каждого:</span> {(rbPlan.summary.per_dest_ton || 0).toFixed(2)} TON</div>
                    <div><span className="text-gray-500">Ног:</span> {rbPlan.summary.leg_count}</div>
                    <div><span className="text-gray-500">К переводу:</span> {(rbPlan.summary.planned_total_ton || 0).toFixed(2)} TON</div>
                    <div><span className="text-gray-500">Source с излишком:</span> {rbPlan.summary.source_wallets_with_excess}/{rbPlan.summary.source_wallets_total}</div>
                    <div><span className="text-gray-500">Dest:</span> {rbPlan.summary.dest_wallets_eligible}/{rbPlan.summary.dest_wallets_total}</div>
                    <div><span className="text-gray-500">Пыль (пропущено):</span> {(rbPlan.summary.skipped_dust_ton || 0).toFixed(2)} TON</div>
                  </div>
                  <div className="mt-2 max-h-40 overflow-y-auto text-xs font-mono">
                    {(rbPlan.legs || []).slice(0, 50).map((l, i) => (
                      <div key={i} className="text-gray-500">
                        {l.source.slice(0, 8)}…→{l.dest.slice(0, 8)}… {l.amount_ton.toFixed(3)} TON
                      </div>
                    ))}
                    {(rbPlan.legs || []).length > 50 && <div>… ещё {rbPlan.legs.length - 50}</div>}
                  </div>
                </div>
              )}

              {rbTask && (
                <div className="rounded border p-3 text-sm">
                  <div className="flex items-center gap-3 mb-2">
                    <Badge className={rbTask.status === 'running' ? 'bg-blue-600 text-white' :
                      rbTask.status === 'completed' ? 'bg-emerald-600 text-white' : 'bg-amber-600 text-white'}>
                      {rbTask.status}
                    </Badge>
                    <span>{rbTask.completed_legs}/{rbTask.total_legs} ног • ✅ {rbTask.success_legs} • ❌ {rbTask.failed_legs} • ~{(rbTask.moved_ton || 0).toFixed(2)} TON</span>
                  </div>
                  <div className="max-h-48 overflow-y-auto text-xs font-mono bg-muted/30 rounded p-2">
                    {(rbTask.logs || []).slice(-60).map((lg, i) => (
                      <div key={i} className={lg.level === 'error' ? 'text-red-500' : lg.level === 'warning' ? 'text-amber-500' : 'text-gray-600'}>
                        {lg.message}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <p className="text-xs text-gray-400">
                ⚠️ Транзитные кошельки генерируются заново и сохраняются (группа «transit»). Если нога упадёт —
                TON остаётся на транзите и восстановим (recovery: POST /wallets/rebalance/sweep-transit).
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pump Cycle Tab */}
        <TabsContent value="pump" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Pump Cycle</CardTitle>
              <CardDescription>
                Накопление аптренда нарастающими покупками (цепочка кошельков), затем 1-3 крупные продажи
                из отдельной группы-мешков (нетто-слив). По итогу — точный отчёт.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1">
                <label className="text-sm font-medium">Направление</label>
                <select className="w-full border rounded px-2 py-2 bg-background text-sm"
                        value={pcDirection} onChange={(e) => { setPcDirection(e.target.value); setPcPlan(null); }}>
                  <option value="pump_dump">📈→📉 PUMP→DUMP (мелкие покупки → крупный слив, нетто-распределение)</option>
                  <option value="dump_pump">📉→📈 DUMP→PUMP (мелкие продажи → крупный откуп, нетто-накопление)</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-sm font-medium">Группа покупок (TON)</label>
                  <select className="w-full border rounded px-2 py-2 bg-background text-sm"
                          value={pcBuyGroup} onChange={(e) => { setPcBuyGroup(e.target.value); setPcPlan(null); }}>
                    <option value="">— группа —</option>
                    {(groups || []).map((g, i) => { const n = typeof g === 'object' ? (g.group_name || g.name) : g; return <option key={i} value={n}>{n}</option>; })}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">Группа мешков (токены)</label>
                  <select className="w-full border rounded px-2 py-2 bg-background text-sm"
                          value={pcSellGroup} onChange={(e) => { setPcSellGroup(e.target.value); setPcPlan(null); }}>
                    <option value="">— группа —</option>
                    {(groups || []).map((g, i) => { const n = typeof g === 'object' ? (g.group_name || g.name) : g; return <option key={i} value={n}>{n}</option>; })}
                  </select>
                </div>
              </div>
              <div className="rounded border p-3 space-y-3 bg-muted/20">
                <div className="text-xs font-medium text-gray-600">Фаза 1 — {pcDirection === 'pump_dump' ? 'мелкие покупки (разгон)' : 'мелкие продажи (просадка)'}</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div className="space-y-1"><label className="text-xs text-gray-500">Количество шагов</label>
                    <Input type="number" min="1" value={pcSteps} onChange={(e) => { setPcSteps(parseInt(e.target.value)||1); setPcPlan(null); }} /></div>
                  <div className="space-y-1"><label className="text-xs text-gray-500">Оборот фазы 1 (TON)</label>
                    <Input type="number" step="any" placeholder="напр. 10 — шаги сложатся в эту сумму" value={pcTurnoverTon} onChange={(e) => { setPcTurnoverTon(e.target.value); setPcPlan(null); }} /></div>
                  <div className="space-y-1"><label className="text-xs text-gray-500">Слив/Откуп × оборот</label>
                    <Input type="number" step="any" value={pcSellMult} onChange={(e) => { setPcSellMult(parseFloat(e.target.value)||0); setPcPlan(null); }} /></div>
                </div>
                {!pcTurnoverTon && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1"><label className="text-xs text-gray-500">1-й шаг (TON) — если оборот пуст</label>
                      <Input type="number" step="any" value={pcBuyStartTon} onChange={(e) => { setPcBuyStartTon(parseFloat(e.target.value)||0); setPcPlan(null); }} /></div>
                    <div className="space-y-1"><label className="text-xs text-gray-500">Последний шаг (TON)</label>
                      <Input type="number" step="any" value={pcBuyEndTon} onChange={(e) => { setPcBuyEndTon(parseFloat(e.target.value)||0); setPcPlan(null); }} /></div>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div className="space-y-1"><label className="text-xs text-gray-500">Фаза 2 — крупных операций (1-3)</label>
                  <Input type="number" min="1" max="3" value={pcSellCount} onChange={(e) => { setPcSellCount(parseInt(e.target.value)||1); setPcPlan(null); }} /></div>
                <div className="space-y-1"><label className="text-xs text-gray-500">Slippage %</label>
                  <Input type="number" step="any" value={pcSlippage} onChange={(e) => setPcSlippage(parseFloat(e.target.value)||0)} /></div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={handlePumpPreview} disabled={pcLoading || pcTask?.status === 'running'}>Preview</Button>
                <Button onClick={handlePumpStart} disabled={pcLoading || !pcPlan || pcTask?.status === 'running'}
                        className="bg-purple-600 hover:bg-purple-700 text-white">Запустить цикл</Button>
              </div>

              {pcPlan?.summary && (
                <div className="rounded border p-3 text-sm bg-muted/30 grid grid-cols-2 md:grid-cols-3 gap-2">
                  <div><span className="text-gray-500">Покупок:</span> {pcPlan.summary.buy_count}</div>
                  <div><span className="text-gray-500">Сумма покупок:</span> {pcPlan.summary.total_buy_ton} TON (${pcPlan.summary.total_buy_usd})</div>
                  <div><span className="text-gray-500">Цель слива:</span> ${pcPlan.summary.target_sell_usd}</div>
                  <div><span className="text-gray-500">Мешки:</span> {pcPlan.summary.dumper_wallets_with_bags} (${pcPlan.summary.dumper_capacity_usd?.toFixed?.(0)})</div>
                  <div><span className="text-gray-500">Слив реален:</span> {pcPlan.summary.dump_feasible ? '✅' : '⚠️ мало мешков'}</div>
                  <div className="col-span-2 md:col-span-3 text-xs font-mono text-gray-500">
                    покупки TON: {(pcPlan.summary.buy_amounts_ton || []).map(x => x.toFixed(2)).join(', ')}
                  </div>
                </div>
              )}

              {pcTask && (
                <div className="rounded border p-3 text-sm">
                  <div className="flex items-center gap-3 mb-2">
                    <Badge className={pcTask.status === 'running' ? 'bg-purple-600 text-white' : pcTask.status === 'completed' ? 'bg-emerald-600 text-white' : 'bg-amber-600 text-white'}>
                      {pcTask.status}{pcTask.phase ? ` · ${pcTask.phase}` : ''}
                    </Badge>
                  </div>
                  {pcTask.report && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2 text-xs">
                      <div><span className="text-gray-500">Куплено:</span> ${pcTask.report.phases?.accumulate?.spent_usd}</div>
                      <div><span className="text-gray-500">Продано:</span> ${pcTask.report.phases?.distribute?.received_usd}</div>
                      <div><span className="text-gray-500">Cash net:</span> ${pcTask.report.net?.net_usd}</div>
                      <div><span className="text-gray-500">Цена ф1→ф2:</span> {pcTask.report.price?.pump_pct}% → {pcTask.report.price?.dump_pct}% (net {pcTask.report.price?.net_pct}%)</div>
                    </div>
                  )}
                  {(pcTask.legs || []).length > 0 && (
                    <div className="mb-2 max-h-44 overflow-y-auto text-xs border rounded">
                      <div className="grid grid-cols-12 gap-1 px-2 py-1 bg-muted/50 font-medium sticky top-0">
                        <div className="col-span-1">side</div><div className="col-span-2 text-right">$</div>
                        <div className="col-span-3 text-right">токены</div><div className="col-span-4">кошелёк</div>
                        <div className="col-span-2">on-chain</div>
                      </div>
                      {pcTask.legs.map((l, i) => (
                        <div key={i} className="grid grid-cols-12 gap-1 px-2 py-1 border-t items-center">
                          <div className={`col-span-1 font-semibold ${l.side === 'buy' ? 'text-emerald-600' : 'text-red-600'}`}>{l.side}</div>
                          <div className="col-span-2 text-right">${(l.usd || 0).toFixed(2)}</div>
                          <div className="col-span-3 text-right">{Math.round(l.tokens || 0).toLocaleString()}</div>
                          <div className="col-span-4 font-mono text-gray-500">
                            <a href={`https://tonviewer.com/${l.address}`} target="_blank" rel="noreferrer" className="text-blue-500 hover:underline">{l.address?.slice(0, 8)}…</a>
                          </div>
                          <div className="col-span-2">
                            {l.status === 'success' && l.tx ? (
                              <a href={`https://tonviewer.com/transaction/${l.tx}`} target="_blank" rel="noreferrer" className="text-blue-500 hover:underline">tx ↗</a>
                            ) : (<span className="text-red-500">{l.status}</span>)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="max-h-56 overflow-y-auto text-xs font-mono bg-muted/30 rounded p-2">
                    {(pcTask.logs || []).slice(-80).map((lg, i) => (
                      <div key={i} className={lg.level === 'error' ? 'text-red-500' : lg.level === 'warning' ? 'text-amber-500' : 'text-gray-600'}>{lg.message}</div>
                    ))}
                  </div>
                </div>
              )}
              <p className="text-xs text-gray-400">⚠️ Это РЕАЛЬНЫЕ сделки. Группа слива должна держать мешки токена. Сначала Preview.</p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Trade Tab */}
        <TabsContent value="trade" className="mt-6">
          <div className="space-y-4">
            {/* Trading Chart and Swap History */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <TradingChart 
                tokenAddress={tokenAddress} 
                tokenSymbol={tokenSymbol || 'TOKEN'}
              />
              <SwapHistory 
                tokenAddress={tokenAddress} 
                tokenSymbol={tokenSymbol || 'TOKEN'}
              />
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Trading Settings</CardTitle>
                <CardDescription>
                  Configure parameters for mass buy/sell operations
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Token Address</label>
                  <Input
                    value={tokenAddress}
                    onChange={(e) => setTokenAddress(e.target.value)}
                    placeholder="EQ..."
                    className="font-mono text-sm"
                  />
                </div>
                
                {/* Amount Type Toggle */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Amount Mode</label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={amountType === 'percentage' ? 'default' : 'outline'}
                      onClick={() => setAmountType('percentage')}
                      className="flex-1"
                    >
                      Percentage (%)
                    </Button>
                    <Button
                      type="button"
                      variant={amountType === 'absolute' ? 'default' : 'outline'}
                      onClick={() => setAmountType('absolute')}
                      className="flex-1"
                    >
                      Absolute (TON)
                    </Button>
                  </div>
                </div>
                
                {/* Conditional Inputs Based on Mode */}
                {amountType === 'percentage' ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Min % of Balance</label>
                      <Input
                        type="number"
                        min="1"
                        max="100"
                        value={minPercent}
                        onChange={(e) => setMinPercent(parseFloat(e.target.value))}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Max % of Balance</label>
                      <Input
                        type="number"
                        min="1"
                        max="100"
                        value={maxPercent}
                        onChange={(e) => setMaxPercent(parseFloat(e.target.value))}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Min Amount (TON)</label>
                      <Input
                        type="number"
                        min="0.1"
                        step="0.1"
                        value={minAmountTon}
                        onChange={(e) => setMinAmountTon(parseFloat(e.target.value))}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Max Amount (TON)</label>
                      <Input
                        type="number"
                        min="0.1"
                        step="0.1"
                        value={maxAmountTon}
                        onChange={(e) => setMaxAmountTon(parseFloat(e.target.value))}
                      />
                    </div>
                  </div>
                )}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center gap-2">
                      Delay Between Trades (sec)
                      {minTradeDelay === 0 && maxTradeDelay === 0 && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">⚡ Моментально</span>
                      )}
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <label className="text-xs text-gray-500">От (min)</label>
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          value={minTradeDelay}
                          onChange={(e) => setMinTradeDelay(Math.max(0, parseFloat(e.target.value) || 0))}
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs text-gray-500">До (max)</label>
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          value={maxTradeDelay}
                          onChange={(e) => setMaxTradeDelay(Math.max(0, parseFloat(e.target.value) || 0))}
                        />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500">
                      {minTradeDelay === 0 && maxTradeDelay === 0 
                        ? "Все сделки выполняются одновременно"
                        : `Случайная задержка от ${minTradeDelay} до ${maxTradeDelay} секунд для каждой сделки`}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Slippage Tolerance (%)</label>
                    <Input
                      type="number"
                      step="0.1"
                      value={slippage}
                      onChange={(e) => setSlippage(parseFloat(e.target.value))}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Mass Trading Progress Panel - Inline (скрыт во время рассылки distribute) */}
            {((operationInProgress || massTradingTask) && !distributeTask) && (
              <Card className={`border-2 ${
                massTradingTask?.status === 'running' ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-900/20' :
                massTradingTask?.status === 'completed' ? 'border-green-500 bg-green-50/50 dark:bg-green-900/20' :
                massTradingTask?.status === 'interrupted' ? 'border-orange-500 bg-orange-50/50 dark:bg-orange-900/20' :
                'border-red-500 bg-red-50/50 dark:bg-red-900/20'
              }`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className={`flex items-center gap-2 ${
                      massTradingTask?.status === 'running' ? 'text-blue-700 dark:text-blue-300' :
                      massTradingTask?.status === 'completed' ? 'text-green-700 dark:text-green-300' :
                      massTradingTask?.status === 'interrupted' ? 'text-orange-700 dark:text-orange-300' :
                      'text-red-700 dark:text-red-300'
                    }`}>
                      <Activity className="h-5 w-5" />
                      {massTradingTask?.operation === 'buy' ? 'Mass Buy' : 'Mass Sell'} 
                      {massTradingTask?.status === 'running' && ' Progress'}
                      {massTradingTask?.status === 'completed' && ' ✅ Completed'}
                      {massTradingTask?.status === 'interrupted' && ' ⚠️ Interrupted'}
                      {massTradingTask?.status === 'cancelled' && ' 🛑 Cancelled'}
                      {massTradingTask?.status === 'running' && <RefreshCw className="h-4 w-4 animate-spin" />}
                    </CardTitle>
                    {massTradingTask?.status !== 'running' && (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => {
                          setMassTradingTask(null);
                          setProgressLogs([]);
                          setOperationInProgress(false);
                          localStorage.removeItem('massTradingTaskId');
                        }}
                      >
                        ✕ Close
                      </Button>
                    )}
                  </div>
                  <CardDescription>
                    {massTradingTask?.status === 'running' && 'Page can be refreshed without stopping the operation'}
                    {massTradingTask?.status === 'completed' && `Finished at ${new Date(massTradingTask.finished_at).toLocaleTimeString()}`}
                    {massTradingTask?.status === 'interrupted' && 'Operation was interrupted (server restart). You can start a new one.'}
                    {massTradingTask?.status === 'cancelled' && 'Operation was cancelled'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Progress Stats */}
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-semibold">
                      Progress: {massTradingTask?.completed || 0} / {massTradingTask?.total || 0}
                    </span>
                    <div className="flex gap-4 text-sm">
                      <span className="text-green-600 font-medium">✅ {massTradingTask?.successful || 0}</span>
                      <span className="text-red-600 font-medium">❌ {massTradingTask?.failed || 0}</span>
                    </div>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4 overflow-hidden">
                    <div 
                      className={`h-4 rounded-full transition-all duration-500 flex items-center justify-center text-xs text-white font-medium ${
                        massTradingTask?.status === 'completed' ? 'bg-green-600' :
                        massTradingTask?.status === 'interrupted' ? 'bg-orange-500' :
                        massTradingTask?.status === 'cancelled' ? 'bg-red-500' :
                        'bg-blue-600'
                      }`}
                      style={{ 
                        width: `${massTradingTask?.total > 0 ? ((massTradingTask?.completed || 0) / massTradingTask.total) * 100 : 0}%`,
                        minWidth: massTradingTask?.completed > 0 ? '2rem' : '0'
                      }}
                    >
                      {massTradingTask?.total > 0 ? Math.round(((massTradingTask?.completed || 0) / massTradingTask.total) * 100) : 0}%
                    </div>
                  </div>
                  
                  {/* Logs */}
                  <div className="bg-gray-900 rounded-lg p-3 max-h-64 overflow-y-auto font-mono text-xs">
                    {progressLogs.slice(-20).map((log, index) => (
                      <div
                        key={index}
                        className={`py-0.5 ${
                          log.level === 'error' ? 'text-red-400' :
                          log.level === 'warning' ? 'text-yellow-400' :
                          log.level === 'success' ? 'text-green-400' :
                          'text-gray-300'
                        }`}
                      >
                        <span className="text-gray-500">[{log.timestamp}]</span> {log.message}
                      </div>
                    ))}
                    {progressLogs.length === 0 && (
                      <div className="text-gray-500">No logs available</div>
                    )}
                  </div>
                  
                  {/* Cancel Button - only when running */}
                  {massTradingTask?.status === 'running' && (
                    <Button
                      variant="destructive"
                      onClick={handleCancelMassTrading}
                      className="w-full"
                    >
                      🛑 Cancel Operation
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}

            <div className="grid grid-cols-2 gap-4">
              <Button 
                onClick={handleMassBuy} 
                disabled={loading || selectedWallets.length === 0 || operationInProgress}
                className="h-20"
              >
                <ShoppingCart className="h-6 w-6 mr-2" />
                <div className="text-left">
                  <div className="font-bold">Mass Buy Token</div>
                  <div className="text-xs opacity-80">{selectedWallets.length} wallets selected</div>
                </div>
              </Button>

              <Button 
                onClick={handleMassSell} 
                disabled={loading || selectedWallets.length === 0 || operationInProgress}
                variant="destructive"
                className="h-20"
              >
                <TrendingDown className="h-6 w-6 mr-2" />
                <div className="text-left">
                  <div className="font-bold">Mass Sell Token</div>
                  <div className="text-xs opacity-80">{selectedWallets.length} wallets selected</div>
                </div>
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* Warmup Tab */}
        <TabsContent value="warmup" className="mt-6">
          <div className="space-y-4">
            <WarmupDashboard />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <WarmupConfiguration />
              <TokenManagement />
            </div>
            <WarmupHistory />
          </div>
        </TabsContent>

        {/* Portfolio Tab */}
        <TabsContent value="portfolio" className="mt-6">
          <div className="space-y-4">
            <PortfolioSummary />
            <div className="text-center text-muted-foreground p-8">
              Portfolio analytics and token positions table coming soon...
            </div>
          </div>
        </TabsContent>

        {/* Binance Tab */}
        <TabsContent value="binance" className="mt-6">
          <div className="space-y-6">
            {/* Binance Settings Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Binance API Configuration
                </CardTitle>
                <CardDescription>
                  Configure your Binance API credentials to distribute TON from your exchange account
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {binanceConfigured && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-sm text-green-800 font-medium">
                      ✅ Binance credentials configured
                    </p>
                  </div>
                )}
                
                <div className="space-y-2">
                  <label className="text-sm font-medium">Binance API Key</label>
                  <Input
                    type="text"
                    placeholder="Enter your Binance API Key"
                    value={binanceApiKey}
                    onChange={(e) => setBinanceApiKey(e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium">Binance API Secret</label>
                  <Input
                    type="password"
                    placeholder="Enter your Binance API Secret"
                    value={binanceApiSecret}
                    onChange={(e) => setBinanceApiSecret(e.target.value)}
                  />
                </div>
                
                <Button 
                  onClick={saveBinanceCredentials} 
                  disabled={loading || !binanceApiKey || !binanceApiSecret}
                  className="w-full"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Save Credentials
                </Button>
              </CardContent>
            </Card>

            {/* Binance Balance Card */}
            {binanceConfigured && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Binance TON Balance</CardTitle>
                    <Button size="sm" variant="outline" onClick={loadBinanceBalance} disabled={loading}>
                      <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {binanceBalance ? (
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <p className="text-sm text-blue-600 font-medium">Available</p>
                        <p className="text-2xl font-bold text-blue-900">{binanceBalance.free.toFixed(2)} TON</p>
                      </div>
                      <div className="bg-yellow-50 p-4 rounded-lg">
                        <p className="text-sm text-yellow-600 font-medium">Locked</p>
                        <p className="text-2xl font-bold text-yellow-900">{binanceBalance.locked.toFixed(2)} TON</p>
                      </div>
                      <div className="bg-green-50 p-4 rounded-lg">
                        <p className="text-sm text-green-600 font-medium">Total</p>
                        <p className="text-2xl font-bold text-green-900">{binanceBalance.total.toFixed(2)} TON</p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500">Loading balance...</p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Binance Internal Transfer Card - Перевод между пополнениями, спотом и фьючерсами */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ArrowLeftRight className="h-5 w-5" />
                  Binance Internal Transfer
                </CardTitle>
                <CardDescription>
                  Move TON between Funding, Spot, and Futures wallets inside Binance
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {!binanceConfigured ? (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                    <p className="text-sm text-yellow-800 font-medium mb-2">
                      ⚠️ Binance credentials not configured
                    </p>
                    <p className="text-xs text-yellow-700">
                      Please configure your Binance API credentials above to use internal transfers
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                      Use this to quickly shift liquidity between Funding (пополнения), Spot and USDT-M Futures без вывода на блокчейн.
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">From</label>
                        <select
                          className="w-full p-2 border border-gray-300 rounded-lg"
                          value={binanceTransferFrom}
                          onChange={(e) => setBinanceTransferFrom(e.target.value)}
                        >
                          {binanceTransferAccounts.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">To</label>
                        <select
                          className="w-full p-2 border border-gray-300 rounded-lg"
                          value={binanceTransferTo}
                          onChange={(e) => setBinanceTransferTo(e.target.value)}
                        >
                          {binanceTransferAccounts.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Asset</label>
                        <Input
                          value={binanceTransferAsset}
                          onChange={(e) => setBinanceTransferAsset(e.target.value.toUpperCase())}
                          placeholder="TON"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Amount</label>
                        <Input
                          type="number"
                          min="0"
                          step="0.00000001"
                          value={binanceTransferAmount}
                          onChange={(e) => setBinanceTransferAmount(parseFloat(e.target.value) || 0)}
                        />
                      </div>
                    </div>
                    
                    <p className="text-xs text-gray-500">
                      Binance charges no withdrawal fees for internal transfers. Доступные направления: Spot ↔ Funding, Spot ↔ Futures, Funding ↔ Futures.
                    </p>
                    
                    <Button
                      onClick={handleBinanceInternalTransfer}
                      disabled={
                        binanceTransferLoading ||
                        binanceTransferFrom === binanceTransferTo ||
                        !binanceTransferAmount
                      }
                      className="w-full h-14"
                    >
                      {binanceTransferLoading ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Processing transfer...
                        </>
                      ) : (
                        <>
                          <ArrowLeftRight className="h-5 w-5 mr-2" />
                          Transfer internally on Binance
                        </>
                      )}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Binance Distribution Card */}
            {binanceConfigured && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Send className="h-5 w-5" />
                    Distribute TON from Binance
                  </CardTitle>
                  <CardDescription>
                    Send TON from your Binance account to managed wallets
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-sm text-yellow-800">
                      ⚠️ Important: Binance has withdrawal limits and fees. Minimum delay between withdrawals is 10 seconds.
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Recipient Wallets</label>
                    <select
                      className="w-full p-2 border border-gray-300 rounded-lg"
                      value={binanceRecipientFilter}
                      onChange={(e) => {
                        setBinanceRecipientFilter(e.target.value);
                        if (e.target.value !== 'groups') {
                          setBinanceSelectedGroups([]);
                        }
                      }}
                    >
                      <option value="selected">Selected Wallets ({selectedWallets.length})</option>
                      <option value="all">All Wallets ({wallets.length})</option>
                      <option value="zero_balance">Zero Balance Wallets ({wallets.filter(w => parseInt(w.ton_balance || 0) === 0).length})</option>
                      <option value="groups">Groups</option>
                    </select>
                    <p className="text-xs text-gray-500">
                      Select which wallets should receive TON from Binance
                    </p>
                  </div>

                  {binanceRecipientFilter === 'groups' && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Select Groups</label>
                      <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-lg p-2 space-y-1">
                        {getWalletGroups().map((group) => (
                          <label key={group.name} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                            <input
                              type="checkbox"
                              checked={binanceSelectedGroups.includes(group.name)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setBinanceSelectedGroups([...binanceSelectedGroups, group.name]);
                                } else {
                                  setBinanceSelectedGroups(binanceSelectedGroups.filter(g => g !== group.name));
                                }
                              }}
                              className="rounded"
                            />
                            <span className="text-sm">
                              {group.name} ({group.count} кошельков)
                            </span>
                          </label>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500">
                        Selected: {binanceSelectedGroups.length} group(s), {wallets.filter(w => binanceSelectedGroups.includes(w.group_name || 'No Group')).length} wallets
                      </p>
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Amount Type</label>
                    <select
                      className="w-full p-2 border border-gray-300 rounded-lg"
                      value={binanceAmountType}
                      onChange={(e) => setBinanceAmountType(e.target.value)}
                    >
                      <option value="absolute">Absolute (TON)</option>
                      <option value="percentage">Percentage of Balance</option>
                    </select>
                  </div>

                  {binanceAmountType === 'absolute' ? (
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Min Amount (TON)</label>
                        <Input
                          type="number"
                          min="0.1"
                          step="0.1"
                          value={binanceMinAmount}
                          onChange={(e) => setBinanceMinAmount(parseFloat(e.target.value) || 0)}
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Max Amount (TON)</label>
                        <Input
                          type="number"
                          min="0.1"
                          step="0.1"
                          value={binanceMaxAmount}
                          onChange={(e) => setBinanceMaxAmount(parseFloat(e.target.value) || 0)}
                        />
                      </div>
                      <p className="text-xs text-gray-500">
                        Amount will be randomly selected between {binanceMinAmount.toFixed(2)} and {binanceMaxAmount.toFixed(2)} TON per wallet
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Min Percent (%)</label>
                        <Input
                          type="number"
                          min="0.1"
                          max="100"
                          step="0.1"
                          value={binanceMinPercent}
                          onChange={(e) => setBinanceMinPercent(parseFloat(e.target.value) || 0)}
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Max Percent (%)</label>
                        <Input
                          type="number"
                          min="0.1"
                          max="100"
                          step="0.1"
                          value={binanceMaxPercent}
                          onChange={(e) => setBinanceMaxPercent(parseFloat(e.target.value) || 0)}
                        />
                      </div>
                      {binanceBalance && (
                        <p className="text-xs text-gray-500">
                          Amount will be randomly selected between {(binanceBalance.free * binanceMinPercent / 100).toFixed(2)} and {(binanceBalance.free * binanceMaxPercent / 100).toFixed(2)} TON per wallet
                          ({(binanceBalance.free * binanceMinPercent / 100).toFixed(2)} - {(binanceBalance.free * binanceMaxPercent / 100).toFixed(2)}% of available balance)
                        </p>
                      )}
                    </div>
                  )}

                  {(() => {
                    let recipientCount = 0;
                    if (binanceRecipientFilter === 'selected') {
                      recipientCount = selectedWallets.length;
                    } else if (binanceRecipientFilter === 'all') {
                      recipientCount = wallets.length;
                    } else if (binanceRecipientFilter === 'zero_balance') {
                      recipientCount = wallets.filter(w => parseInt(w.ton_balance || 0) === 0).length;
                    } else if (binanceRecipientFilter === 'groups') {
                      recipientCount = wallets.filter(w => binanceSelectedGroups.includes(w.group_name || 'No Group')).length;
                    }
                    
                    let estimatedTotal = 0;
                    let estimatedText = '';
                    if (binanceAmountType === 'absolute' && recipientCount > 0) {
                      estimatedTotal = ((binanceMinAmount + binanceMaxAmount) / 2) * recipientCount;
                      estimatedText = `Estimated total required: ${estimatedTotal.toFixed(2)} TON for ${recipientCount} wallet(s)`;
                    } else if (binanceAmountType === 'percentage' && binanceBalance && recipientCount > 0) {
                      // For percentage mode, show info that amounts are calculated from remaining balance
                      const minAmount = (binanceBalance.free * binanceMinPercent / 100);
                      const maxAmount = (binanceBalance.free * binanceMaxPercent / 100);
                      estimatedText = `Amount will be randomly selected between ${minAmount.toFixed(2)} and ${maxAmount.toFixed(2)} TON per wallet (${binanceMinPercent}% - ${binanceMaxPercent}% of remaining balance)`;
                    }
                    
                    return estimatedText && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-sm text-blue-800">
                          {estimatedText}
                        </p>
                      </div>
                    );
                  })()}
                  
                  <Button 
                    onClick={handleBinanceDistribute} 
                    disabled={loading || !binanceConfigured || (binanceRecipientFilter === 'groups' && binanceSelectedGroups.length === 0)}
                    className="w-full h-16"
                  >
                    <Send className="h-5 w-5 mr-2" />
                    <div className="text-left">
                      <div className="font-bold">Distribute from Binance</div>
                      <div className="text-xs opacity-80">
                        {binanceRecipientFilter === 'selected' && `${selectedWallets.length} wallets selected`}
                        {binanceRecipientFilter === 'all' && `${wallets.length} wallets`}
                        {binanceRecipientFilter === 'zero_balance' && `${wallets.filter(w => parseInt(w.ton_balance || 0) === 0).length} zero-balance wallets`}
                        {binanceRecipientFilter === 'groups' && `${wallets.filter(w => binanceSelectedGroups.includes(w.group_name || 'No Group')).length} wallets from ${binanceSelectedGroups.length} group(s)`}
                      </div>
                    </div>
                  </Button>
                </CardContent>
              </Card>
            )}

            {!binanceConfigured && (
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center py-8">
                    <Settings className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                    <p className="text-gray-600 mb-2">Binance not configured</p>
                    <p className="text-sm text-gray-500">Please enter your API credentials above to get started</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Import Mnemonic Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Импорт по мнемонике
                </CardTitle>
                <CardDescription>
                  Импортируйте мнемоническую фразу. Система покажет балансы для разных версий кошельков.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Мнемоника (24 слова)</label>
                  <textarea
                    className="w-full p-3 border rounded-md min-h-[100px] font-mono text-sm"
                    placeholder="word1 word2 word3 ... word24"
                    value={importMnemonic}
                    onChange={handleMnemonicChange}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Вставьте 24 слова через пробел. Система проверит балансы для WalletV4R2 и WalletV5R1.
                  </p>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                  <p className="text-sm text-yellow-800">
                    ⚠️ Никогда не делитесь мнемоникой ни с кем.
                  </p>
                </div>

                <Button
                  onClick={handlePreviewMnemonic}
                  disabled={previewLoading || importLoading || !importMnemonic}
                  className="w-full"
                >
                  {previewLoading ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Проверка...
                    </>
                  ) : (
                    <>
                      <Wallet className="h-4 w-4 mr-2" />
                      Проверить балансы
                    </>
                  )}
                </Button>

                <div className="text-sm text-gray-600 space-y-1">
                  <p className="font-medium">Как это работает:</p>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li>Одна мнемоника = два адреса (V4R2 и V5R1)</li>
                    <li>Система покажет балансы обоих кошельков</li>
                    <li>Выберите какой импортировать (или оба)</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Wallet Version Selection Dialog */}
            <Dialog open={showVersionDialog} onOpenChange={setShowVersionDialog}>
              <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Wallet className="h-5 w-5" />
                    Выберите кошелёк для импорта
                  </DialogTitle>
                  <DialogDescription>
                    Одна мнемоника создаёт разные адреса для разных версий кошельков TON.
                    Выберите какой импортировать.
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 mt-4">
                  {previewWallets.map((wallet) => (
                    <div
                      key={wallet.version}
                      onClick={() => !wallet.error && toggleVersionSelection(wallet.version)}
                      className={`p-4 border rounded-lg cursor-pointer transition-all ${
                        wallet.error 
                          ? 'bg-gray-50 border-gray-200 cursor-not-allowed opacity-60'
                          : selectedVersions.includes(wallet.version)
                            ? 'bg-blue-50 border-blue-500 ring-2 ring-blue-200'
                            : 'bg-white border-gray-200 hover:border-gray-400'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={selectedVersions.includes(wallet.version)}
                            onChange={() => toggleVersionSelection(wallet.version)}
                            disabled={wallet.error}
                            className="w-4 h-4 rounded"
                          />
                          <span className="font-semibold">{wallet.version_name}</span>
                          {wallet.exists_in_db && (
                            <Badge variant="outline" className="text-xs bg-yellow-50">
                              В базе{wallet.group_name ? ` (${wallet.group_name})` : ''}
                            </Badge>
                          )}
                        </div>
                        {(wallet.ton_balance > 0 || wallet.token_balance > 0) && (
                          <Badge className="bg-green-100 text-green-700">С балансом</Badge>
                        )}
                      </div>

                      {wallet.error ? (
                        <p className="text-sm text-red-500">Ошибка: {wallet.error}</p>
                      ) : (
                        <>
                          <p className="text-xs text-gray-500 font-mono mb-2">
                            {wallet.address}
                          </p>
                          <div className="flex gap-4 text-sm">
                            <div className="flex items-center gap-1">
                              <DollarSign className="h-3 w-3 text-blue-500" />
                              <span className="font-medium">{wallet.ton_balance?.toFixed(4) || '0'} TON</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Activity className="h-3 w-3 text-yellow-500" />
                              <span className="font-medium">
                                {wallet.token_balance?.toFixed(2) || '0'} {wallet.token_symbol || 'TOKEN'}
                              </span>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>

                {/* Group Selection */}
                <div className="border-t pt-4 mt-4 space-y-3">
                  <div className="text-sm font-semibold">Группа для кошелька:</div>
                  
                  {/* Check if wallet already exists */}
                  {previewWallets.some(w => w.exists_in_db) && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                      <p className="text-sm text-yellow-800">
                        ⚠️ Кошелек уже существует в базе
                        {previewWallets.find(w => w.exists_in_db)?.group_name && 
                          ` в группе "${previewWallets.find(w => w.exists_in_db).group_name}"`
                        }
                      </p>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="createNewGroup"
                      checked={createNewGroup}
                      onChange={(e) => {
                        setCreateNewGroup(e.target.checked);
                        if (e.target.checked) setNewGroupName('');
                      }}
                      className="w-4 h-4 rounded"
                    />
                    <label htmlFor="createNewGroup" className="text-sm cursor-pointer">
                      Создать новую группу
                    </label>
                  </div>
                  
                  {createNewGroup ? (
                    <input
                      type="text"
                      value={newGroupName}
                      onChange={(e) => setNewGroupName(e.target.value)}
                      placeholder="Название новой группы"
                      className="w-full px-3 py-2 border rounded-md text-sm"
                    />
                  ) : (
                    <select
                      value={importGroupName}
                      onChange={(e) => setImportGroupName(e.target.value)}
                      className="w-full px-3 py-2 border rounded-md text-sm"
                    >
                      <option value="Imported">Imported (по умолчанию)</option>
                      {availableGroups.map(group => (
                        <option key={group.group_name} value={group.group_name}>
                          {group.group_name} ({group.wallet_count} кошельков)
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <div className="flex gap-3 mt-6">
                  <Button
                    variant="outline"
                    onClick={() => setShowVersionDialog(false)}
                    className="flex-1"
                  >
                    Отмена
                  </Button>
                  <Button
                    onClick={handleImportSelectedVersions}
                    disabled={selectedVersions.length === 0 || importLoading}
                    className="flex-1"
                  >
                    {importLoading ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Импорт...
                      </>
                    ) : (
                      <>
                        <Plus className="h-4 w-4 mr-2" />
                        Импортировать ({selectedVersions.length})
                      </>
                    )}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Export/Backup Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Backup & Export
                </CardTitle>
                <CardDescription>
                  Import/Export all wallets and mnemonics for backup
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Import Section */}
                <div className="space-y-3">
                  <div className="bg-green-50 border border-green-200 rounded-md p-4">
                    <h4 className="font-semibold text-green-900 mb-2">📥 Import Backup</h4>
                    <p className="text-sm text-green-800 mb-3">
                      Restore wallets from a previously exported JSON backup file.
                    </p>
                    <ul className="text-xs text-green-700 space-y-1 mb-3">
                      <li>• Imports wallet addresses and mnemonics</li>
                      <li>• Option to overwrite existing wallets</li>
                      <li>• Skips duplicates if overwrite is disabled</li>
                    </ul>
                  </div>

                  <div className="space-y-2">
                    <label className="flex items-center space-x-2 text-sm">
                      <input
                        type="checkbox"
                        checked={overwriteExisting}
                        onChange={(e) => setOverwriteExisting(e.target.checked)}
                        className="rounded"
                      />
                      <span>Overwrite existing wallets</span>
                    </label>

                    <input
                      type="file"
                      accept=".json"
                      onChange={handleFileSelect}
                      className="w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />

                    {importFileContent && (
                      <div className="bg-gray-50 p-3 rounded text-sm">
                        <p className="font-semibold">File loaded:</p>
                        <p className="text-gray-600">
                          {importFileContent.wallets?.length || 0} wallets ready to import
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Export date: {importFileContent.export_date}
                        </p>
                      </div>
                    )}

                    <Button
                      onClick={handleImportBackup}
                      disabled={importLoading || !importFileContent}
                      className="w-full"
                      variant="outline"
                    >
                      {importLoading ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Importing...
                        </>
                      ) : (
                        <>
                          <Plus className="h-4 w-4 mr-2" />
                          Import Backup File
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                <div className="border-t pt-4"></div>

                {/* Export Section */}
                <div className="space-y-3">
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                    <h4 className="font-semibold text-blue-900 mb-2">📦 Export Backup</h4>
                    <p className="text-sm text-blue-800 mb-3">
                      Export all your wallets, addresses, balances, and mnemonics to a JSON file.
                    </p>
                    <ul className="text-xs text-blue-700 space-y-1 mb-3">
                      <li>• Includes all wallet addresses</li>
                      <li>• Includes mnemonic phrases (if available)</li>
                      <li>• Includes balances and metadata</li>
                      <li>• Safe offline storage format</li>
                    </ul>
                  </div>

                  <Button
                    onClick={() => handleExportWallets()}
                    className="w-full"
                    variant="outline"
                  >
                    <Activity className="h-4 w-4 mr-2" />
                    Export All Wallets to JSON
                  </Button>

                  {/* Export a specific group */}
                  <div className="mt-3 pt-3 border-t space-y-2">
                    <label className="text-sm font-medium">Export a specific group</label>
                    <select
                      value={exportGroupName}
                      onChange={(e) => setExportGroupName(e.target.value)}
                      className="w-full text-sm border rounded p-2 bg-white"
                    >
                      <option value="">— Select group —</option>
                      {groups.map((group) => (
                        <option key={group.group_name} value={group.group_name}>
                          {group.group_name || 'No Group'}{typeof group.count === 'number' ? ` (${group.count})` : ''}
                        </option>
                      ))}
                    </select>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={exportOnlyMnemonics}
                        onChange={(e) => setExportOnlyMnemonics(e.target.checked)}
                        className="h-4 w-4"
                      />
                      Только мнемоники (.txt, по одной на строку)
                    </label>
                    <Button
                      onClick={() => handleExportWallets(exportGroupName)}
                      disabled={!exportGroupName}
                      className="w-full"
                      variant="outline"
                    >
                      <Activity className="h-4 w-4 mr-2" />
                      {exportOnlyMnemonics ? 'Export Group mnemonics (.txt)' : 'Export Group to JSON'}
                    </Button>
                  </div>
                </div>

                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <p className="text-sm text-red-800">
                    🔒 <strong>Security Notice:</strong> The exported file contains your mnemonics. 
                    Store it securely offline (USB drive, encrypted storage, etc.). 
                    Never upload it to cloud services or share it.
                  </p>
                </div>

                <div className="space-y-2">
                  <h4 className="font-semibold text-sm">Statistics</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="bg-gray-50 p-3 rounded">
                      <p className="text-gray-600 text-xs">Total Wallets</p>
                      <p className="font-bold text-lg">{wallets.length}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded">
                      <p className="text-gray-600 text-xs">With Mnemonics</p>
                      <p className="font-bold text-lg">
                        {wallets.filter(w => w.mnemonic).length}
                      </p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded">
                      <p className="text-gray-600 text-xs">Without Mnemonics</p>
                      <p className="font-bold text-lg text-red-600">
                        {wallets.filter(w => !w.mnemonic).length}
                      </p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded">
                      <p className="text-gray-600 text-xs">Total Balance</p>
                      <p className="font-bold text-lg">
                        {(wallets.reduce((sum, w) => sum + parseInt(w.ton_balance || 0), 0) / 1e9).toFixed(2)} TON
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Mnemonic Dialog */}
      <Dialog open={showMnemonicDialog} onOpenChange={setShowMnemonicDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-red-600">⚠️ Wallet Mnemonic (Secret Phrase)</DialogTitle>
            <DialogDescription>
              Never share your mnemonic with anyone! Anyone with access to this phrase can steal your funds.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800 font-medium">
                🔐 Security Warning: Write this down and store it in a safe place. Never store it digitally!
              </p>
            </div>
            
            <div className="bg-gray-100 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-gray-600">Wallet Address:</p>
                <Badge variant="outline" className="text-xs font-mono">
                  {walletVersionForMnemonic === 'v5r1' ? 'Wallet V5R1' : 'Wallet V4R2'}
                </Badge>
              </div>
              <p 
                className="font-mono text-xs break-all cursor-pointer hover:bg-gray-200 p-2 rounded"
                onClick={() => copyToClipboard(selectedWalletForMnemonic)}
              >
                {selectedWalletForMnemonic}
              </p>
            </div>

            <div className="bg-white border-2 border-gray-300 p-6 rounded-lg">
              <p className="text-xs text-gray-600 mb-3">24-Word Mnemonic Phrase:</p>
              <div className="grid grid-cols-3 gap-3">
                {walletMnemonic.split(' ').map((word, index) => (
                  <div key={index} className="flex items-center gap-2 bg-gray-50 p-2 rounded">
                    <span className="text-xs text-gray-500 w-6">{index + 1}.</span>
                    <span className="font-mono text-sm font-medium">{word}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => copyToClipboard(walletMnemonic, 'Mnemonic')}
                className="flex-1"
                variant="outline"
              >
                Copy Mnemonic
              </Button>
              <Button
                onClick={() => {
                  setShowMnemonicDialog(false);
                  setWalletMnemonic('');
                }}
                className="flex-1"
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default WalletManager;
